/* global clearInterval, setInterval */

import { CfarFunctions } from "./asyncBatchCfar";
import { MeasuresFunctions } from "./asyncBatchMeasures";
import { BenchmarkRankFunctions } from "./asyncBatchBenchmarkRank";
import { AttributesFunctions } from "./asyncBatchGetAttributes";
import { HoldingsFunctions } from "./asyncBatchHoldings";
import { UAMeasuresFunctions } from "./asyncBatchUAMeasures";
import { PooledBenchmarkFunctions } from "./asyncBatchPooledBenchmark";
import { PooledBenchMarkPhase2 } from "./asyncBatchPooledBenchmarkPhase2";
import { ProfilerFunctions } from "./asyncBatchProfiler";
import { PercentileRankFunctions } from "./asyncBatchPercentileRank";
import { MinAndMaxDateFunctions } from "./asyncMinAndMaxDate";
import { PeerGroupCapitalizationFunctions } from "./asyncBatchPeerGroupCapitalization";
import { TargetSizeFunctions } from "./asyncBatchTargetSize";
import { PooledBenchmarkPhase2UnivFunctions } from "./asyncBatchPooledBenchmarkPhase2Univ";
import { PooledBenchmarkUnivFunctions } from "./asyncBatchPooledBenchmarkUniv";
import { BenchmarkRankUnivFunctions } from "./asyncBatchBenchmarkRankUniv";
import { PercentRankMeasuresFunctions } from "./asyncBatchPercentRankMeasures";
import { generateUniqueCellId, CheckProfile } from "../utils/common";

//#region cfar
/**
 * Computes the requested sample statistic of the forecast for capital calls for an investment or multiple investments.", Category = "Burgiss-Calculations")]
 * @CustomFunction PrivateiCalcCaR
 * @param [ID[][]]	ID(s) corresponding to the record type defined by the ID Type parameter. Use “All” to include all records. Default ID Type is Investment if ID Type is omitted.
 * @param [IdType]	Describes the record type corresponding to values passed to the ID parameter. Default is Investment if omitted. See Burgiss Help for more details.
 * @param [AnalysisDate]	The date of the analysis. Investment metrics computed as of this date. Prediction period starts the following day. Default is today if omitted.
 * @param [Currency]	Reflects the Currency of the amounts in which calculations are performed. Default is base if omitted.
 * @param [SelectionSetID]	Selection set filter applied to records denoted by the ID parameter. Default is no selection set if omitted.
 * @param [Filters]	Filter applied to records denoted by the ID parameter. Filter is applied after selection set is applied. Default is no filter if omitted. See Burgiss Help for more details.
 * @param [HorizonQtrs]	The horizon of the prediction period defined in quarters. The default is 1 if omitted.
 * @param [SampleStatistic]	Requested sample statistic. Available values: Count, Mean, PercentileN where in N is requested percentile. The default is Percentile95 if omitted.
 * @param [NumSimulations]	Number of simulations. The default is 1,000 if omitted.
 * @param [DesiredSampleSize]	Desired sample size per period. The default is 5 if omitted.
 * @param [MinSampleSize]	Minimum sample size per period. Periods with less than this number will be dropped. The default is 1 if omitted.
 * @param [MaxDroppedPeriodsPct]	The maximum fraction of periods that can be dropped. If more than this fraction are dropped then this will cause an error. The default is 0.5 if omitted.
 * @param [BMUWindowEndDate]	Last quarter of the set of historical quarters used in the analysis. Must be a quarter end date. The default is the latest BMU quarter end date if omitted.
 * @param [BMUWindowLengthQtrs]	Number of historical quarters used in the analysis. The default is 40 quarters (10 years) if omitted.
 * @param [MaxAge]	The greatest allowed age difference in years for similar funds in a Peer Group. The default is 0.25 if omitted.
 * @param [StopOn]	Specifies the level of severity in which to stop the simulation and return a message. The default is warning if omitted.
 * @param {CustomFunctions.Invocation} invocation - The invocation context.
 * @helpUrl https://burgiss.docsend.com/view/4c3zt8i
 * @requiresAddress
 */
// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
function BurgissCalcCFaR(
  ID?: any[][],
  IdType?: string,
  AnalysisDate?: any,
  Currency?: any,
  SelectionSetID?: any,
  Filters?: any,
  HorizonQtrs?: any,
  SampleStatistic?: any,
  NumSimulations?: any,
  DesiredSampleSize?: any,
  MinSampleSize?: any,
  MaxDroppedPeriodsPct?: any,
  BMUWindowEndDate?: any,
  BMUWindowLengthQtrs?: any,
  MaxAge?: any,
  StopOn?: any,
  invocation?: any
) {
  const result = CheckProfile();
  if (result == true) {
    const uniqueCellId = generateUniqueCellId(invocation.address);
    return new CfarFunctions().fnBurgissCalcCFaR(
      ID,
      IdType,
      AnalysisDate,
      Currency,
      SelectionSetID,
      Filters,
      HorizonQtrs,
      SampleStatistic,
      NumSimulations,
      DesiredSampleSize,
      MinSampleSize,
      MaxDroppedPeriodsPct,
      BMUWindowEndDate,
      BMUWindowLengthQtrs,
      MaxAge,
      StopOn,
      uniqueCellId
    );
  }
  return result;
}
//#endregion cfar
//#region Getattributes
/**
 * Returns the requested attribute for an investment..
 * @CustomFunction PrivateiGetAttribute
 * @param ID[][] ID(s) corresponding to the record type defined by the ID Type parameter. Use “All” to include all records. Default ID Type is Investment if ID Type is omitted.
 * @param [Attribute] Attribute of the record.
 * @param {CustomFunctions.Invocation} invocation - The invocation context.
 * @helpUrl https://burgiss.docsend.com/view/4c3zt8i
 * @requiresAddress
 */
// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
function BurgissGetAttribute(ID?: any[][], Attribute?: string, invocation?: any) {
  const result = CheckProfile();
  if (result == true) {
    const uniqueCellId = generateUniqueCellId(invocation.address);
    return new AttributesFunctions().fnBurgissGetAttribute(ID, Attribute, uniqueCellId);
  }
  return result;
}
//#endregion Getattributes
//#region TargetSize
/**
 * Calculates the target size for a portfolio or group of portfolios.
 * @CustomFunction PrivateiCalcTargetSize
 * @param ID[][] Portfolio ID(s). Use “All” to include all portfolios.
 * @param [EndDate] End date that drives the measurement date for calculations and comparison date for peer group comparison. Default date is today if omitted. See Burgiss Help for more details.
 * @param [Currency]  Reflects the currency of the amounts in which calculations are performed. Default is base if omitted.
 * @param {CustomFunctions.Invocation} invocation - The invocation context.
 * @helpUrl https://burgiss.docsend.com/view/4c3zt8i
 * @requiresAddress
 */
// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
function BurgissCalcTargetSize(ID: any[][], EndDate?: any, Currency?: string, invocation?: any) {
  const result = CheckProfile();
  if (result == true) {
    const uniqueCellId = generateUniqueCellId(invocation.address);
    return new TargetSizeFunctions().fnBurgissCalcTargetSize(ID, EndDate, Currency, uniqueCellId);
  }
  return result;
}
//#endregion TargetSize
//#region BenchmarkRank
/**
 * Calculates the internal rate of return for an investment or group of investments.
 * @CustomFunction PrivateiCalcPooledRankingIRR
 * @param ID[][] ID(s) corresponding to the record type defined by the ID Type parameter. Use “All” to include all records. Default ID Type is Investment if ID Type is omitted.
 * @param [IdType] Describes the record type corresponding to values passed to the ID parameter. Default is Investment if omitted. See Burgiss Help for more details
 * @param [StartDate] Start Date that drives the measurement date for calculations. Date must be a valid BMU Start Date. Default date is inception if omitted.
 * @param [EndDate] End date that drives the measurement date for calculations. Date must be a valid Burgiss Manager Universe (BMU) analysis date. Default date is the latest BMU analysis date if omitted.
 * @param [Currency] Denotes the Currency of the amounts in which calculations are performed. Default is base if omitted.
 * @param [SelectionSetId] Selection set filter applied to records denoted by the ID parameter. Default is no selection set if omitted
 * @param [Filter] Filter applied to records denoted by the ID parameter. Filter is applied after selection set is applied. Default is no filter if omitted. See Burgiss Help for more details.
 * @param [SampleStatistic] Requested sample statistic. Available values: Count, Mean, PercentileN where in N is requested percentile, Rank. The default is Rank if omitted.
 * @param [RankValue] Value used to rank against simulated portfolios. Value will be calculated if omitted and rank requested.
 * @param [NumSimulations] Number of simulations. The default is 1000 if omitted.
 * @param [DesiredSampleSize] Desired sample size per period. The default is 5 if omitted.
 * @param [RequirReportedValuation] Denotes whether or not to only include BMU funds that have a reported valuation as of the Start Date and/or End Date. Default is false if omitted.
 * @param [StopOn] Specifies the level of severity in which to stop the simulation and return a message. The default is warning if omitted.
 * @param {CustomFunctions.Invocation} invocation - The invocation context.
 * @helpUrl https://burgiss.docsend.com/view/4c3zt8i
 * @requiresAddress
 */
// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
function BurgissCalcPooledRankingIRR(
  ID?: any[][],
  IdType?: string,
  StartDate?: any,
  EndDate?: any,
  Currency?: string,
  SelectionSetId?: any,
  Filter?: any,
  SampleStatistic?: any,
  RankValue?: any,
  NumSimulations?: any,
  DesiredSampleSize?: any,
  RequirReportedValuation?: any,
  StopOn?: any,
  invocation?: any
) {
  const result = CheckProfile();
  if (result == true) {
    const uniqueCellId = generateUniqueCellId(invocation.address);
    return new BenchmarkRankFunctions().fnBurgissCalcPooledRankingIRR(
      ID,
      IdType,
      StartDate,
      EndDate,
      Currency,
      SelectionSetId,
      Filter,
      SampleStatistic,
      RankValue,
      NumSimulations,
      DesiredSampleSize,
      RequirReportedValuation,
      StopOn,
      uniqueCellId
    );
  }
  return result;
}

/**
 * Calculates the internal rate of return for an investment or group of investments.
 * @CustomFunction PrivateiCalcPooledRankingTWRR
 * @param ID[][] ID(s) corresponding to the record type defined by the ID Type parameter. Use “All” to include all records. Default ID Type is Investment if ID Type is omitted.
 * @param [IdType] Describes the record type corresponding to values passed to the ID parameter. Default is Investment if omitted. See Burgiss Help for more details
 * @param [StartDate] Start Date that drives the measurement date for calculations. Date must be a valid BMU Start Date. Default date is inception if omitted.
 * @param [EndDate] End date that drives the measurement date for calculations. Date must be a valid Burgiss Manager Universe (BMU) analysis date. Default date is the latest BMU analysis date if omitted.
 * @param [Currency] Reflects the Currency of the amounts in which calculations are performed. Default is base if omitted.
 * @param [SelectionSetID] Selection set filter applied to records denoted by the ID parameter. Default is no selection set if omitted
 * @param [Filter] Filter applied to records denoted by the ID parameter. Filter is applied after selection set is applied. Default is no filter if omitted. See Burgiss Help for more details.
 * @param [SampleStatistic] Requested sample statistic. Available values: Count, Mean, PercentileN where in N is requested percentile, Rank. The default is Rank if omitted.
 * @param [RankValue] Value used to rank against simulated portfolios. Value will be calculated if omitted and rank requested.
 * @param [NumSimulations] Number of simulations. The default is 1000 if omitted.
 * @param [DesiredSampleSize] Desired sample size per period. The default is 5 if omitted.
 * @param [RequirReportedValuation] Denotes whether or not to only include BMU funds that have a reported valuation as of the Start Date and/or End Date. Default is false if omitted.
 * @param [StopOn] Specifies the level of severity in which to stop the simulation and return a message. The default is warning if omitted.

 * @param {CustomFunctions.Invocation} invocation - The invocation context.
 * @param [AdvancedParameter] To enabled supported attributes for the formula.
 * @helpUrl https://burgiss.docsend.com/view/4c3zt8i
 * @requiresAddress
 */
// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
function BurgissCalcPooledRankingTWRR(
  ID?: any[][],
  IdType?: string,
  StartDate?: any,
  EndDate?: any,
  Currency?: string,
  SelectionSetId?: any,
  Filter?: any,
  SampleStatistic?: any,
  RankValue?: any,
  NumSimulations?: any,
  DesiredSampleSize?: any,
  RequirReportedValuation?: any,
  StopOn?: any,
  AdvancedParameter?: string,
  invocation?: any
) {
  const result = CheckProfile();
  if (result == true) {
    const uniqueCellId = generateUniqueCellId(invocation.address);
    return new BenchmarkRankFunctions().fnBurgissCalcPooledRankingTWRR(
      ID,
      IdType,
      StartDate,
      EndDate,
      Currency,
      SelectionSetId,
      Filter,
      SampleStatistic,
      RankValue,
      NumSimulations,
      DesiredSampleSize,
      RequirReportedValuation,
      StopOn,
      uniqueCellId,
      AdvancedParameter
    );
  }
  return result;
}

/**
 * Calculates the internal rate of return for an investment or group of investments.
 * @CustomFunction PrivateiCalcPooledRankingDPI
 * @param ID[][] ID(s) corresponding to the record type defined by the ID Type parameter. Use “All” to include all records. Default ID Type is Investment if ID Type is omitted.
 * @param [IdType] Describes the record type corresponding to values passed to the ID parameter. Default is Investment if omitted. See Burgiss Help for more details
 * @param [EndDate] End date that drives the measurement date for calculations. Date must be a valid Burgiss Manager Universe (BMU) analysis date. Default date is the latest BMU analysis date if omitted.
 * @param [Currency] Reflects the Currency of the amounts in which calculations are performed. Default is base if omitted.
 * @param [SelectionSetID] Selection set filter applied to records denoted by the ID parameter. Default is no selection set if omitted
 * @param [Filter] Filter applied to records denoted by the ID parameter. Filter is applied after selection set is applied. Default is no filter if omitted. See Burgiss Help for more details.
 * @param [SampleStatistic] Requested sample statistic. Available values: Count, Mean, PercentileN where in N is requested percentile, Rank. The default is Rank if omitted.
 * @param [RankValue] Value used to rank against simulated portfolios. Value will be calculated if omitted and rank requested.
 * @param [NumSimulations] Number of simulations. The default is 1000 if omitted.
 * @param [DesiredSampleSize] Desired sample size per period. The default is 5 if omitted.
 * @param [RequirReportedValuation] Denotes whether or not to only include BMU funds that have a reported valuation as of the Start Date and/or End Date. Default is false if omitted.
 * @param [StopOn] Specifies the level of severity in which to stop the simulation and return a message. The default is warning if omitted.
 * @param {CustomFunctions.Invocation} invocation - The invocation context.
 * @helpUrl https://burgiss.docsend.com/view/4c3zt8i
 * @requiresAddress
 */
// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
function BurgissCalcPooledRankingDPI(
  ID?: any[][],
  IdType?: string,
  EndDate?: any,
  Currency?: string,
  SelectionSetId?: any,
  Filter?: any,
  SampleStatistic?: any,
  RankValue?: any,
  NumSimulations?: any,
  DesiredSampleSize?: any,
  RequirReportedValuation?: any,
  StopOn?: any,
  invocation?: any
) {
  const result = CheckProfile();
  if (result == true) {
    const uniqueCellId = generateUniqueCellId(invocation.address);
    return new BenchmarkRankFunctions().fnBurgissCalcPooledRankingDPI(
      ID,
      EndDate,
      Currency,
      IdType,
      SelectionSetId,
      Filter,
      SampleStatistic,
      RankValue,
      NumSimulations,
      DesiredSampleSize,
      RequirReportedValuation,
      StopOn,
      uniqueCellId
    );
  }
  return result;
}

/**
 * Calculates the internal rate of return for an investment or group of investments.
 * @CustomFunction PrivateiCalcPooledRankingRVPI
 * @param ID[][] ID(s) corresponding to the record type defined by the ID Type parameter. Use “All” to include all records. Default ID Type is Investment if ID Type is omitted.
 * @param [IdType] Describes the record type corresponding to values passed to the ID parameter. Default is Investment if omitted. See Burgiss Help for more details
 * @param [EndDate] End date that drives the measurement date for calculations. Date must be a valid Burgiss Manager Universe (BMU) analysis date. Default date is the latest BMU analysis date if omitted.
 * @param [Currency] Reflects the Currency of the amounts in which calculations are performed. Default is base if omitted.
 * @param [SelectionSetID] Selection set filter applied to records denoted by the ID parameter. Default is no selection set if omitted
 * @param [Filter] Filter applied to records denoted by the ID parameter. Filter is applied after selection set is applied. Default is no filter if omitted. See Burgiss Help for more details.
 * @param [SampleStatistic] Requested sample statistic. Available values: Count, Mean, PercentileN where in N is requested percentile, Rank. The default is Rank if omitted.
 * @param [RankValue] Value used to rank against simulated portfolios. Value will be calculated if omitted and rank requested.
 * @param [NumSimulations] Number of simulations. The default is 1000 if omitted.
 * @param [DesiredSampleSize] Desired sample size per period. The default is 5 if omitted.
 * @param [RequirReportedValuation] Denotes whether or not to only include BMU funds that have a reported valuation as of the Start Date and/or End Date. Default is false if omitted.
 * @param [StopOn] Specifies the level of severity in which to stop the simulation and return a message. The default is warning if omitted.
 * @param {CustomFunctions.Invocation} invocation - The invocation context.
 * @helpUrl https://burgiss.docsend.com/view/4c3zt8i
 * @requiresAddress
 */
// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
function BurgissCalcPooledRankingRVPI(
  ID?: any[][],
  IdType?: string,
  EndDate?: any,
  Currency?: string,
  SelectionSetId?: any,
  Filter?: any,
  SampleStatistic?: any,
  RankValue?: any,
  NumSimulations?: any,
  DesiredSampleSize?: any,
  RequirReportedValuation?: any,
  StopOn?: any,
  invocation?: any
) {
  const result = CheckProfile();
  if (result == true) {
    const uniqueCellId = generateUniqueCellId(invocation.address);
    return new BenchmarkRankFunctions().fnBurgissCalcPooledRankingRVPI(
      ID,
      IdType,
      EndDate,
      Currency,
      SelectionSetId,
      Filter,
      SampleStatistic,
      RankValue,
      NumSimulations,
      DesiredSampleSize,
      RequirReportedValuation,
      StopOn,
      uniqueCellId
    );
  }
  return result;
}

/**
 * Calculates the internal rate of return for an investment or group of investments.
 * @CustomFunction PrivateiCalcPooledRankingTVPI
 * @param ID[][] ID(s) corresponding to the record type defined by the ID Type parameter. Use “All” to include all records. Default ID Type is Investment if ID Type is omitted.
 * @param [IdType] Describes the record type corresponding to values passed to the ID parameter. Default is Investment if omitted. See Burgiss Help for more details
 * @param [EndDate] End date that drives the measurement date for calculations. Date must be a valid Burgiss Manager Universe (BMU) analysis date. Default date is the latest BMU analysis date if omitted.
 * @param [Currency] Reflects the Currency of the amounts in which calculations are performed. Default is base if omitted.
 * @param [SelectionSetID] Selection set filter applied to records denoted by the ID parameter. Default is no selection set if omitted
 * @param [Filter] Filter applied to records denoted by the ID parameter. Filter is applied after selection set is applied. Default is no filter if omitted. See Burgiss Help for more details.
 * @param [SampleStatistic] Requested sample statistic. Available values: Count, Mean, PercentileN where in N is requested percentile, Rank. The default is Rank if omitted.
 * @param [RankValue] Value used to rank against simulated portfolios. Value will be calculated if omitted and rank requested.
 * @param [NumSimulations] Number of simulations. The default is 1000 if omitted.
 * @param [DesiredSampleSize] Desired sample size per period. The default is 5 if omitted.
 * @param [RequirReportedValuation] Denotes whether or not to only include BMU funds that have a reported valuation as of the Start Date and/or End Date. Default is false if omitted.
 * @param [StopOn] Specifies the level of severity in which to stop the simulation and return a message. The default is warning if omitted.
 * @param {CustomFunctions.Invocation} invocation - The invocation context.
 * @helpUrl https://burgiss.docsend.com/view/4c3zt8i
 * @requiresAddress
 */
// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
function BurgissCalcPooledRankingTVPI(
  ID?: any[][],
  IdType?: string,
  EndDate?: any,
  Currency?: string,
  SelectionSetId?: any,
  Filter?: any,
  SampleStatistic?: any,
  RankValue?: any,
  NumSimulations?: any,
  DesiredSampleSize?: any,
  RequirReportedValuation?: any,
  StopOn?: any,
  invocation?: any
) {
  const result = CheckProfile();
  if (result == true) {
    const uniqueCellId = generateUniqueCellId(invocation.address);
    return new BenchmarkRankFunctions().fnBurgissCalcPooledRankingTVPI(
      ID,
      IdType,
      EndDate,
      Currency,
      SelectionSetId,
      Filter,
      SampleStatistic,
      RankValue,
      NumSimulations,
      DesiredSampleSize,
      RequirReportedValuation,
      StopOn,
      uniqueCellId
    );
  }
  return result;
}
//#endregion BenchmarkRank
//#region BenchmarkRankUniverse
/**
 * Computes the requested sample statistic of internal rate of return for simulated portfolios for an investment or multiple investments. Results are unscaled.
 * @CustomFunction PrivateiCalcPooledRankingIRR.ByWeight
 * @param [Universe] Peer group ID or Filter applied to Burgiss Manager Universe.
 * @param [Weight]  Weights for each group.
 * @param [StartDate] Start Date that drives the measurement date for calculations. Date must be a valid BMU Start Date. Default date is inception if omitted.
 * @param [EndDate] End date that drives the measurement date for calculations. Date must be a valid Burgiss Manager Universe (BMU) analysis date. Default date is the latest BMU analysis date if omitted.
 * @param [Currency] Reflects the Currency of the amounts in which calculations are performed. Default is base if omitted.
 * @param [SampleStatistic] Requested sample statistic. Available values: Count, Mean, PercentileN where in N is requested percentile, Rank. The default is Percentile50 if omitted.
 * @param [RankValue] Value used to rank against simulated portfolios. Value will be calculated if omitted and rank requested.
 * @param [NumSimulations] Number of simulations. The default is 1000 if omitted.
 * @param [DesiredSampleSize] Desired sample size per period. The default is 5 if omitted.
 * @param [RequireReportedValuation] Denotes whether or not to only include BMU funds that have a reported valuation as of the Start Date and/or End Date. Default is false if omitted.
 * @param [StopOn] Specifies the level of severity in which to stop the simulation and return a message. The default is warning if omitted.
 * @param {CustomFunctions.Invocation} invocation - The invocation context.
 * @helpUrl https://burgiss.docsend.com/view/4c3zt8i
 * @requiresAddress
 */
// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
function burgissCalcPooledRankingIRRByWeight(
  Universe: any,
  Weight: any,
  StartDate?: any,
  EndDate?: any,
  Currency?: string,
  SampleStatistic?: any,
  RankValue?: any,
  NumSimulations?: any,
  DesiredSampleSize?: any,
  RequireReportedValuation?: any,
  StopOn?: any,
  invocation?: any
) {
  const result = CheckProfile();
  if (result == true) {
    const uniqueCellId = generateUniqueCellId(invocation.address);
    return new BenchmarkRankUnivFunctions().fnBurgissCalcPooledRankingIRRByWeight(
      Universe,
      Weight,
      StartDate,
      EndDate,
      Currency,
      SampleStatistic,
      RankValue,
      NumSimulations,
      DesiredSampleSize,
      RequireReportedValuation,
      StopOn,
      uniqueCellId
    );
  }
  return result;
}
/**
 * Computes the requested sample statistic of time-Weighted rate of return for simulated portfolios for an investment or multiple investments. Results are unscaled.
 * @CustomFunction PrivateiCalcPooledRankingTWRR.ByWeight
 * @param [Universe] Peer group ID or Filter applied to Burgiss Manager Universe.
 * @param [Weight]  Weights for each group.
 * @param [StartDate] Start Date that drives the measurement date for calculations. Date must be a valid BMU Start Date. Default date is inception if omitted.
 * @param [EndDate] End date that drives the measurement date for calculations. Date must be a valid Burgiss Manager Universe (BMU) analysis date. Default date is the latest BMU analysis date if omitted.
 * @param [Currency] Reflects the Currency of the amounts in which calculations are performed. Default is base if omitted.
 * @param [SampleStatistic] Requested sample statistic. Available values: Count, Mean, PercentileN where in N is requested percentile, Rank. The default is Percentile50 if omitted.
 * @param [RankValue] Value used to rank against simulated portfolios. Value will be calculated if omitted and rank requested.
 * @param [NumSimulations] Number of simulations. The default is 1000 if omitted.
 * @param [DesiredSampleSize] Desired sample size per period. The default is 5 if omitted.
 * @param [RequireReportedValuation] Denotes whether or not to only include BMU funds that have a reported valuation as of the Start Date and/or End Date. Default is false if omitted.
 * @param [StopOn] Specifies the level of severity in which to stop the simulation and return a message. The default is warning if omitted.
 * @param [AdvancedParameter] To enabled supported attributes for the formula.
 * @param {CustomFunctions.Invocation} invocation - The invocation context.
 * @helpUrl https://burgiss.docsend.com/view/4c3zt8i
 * @requiresAddress
 * */
// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
function burgissCalcPooledRankingTWRRByWeight(
  Universe: any,
  Weight: any,
  StartDate?: any,
  EndDate?: any,
  Currency?: string,
  SampleStatistic?: any,
  RankValue?: any,
  NumSimulations?: any,
  DesiredSampleSize?: any,
  RequireReportedValuation?: any,
  StopOn?: any,
  AdvancedParameter?: string,
  invocation?: any
) {
  const result = CheckProfile();
  if (result == true) {
    const uniqueCellId = generateUniqueCellId(invocation.address);
    return new BenchmarkRankUnivFunctions().fnBurgissCalcPooledRankingTWRRByWeight(
      Universe,
      Weight,
      StartDate,
      EndDate,
      Currency,
      SampleStatistic,
      RankValue,
      NumSimulations,
      DesiredSampleSize,
      RequireReportedValuation,
      StopOn,
      uniqueCellId,
      AdvancedParameter
    );
  }
  return result;
}
/**
 * Computes the requested sample statistic of DPI for simulated portfolios for an investment or multiple investments. Results are unscaled.
 * @CustomFunction PrivateiCalcPooledRankingDPI.ByWeight
 * @param [Universe] Peer group ID or Filter applied to Burgiss Manager Universe.
 * @param [Weight]  Weights for each group.
 * @param [EndDate] End date that drives the measurement date for calculations. Date must be a valid Burgiss Manager Universe (BMU) analysis date. Default date is the latest BMU analysis date if omitted.
 * @param [Currency] Reflects the Currency of the amounts in which calculations are performed. Default is base if omitted.
 * @param [SampleStatistic] Requested sample statistic. Available values: Count, Mean, PercentileN where in N is requested percentile, Rank. The default is Percentile50 if omitted.
 * @param [RankValue] Value used to rank against simulated portfolios. Value will be calculated if omitted and rank requested.
 * @param [NumSimulations] Number of simulations. The default is 1000 if omitted.
 * @param [DesiredSampleSize] Desired sample size per period. The default is 5 if omitted.
 * @param [RequireReportedValuation] Denotes whether or not to only include BMU funds that have a reported valuation as of the Start Date and/or End Date. Default is false if omitted.
 * @param [StopOn] Specifies the level of severity in which to stop the simulation and return a message. The default is warning if omitted.
 * @param {CustomFunctions.Invocation} invocation - The invocation context.
 * @helpUrl https://burgiss.docsend.com/view/4c3zt8i
 * @requiresAddress
 * */
// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
function burgissCalcPooledRankingDPIByWeight(
  Universe: any,
  Weight: any,
  EndDate?: any,
  Currency?: string,
  SampleStatistic?: any,
  RankValue?: any,
  NumSimulations?: any,
  DesiredSampleSize?: any,
  RequireReportedValuation?: any,
  StopOn?: any,
  invocation?: any
) {
  const result = CheckProfile();
  if (result == true) {
    const uniqueCellId = generateUniqueCellId(invocation.address);
    return new BenchmarkRankUnivFunctions().fnBurgissCalcPooledRankingDPIByWeight(
      Universe,
      Weight,
      EndDate,
      Currency,
      SampleStatistic,
      RankValue,
      NumSimulations,
      DesiredSampleSize,
      RequireReportedValuation,
      StopOn,
      uniqueCellId
    );
  }
  return result;
}
/**
 * Computes the requested sample statistic of RVPI for simulated portfolios for an investment or multiple investments. Results are unscaled.
 * @CustomFunction PrivateiCalcPooledRankingRVPI.ByWeight
 * @param [Universe] Peer group ID or Filter applied to Burgiss Manager Universe.
 * @param [Weight]  Weights for each group.
 * @param [EndDate] End date that drives the measurement date for calculations. Date must be a valid Burgiss Manager Universe (BMU) analysis date. Default date is the latest BMU analysis date if omitted.
 * @param [Currency] Reflects the Currency of the amounts in which calculations are performed. Default is base if omitted.
 * @param [SampleStatistic] Requested sample statistic. Available values: Count, Mean, PercentileN where in N is requested percentile, Rank. The default is Percentile50 if omitted.
 * @param [RankValue] Value used to rank against simulated portfolios. Value will be calculated if omitted and rank requested.
 * @param [NumSimulations] Number of simulations. The default is 1000 if omitted.
 * @param [DesiredSampleSize] Desired sample size per period. The default is 5 if omitted.
 * @param [RequireReportedValuation] Denotes whether or not to only include BMU funds that have a reported valuation as of the Start Date and/or End Date. Default is false if omitted.
 * @param [StopOn] Specifies the level of severity in which to stop the simulation and return a message. The default is warning if omitted.
 * @param {CustomFunctions.Invocation} invocation - The invocation context.
 * @helpUrl https://burgiss.docsend.com/view/4c3zt8i
 * @requiresAddress
 * */
// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
function burgissCalcPooledRankingRVPIByWeight(
  Universe: any,
  Weight: any,
  EndDate?: any,
  Currency?: string,
  SampleStatistic?: any,
  RankValue?: any,
  NumSimulations?: any,
  DesiredSampleSize?: any,
  RequireReportedValuation?: any,
  StopOn?: any,
  invocation?: any
) {
  const result = CheckProfile();
  if (result == true) {
    const uniqueCellId = generateUniqueCellId(invocation.address);
    return new BenchmarkRankUnivFunctions().fnBurgissCalcPooledRankingRVPIByWeight(
      Universe,
      Weight,
      EndDate,
      Currency,
      SampleStatistic,
      RankValue,
      NumSimulations,
      DesiredSampleSize,
      RequireReportedValuation,
      StopOn,
      uniqueCellId
    );
  }
  return result;
}
/**
 * Computes the requested sample statistic of TVPI for simulated portfolios for an investment or multiple investments. Results are unscaled.
 * @CustomFunction PrivateiCalcPooledRankingTVPI.ByWeight
 * @param [Universe] Peer group ID or Filter applied to Burgiss Manager Universe.
 * @param [Weight]  Weights for each group.
 * @param [EndDate] End date that drives the measurement date for calculations. Date must be a valid Burgiss Manager Universe (BMU) analysis date. Default date is the latest BMU analysis date if omitted.
 * @param [Currency] Reflects the Currency of the amounts in which calculations are performed. Default is base if omitted.
 * @param [SampleStatistic] Requested sample statistic. Available values: Count, Mean, PercentileN where in N is requested percentile, Rank. The default is Percentile50 if omitted.
 * @param [RankValue] Value used to rank against simulated portfolios. Value will be calculated if omitted and rank requested.
 * @param [NumSimulations] Number of simulations. The default is 1000 if omitted.
 * @param [DesiredSampleSize] Desired sample size per period. The default is 5 if omitted.
 * @param [RequireReportedValuation] Denotes whether or not to only include BMU funds that have a reported valuation as of the Start Date and/or End Date. Default is false if omitted.
 * @param [StopOn] Specifies the level of severity in which to stop the simulation and return a message. The default is warning if omitted.
 * @param {CustomFunctions.Invocation} invocation - The invocation context.
 * @helpUrl https://burgiss.docsend.com/view/4c3zt8i
 * @requiresAddress
 * */
// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
function burgissCalcPooledRankingTVPIByWeight(
  Universe: any,
  Weight: any,
  EndDate?: any,
  Currency?: string,
  SampleStatistic?: any,
  RankValue?: any,
  NumSimulations?: any,
  DesiredSampleSize?: any,
  RequireReportedValuation?: any,
  StopOn?: any,
  invocation?: any
) {
  const result = CheckProfile();
  if (result == true) {
    const uniqueCellId = generateUniqueCellId(invocation.address);
    return new BenchmarkRankUnivFunctions().fnBurgissCalcPooledRankingTVPIByWeight(
      Universe,
      Weight,
      EndDate,
      Currency,
      SampleStatistic,
      RankValue,
      NumSimulations,
      DesiredSampleSize,
      RequireReportedValuation,
      StopOn,
      uniqueCellId
    );
  }
  return result;
}

/**
 * Computes the requested sample statistic of internal rate of return for simulated portfolios for an investment or multiple investments. Results are unscaled.
 * @CustomFunction PrivateiCalcPooledRankingIRR.ByCommitment
 * @param [Universe] Peer group ID or Filter applied to Burgiss Manager Universe.
 * @param [Commitment]  Commitment for each group.
 * @param [ StartDate] Start Date that drives the measurement date for calculations. Date must be a valid BMU Start Date. Default date is inception if omitted.
 * @param [EndDate] End date that drives the measurement date for calculations. Date must be a valid Burgiss Manager Universe (BMU) analysis date. Default date is the latest BMU analysis date if omitted.
 * @param [Currency] Reflects the Currency of the amounts in which calculations are performed. Default is base if omitted.
 * @param [SampleStatistic] Requested sample statistic. Available values: Count, Mean, PercentileN where in N is requested percentile, Rank. The default is Percentile50 if omitted.
 * @param [RankValue] Value used to rank against simulated portfolios. Value will be calculated if omitted and rank requested.
 * @param [NumSimulations] Number of simulations. The default is 1000 if omitted.
 * @param [DesiredSampleSize] Desired sample size per period. The default is 5 if omitted.
 * @param [RequireReportedValuation] Denotes whether or not to only include BMU funds that have a reported valuation as of the Start Date and/or End Date. Default is false if omitted.
 * @param [StopOn] Specifies the level of severity in which to stop the simulation and return a message. The default is warning if omitted.
 * @param {CustomFunctions.Invocation} invocation - The invocation context.
 * @helpUrl https://burgiss.docsend.com/view/4c3zt8i
 * @requiresAddress
 * */
// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
function burgissCalcPooledRankingIRRByCommitment(
  Universe: any,
  Commitment?: any,
  StartDate?: any,
  EndDate?: any,
  Currency?: string,
  SampleStatistic?: any,
  RankValue?: any,
  NumSimulations?: any,
  DesiredSampleSize?: any,
  RequireReportedValuation?: any,
  StopOn?: any,
  invocation?: any
) {
  const result = CheckProfile();
  if (result == true) {
    const uniqueCellId = generateUniqueCellId(invocation.address);
    return new BenchmarkRankUnivFunctions().fnBurgissCalcPooledRankingIRRByCommitment(
      Universe,
      Commitment,
      StartDate,
      EndDate,
      Currency,
      SampleStatistic,
      RankValue,
      NumSimulations,
      DesiredSampleSize,
      RequireReportedValuation,
      StopOn,
      uniqueCellId
    );
  }
  return result;
}

/**
 * Computes the requested sample statistic of time-Weighted rate of return for simulated portfolios for an investment or multiple investments. Results are unscaled.
 * @CustomFunction PrivateiCalcPooledRankingTWRR.ByCommitment
 * @param [Universe] Peer group ID or Filter applied to Burgiss Manager Universe.
 * @param [Commitment]  Commitment for each group.
 * @param [ StartDate] Start Date that drives the measurement date for calculations. Date must be a valid BMU Start Date. Default date is inception if omitted.
 * @param [EndDate] End date that drives the measurement date for calculations. Date must be a valid Burgiss Manager Universe (BMU) analysis date. Default date is the latest BMU analysis date if omitted.
 * @param [Currency] Reflects the Currency of the amounts in which calculations are performed. Default is base if omitted.
 * @param [SampleStatistic] Requested sample statistic. Available values: Count, Mean, PercentileN where in N is requested percentile, Rank. The default is Percentile50 if omitted.
 * @param [RankValue] Value used to rank against simulated portfolios. Value will be calculated if omitted and rank requested.
 * @param [NumSimulations] Number of simulations. The default is 1000 if omitted.
 * @param [DesiredSampleSize] Desired sample size per period. The default is 5 if omitted.
 * @param [RequireReportedValuation] Denotes whether or not to only include BMU funds that have a reported valuation as of the Start Date and/or End Date. Default is false if omitted.
 * @param [StopOn] Specifies the level of severity in which to stop the simulation and return a message. The default is warning if omitted.
 * @param [AdvancedParameter] To enabled supported attributes for the formula.
 * @param {CustomFunctions.Invocation} invocation - The invocation context.
 * @helpUrl https://burgiss.docsend.com/view/4c3zt8i
 * @requiresAddress
 */
// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
function burgissCalcPooledRankingTWRRByCommitment(
  Universe: any,
  Commitment?: any,
  StartDate?: any,
  EndDate?: any,
  Currency?: string,
  SampleStatistic?: any,
  RankValue?: any,
  NumSimulations?: any,
  DesiredSampleSize?: any,
  RequireReportedValuation?: any,
  StopOn?: any,
  AdvancedParameter?: string,
  invocation?: any
) {
  const result = CheckProfile();
  if (result == true) {
    const uniqueCellId = generateUniqueCellId(invocation.address);
    return new BenchmarkRankUnivFunctions().fnBurgissCalcPooledRankingTWRRByCommitment(
      Universe,
      Commitment,
      StartDate,
      EndDate,
      Currency,
      SampleStatistic,
      RankValue,
      NumSimulations,
      DesiredSampleSize,
      RequireReportedValuation,
      StopOn,
      uniqueCellId,
      AdvancedParameter
    );
  }
  return result;
}

/**
 * Computes the requested sample statistic of DPI for simulated portfolios for an investment or multiple investments. Results are unscaled.
 * @CustomFunction PrivateiCalcPooledRankingDPI.ByCommitment
 * @param [Universe] Peer group ID or Filter applied to Burgiss Manager Universe.
 * @param [Commitment]  Commitment for each group.
 * @param [EndDate] End date that drives the measurement date for calculations. Date must be a valid Burgiss Manager Universe (BMU) analysis date. Default date is the latest BMU analysis date if omitted.
 * @param [Currency] Reflects the Currency of the amounts in which calculations are performed. Default is base if omitted.
 * @param [SampleStatistic] Requested sample statistic. Available values: Count, Mean, PercentileN where in N is requested percentile, Rank. The default is Percentile50 if omitted.
 * @param [RankValue] Value used to rank against simulated portfolios. Value will be calculated if omitted and rank requested.
 * @param [NumSimulations] Number of simulations. The default is 1000 if omitted.
 * @param [DesiredSampleSize] Desired sample size per period. The default is 5 if omitted.
 * @param [RequireReportedValuation] Denotes whether or not to only include BMU funds that have a reported valuation as of the Start Date and/or End Date. Default is false if omitted.
 * @param [StopOn] Specifies the level of severity in which to stop the simulation and return a message. The default is warning if omitted.
 * @param {CustomFunctions.Invocation} invocation - The invocation context.
 * @helpUrl https://burgiss.docsend.com/view/4c3zt8i
 * @requiresAddress
 */
// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
function burgissCalcPooledRankingDPIByCommitment(
  Universe: any,
  Commitment?: any,
  EndDate?: any,
  Currency?: string,
  SampleStatistic?: any,
  RankValue?: any,
  NumSimulations?: any,
  DesiredSampleSize?: any,
  RequireReportedValuation?: any,
  StopOn?: any,
  invocation?: any
) {
  const result = CheckProfile();
  if (result == true) {
    const uniqueCellId = generateUniqueCellId(invocation.address);
    return new BenchmarkRankUnivFunctions().fnBurgissCalcPooledRankingDPIByCommitment(
      Universe,
      Commitment,
      EndDate,
      Currency,
      SampleStatistic,
      RankValue,
      NumSimulations,
      DesiredSampleSize,
      RequireReportedValuation,
      StopOn,
      uniqueCellId
    );
  }
  return result;
}

/**
 * Computes the requested sample statistic of RVPI for simulated portfolios for an investment or multiple investments. Results are unscaled.
 * @CustomFunction PrivateiCalcPooledRankingRVPI.ByCommitment
 * @param [Universe] Peer group ID or Filter applied to Burgiss Manager Universe.
 * @param [Commitment]  Commitment for each group.
 * @param [EndDate] End date that drives the measurement date for calculations. Date must be a valid Burgiss Manager Universe (BMU) analysis date. Default date is the latest BMU analysis date if omitted.
 * @param [Currency] Reflects the Currency of the amounts in which calculations are performed. Default is base if omitted.
 * @param [SampleStatistic] Requested sample statistic. Available values: Count, Mean, PercentileN where in N is requested percentile, Rank. The default is Percentile50 if omitted.
 * @param [RankValue] Value used to rank against simulated portfolios. Value will be calculated if omitted and rank requested.
 * @param [NumSimulations] Number of simulations. The default is 1000 if omitted.
 * @param [DesiredSampleSize] Desired sample size per period. The default is 5 if omitted.
 * @param [RequireReportedValuation] Denotes whether or not to only include BMU funds that have a reported valuation as of the Start Date and/or End Date. Default is false if omitted.
 * @param [StopOn] Specifies the level of severity in which to stop the simulation and return a message. The default is warning if omitted.
 * @param {CustomFunctions.Invocation} invocation - The invocation context.
 * @helpUrl https://burgiss.docsend.com/view/4c3zt8i
 * @requiresAddress
 */
// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
function burgissCalcPooledRankingRVPIByCommitment(
  Universe: any,
  Commitment?: any,
  EndDate?: any,
  Currency?: string,
  SampleStatistic?: any,
  RankValue?: any,
  NumSimulations?: any,
  DesiredSampleSize?: any,
  RequireReportedValuation?: any,
  StopOn?: any,
  invocation?: any
) {
  const result = CheckProfile();
  if (result == true) {
    const uniqueCellId = generateUniqueCellId(invocation.address);
    return new BenchmarkRankUnivFunctions().fnBurgissCalcPooledRankingRVPIByCommitment(
      Universe,
      Commitment,
      EndDate,
      Currency,
      SampleStatistic,
      RankValue,
      NumSimulations,
      DesiredSampleSize,
      RequireReportedValuation,
      StopOn,
      uniqueCellId
    );
  }
  return result;
}

/**
 * Computes the requested sample statistic of internal rate of return for simulated portfolios for an investment or multiple investments. Results are unscaled.
 * @CustomFunction PrivateiCalcPooledRankingTVPI.ByCommitment
 * @param [Universe] Peer group ID or Filter applied to Burgiss Manager Universe.
 * @param [Commitment]  Commitment for each group.
 * @param [EndDate] End date that drives the measurement date for calculations. Date must be a valid Burgiss Manager Universe (BMU) analysis date. Default date is the latest BMU analysis date if omitted.
 * @param [Currency] Reflects the Currency of the amounts in which calculations are performed. Default is base if omitted.
 * @param [SampleStatistic] Requested sample statistic. Available values: Count, Mean, PercentileN where in N is requested percentile, Rank. The default is Percentile50 if omitted.
 * @param [RankValue] Value used to rank against simulated portfolios. Value will be calculated if omitted and rank requested.
 * @param [NumSimulations] Number of simulations. The default is 1000 if omitted.
 * @param [DesiredSampleSize] Desired sample size per period. The default is 5 if omitted.
 * @param [RequireReportedValuation] Denotes whether or not to only include BMU funds that have a reported valuation as of the Start Date and/or End Date. Default is false if omitted.
 * @param [StopOn] Specifies the level of severity in which to stop the simulation and return a message. The default is warning if omitted.
 * @param {CustomFunctions.Invocation} invocation - The invocation context.
 * @helpUrl https://burgiss.docsend.com/view/4c3zt8i
 * @requiresAddress
 */
// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
function burgissCalcPooledRankingTVPIByCommitment(
  Universe: any,
  Commitment?: any,
  EndDate?: any,
  Currency?: string,
  SampleStatistic?: any,
  RankValue?: any,
  NumSimulations?: any,
  DesiredSampleSize?: any,
  RequireReportedValuation?: any,
  StopOn?: any,
  invocation?: any
) {
  const result = CheckProfile();
  if (result == true) {
    const uniqueCellId = generateUniqueCellId(invocation.address);
    return new BenchmarkRankUnivFunctions().fnBurgissCalcPooledRankingTVPIByCommitment(
      Universe,
      Commitment,
      EndDate,
      Currency,
      SampleStatistic,
      RankValue,
      NumSimulations,
      DesiredSampleSize,
      RequireReportedValuation,
      StopOn,
      uniqueCellId
    );
  }
  return result;
}
//#endregion BenchmarkRankUniverse
//#region Holdings
/**
 * Calculates the pro-rata cost basis amount for holdings associated with an investment or group of investments.
 * @CustomFunction PrivateiHoldingCalcCostBasis
 * @param ID[][] ID(s) corresponding to the record type defined by the ID Type parameter. Use “All” to include all records. Default ID Type is Investment if ID Type is omitted.
 * @param [IdType] Describes the record type corresponding to values passed to the ID parameter. Default is Investment if omitted. See Burgiss Help for more details.
 * @param [BeginDate] Begin date that drives the measurement period for calculations. Default is inception if omitted.
 * @param [EndDate] End date that drives the measurement date for calculations. Actual measurement date can be affected by Roll Forward parameter. Default date is today if omitted.
 * @param [Currency] Reflects the Currency of the amounts in which calculations are performed. Default is base if omitted.
 * @param [SelectionSetID] Selection set filter applied to records denoted by the ID parameter. Default is no selection set if omitted.
 * @param [Filter] Filter applied to records denoted by the ID parameter. Filter is applied after selection set is applied. Default is no filter if omitted. See Burgiss Help for more details.
 * @param [ExcludeBalanceSheetItems] Option to exclude balance sheet items selection. Default is FALSE if omitted.
 * @param [ExcludeUndisclosedItems] Option to exclude undisclosed items from the selection. Default is FALSE if omitted.
 * @param [ExcludeUnknownEntities] Option to exclude unknown entities from the selection. Default is FALSE if omitted.
 * @param [ExcludRealizationspriorclosingdt] Option to exclude unknown entities from the selection. Default is FALSE if omitted.
 * @param {CustomFunctions.Invocation} invocation - The invocation context.
 * @helpUrl https://burgiss.docsend.com/view/4c3zt8i
 * @requiresAddress
 */
// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
function BurgissHoldingCalcCostBasis(
  ID?: any[][],
  IdType?: string,
  BeginDate?: any,
  EndDate?: any,
  Currency?: string,
  SelectionSetID?: any,
  Filters?: any,
  ExcludeBalanceSheetItems?: any,
  ExcludeUndisclosedItems?: any,
  ExcludeUnknownEntities?: any,
  ExcludRealizationspriorclosingdt?: any,
  invocation?: any
) {
  const result = CheckProfile();
  if (result == true) {
    const uniqueCellId = generateUniqueCellId(invocation.address);
    return new HoldingsFunctions().fnBurgissHoldingCalcCostBasis(
      ID,
      EndDate,
      Currency,
      BeginDate,
      IdType,
      SelectionSetID,
      Filters,
      ExcludeBalanceSheetItems,
      ExcludeUndisclosedItems,
      ExcludeUnknownEntities,
      ExcludRealizationspriorclosingdt,
      uniqueCellId
    );
  }
  return result;
}

/**
 * Calculates the pro-rata return of capital amount for holdings associated with an investment or group of investments.
 * @CustomFunction PrivateiHoldingCalcReturnOfCapital
 * @param ID[][] ID(s) corresponding to the record type defined by the ID Type parameter. Use “All” to include all records. Default ID Type is Investment if ID Type is omitted.
 * @param [IdType] Describes the record type corresponding to values passed to the ID parameter. Default is Investment if omitted. See Burgiss Help for more details.
 * @param [BeginDate] Begin date that drives the measurement period for calculations. Default is inception if omitted.
 * @param [EndDate] End date that drives the measurement date for calculations. Actual measurement date can be affected by Roll Forward parameter. Default date is today if omitted.
 * @param [Currency] Reflects the Currency of the amounts in which calculations are performed. Default is base if omitted.
 * @param [SelectionSetID] Selection set Filter applied to records denoted by the ID parameter. Default is no selection set if omitted.
 * @param [Filter] Filter applied to records denoted by the ID parameter. Filter is applied after selection set is applied. Default is no filter if omitted. See Burgiss Help for more details.
 * @param [ExcludeBalanceSheetItems] Option to exclude balance sheet items selection. Default is FALSE if omitted.
 * @param [ExcludeUndisclosedItems] Option to exclude undisclosed items from the selection. Default is FALSE if omitted.
 * @param [ExcludeUnknownEntities] Option to exclude unknown entities from the selection. Default is FALSE if omitted.
 * @param [ExcludRealizationspriorclosingdt] Option to exclude unknown entities from the selection. Default is FALSE if omitted.
 * @param {CustomFunctions.Invocation} invocation - The invocation context.
 * @helpUrl https://burgiss.docsend.com/view/4c3zt8i
 * @requiresAddress
 */
// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
function BurgissHoldingCalcReturnOfCapital(
  ID?: any[][],
  IdType?: string,
  BeginDate?: any,
  EndDate?: any,
  Currency?: string,
  SelectionSetID?: any,
  Filters?: any,
  ExcludeBalanceSheetItems?: any,
  ExcludeUndisclosedItems?: any,
  ExcludeUnknownEntities?: any,
  ExcludRealizationspriorclosingdt?: any,
  invocation?: any
) {
  const result = CheckProfile();
  if (result == true) {
    const uniqueCellId = generateUniqueCellId(invocation.address);
    return new HoldingsFunctions().fnBurgissHoldingCalcReturnOfCapital(
      ID,
      EndDate,
      Currency,
      BeginDate,
      IdType,
      SelectionSetID,
      Filters,
      ExcludeBalanceSheetItems,
      ExcludeUndisclosedItems,
      ExcludeUnknownEntities,
      ExcludRealizationspriorclosingdt,
      uniqueCellId
    );
  }
  return result;
}

/**
 * Calculates the pro-rata proceeds amount for holdings associated with an investment or group of investments.
 * @CustomFunction PrivateiHoldingCalcProceeds
 * @param ID[][] ID(s) corresponding to the record type defined by the ID Type parameter. Use “All” to include all records. Default ID Type is Investment if ID Type is omitted.
 * @param [IdType] Describes the record type corresponding to values passed to the ID parameter. Default is Investment if omitted. See Burgiss Help for more details.
 * @param [BeginDate] Begin date that drives the measurement period for calculations. Default is inception if omitted.
 * @param [EndDate] End date that drives the measurement date for calculations. Actual measurement date can be affected by Roll Forward parameter. Default date is today if omitted.
 * @param [Currency] Reflects the Currency of the amounts in which calculations are performed. Default is base if omitted.
 * @param [SelectionSetID] Selection set filter applied to records denoted by the ID parameter. Default is no selection set if omitted.
 * @param [Filter] Filter applied to records denoted by the ID parameter. Filter is applied after selection set is applied. Default is no filter if omitted. See Burgiss Help for more details.
 * @param [ExcludeBalanceSheetItems] Option to exclude balance sheet items selection. Default is FALSE if omitted.
 * @param [ExcludeUndisclosedItems] Option to exclude undisclosed items from the selection. Default is FALSE if omitted.
 * @param [ExcludeUnknownEntities] Option to exclude unknown entities from the selection. Default is FALSE if omitted.
 * @param [ExcludRealizationspriorclosingdt] Option to exclude unknown entities from the selection. Default is FALSE if omitted.
 * @param {CustomFunctions.Invocation} invocation - The invocation context.
 * @helpUrl https://burgiss.docsend.com/view/4c3zt8i
 * @requiresAddress
 */
// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
function BurgissHoldingCalcProceeds(
  ID?: any[][],
  IdType?: string,
  BeginDate?: any,
  EndDate?: any,
  Currency?: string,
  SelectionSetID?: any,
  Filters?: any,
  ExcludeBalanceSheetItems?: any,
  ExcludeUndisclosedItems?: any,
  ExcludeUnknownEntities?: any,
  ExcludRealizationspriorclosingdt?: any,
  invocation?: any
) {
  const result = CheckProfile();
  if (result == true) {
    const uniqueCellId = generateUniqueCellId(invocation.address);
    return new HoldingsFunctions().fnBurgissHoldingCalcProceeds(
      ID,
      EndDate,
      Currency,
      BeginDate,
      IdType,
      SelectionSetID,
      Filters,
      ExcludeBalanceSheetItems,
      ExcludeUndisclosedItems,
      ExcludeUnknownEntities,
      ExcludRealizationspriorclosingdt,
      uniqueCellId
    );
  }
  return result;
}

/**
 * Returns the pro-rata valuation amount for holdings associated with an investment or group of investments. Uses roll forward valuations where necessary.
 * @CustomFunction PrivateiHoldingCalcValuation
 * @param ID[][] ID(s) corresponding to the record type defined by the ID Type parameter. Use “All” to include all records. Default ID Type is Investment if ID Type is omitted.
 * @param [IdType] Describes the record type corresponding to values passed to the ID parameter. Default is Investment if omitted. See Burgiss Help for more details.
 * @param [EndDate] End date that drives the measurement date for calculations. Actual measurement date can be affected by Roll Forward parameter. Default date is today if omitted.
 * @param [RollForward] Methodology for determining the measurement date of the calculation. Default is FALSE if omitted. See Burgiss Help for more details.
 * @param [Currency] Reflects the Currency of the amounts in which calculations are performed. Default is base if omitted.
 * @param [SelectionSetID] Selection set filter applied to records denoted by the ID parameter. Default is no selection set if omitted.
 * @param [Filter] Filter applied to records denoted by the ID parameter. Filter is applied after selection set is applied. Default is no filter if omitted. See Burgiss Help for more details.
 * @param [ExcludeBalanceSheetItems] Option to exclude balance sheet items selection. Default is FALSE if omitted.
 * @param [ExcludeUndisclosedItems] Option to exclude undisclosed items from the selection. Default is FALSE if omitted.
 * @param [ExcludeUnknownEntities] Option to exclude unknown entities from the selection. Default is FALSE if omitted.
 * @param [ExcludRealizationspriorclosingdt] Option to exclude unknown entities from the selection. Default is FALSE if omitted.
 * @param {CustomFunctions.Invocation} invocation - The invocation context.
 * @helpUrl https://burgiss.docsend.com/view/4c3zt8i
 * @requiresAddress
 */
// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
function BurgissHoldingCalcValuation(
  ID?: any[][],
  IdType?: string,
  EndDate?: any,
  RollForward?: any,
  Currency?: string,
  SelectionSetID?: any,
  Filters?: any,
  ExcludeBalanceSheetItems?: any,
  ExcludeUndisclosedItems?: any,
  ExcludeUnknownEntities?: any,
  ExcludRealizationspriorclosingdt?: any,
  invocation?: any
) {
  const result = CheckProfile();
  if (result == true) {
    const uniqueCellId = generateUniqueCellId(invocation.address);
    return new HoldingsFunctions().fnBurgissHoldingCalcValuation(
      ID,
      EndDate,
      Currency,
      RollForward,
      IdType,
      SelectionSetID,
      Filters,
      ExcludeBalanceSheetItems,
      ExcludeUndisclosedItems,
      ExcludeUnknownEntities,
      ExcludRealizationspriorclosingdt,
      uniqueCellId
    );
  }
  return result;
}
/**
 * Returns the pro-rata valuation amount for holdings associated with an investment or group of investments. Uses roll forward valuations where necessary.
 * @CustomFunction PrivateiHoldingCalcValuation.Date
 * @param ID[][] ID(s) corresponding to the record type defined by the ID Type parameter. Use “All” to include all records. Default ID Type is Investment if ID Type is omitted.
 * @param [IdType] Describes the record type corresponding to values passed to the ID parameter. Default is Investment if omitted. See Burgiss Help for more details.
 * @param [EndDate] End date that drives the measurement date for calculations. Actual measurement date can be affected by Roll Forward parameter. Default date is today if omitted.
 * @param [RollForward] Methodology for determining the measurement date of the calculation. Default is FALSE if omitted. See Burgiss Help for more details.
 * @param [Currency] Reflects the Currency of the amounts in which calculations are performed. Default is base if omitted.
 * @param [SelectionSetID] Selection set filter applied to records denoted by the ID parameter. Default is no selection set if omitted.
 * @param [Filter] Filter applied to records denoted by the ID parameter. Filter is applied after selection set is applied. Default is no filter if omitted. See Burgiss Help for more details.
 * @param [ExcludeBalanceSheetItems] Option to exclude balance sheet items selection. Default is FALSE if omitted.
 * @param [ExcludeUndisclosedItems] Option to exclude undisclosed items from the selection. Default is FALSE if omitted.
 * @param [ExcludeUnknownEntities] Option to exclude unknown entities from the selection. Default is FALSE if omitted.
 * @param [ExcludRealizationspriorclosingdt] Option to exclude unknown entities from the selection. Default is FALSE if omitted.
 * @param {CustomFunctions.Invocation} invocation - The invocation context.
 * @helpUrl https://burgiss.docsend.com/view/4c3zt8i
 * @requiresAddress
 */
// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
function BurgissHoldingCalcValuationDate(
  ID?: any[][],
  IdType?: string,
  EndDate?: any,
  RollForward?: any,
  Currency?: string,
  SelectionSetID?: any,
  Filters?: any,
  ExcludeBalanceSheetItems?: any,
  ExcludeUndisclosedItems?: any,
  ExcludeUnknownEntities?: any,
  ExcludRealizationspriorclosingdt?: any,
  invocation?: any
) {
  const result = CheckProfile();
  if (result == true) {
    const uniqueCellId = generateUniqueCellId(invocation.address);
    return new HoldingsFunctions().fnBurgissHoldingCalcValuationDate(
      ID,
      EndDate,
      Currency,
      RollForward,
      IdType,
      SelectionSetID,
      Filters,
      ExcludeBalanceSheetItems,
      ExcludeUndisclosedItems,
      ExcludeUnknownEntities,
      ExcludRealizationspriorclosingdt,
      uniqueCellId
    );
  }
  return result;
}
/**
 * Calculates the modified internal rate of return for an investment or group of investments.
 * @CustomFunction PrivateiHoldingCalcDPI
 * @param ID[][] ID(s) corresponding to the record type defined by the ID Type parameter. Use “All” to include all records. Default ID Type is Investment if ID Type is omitted.
 * @param [IdType] Describes the record type corresponding to values passed to the ID parameter. Default is Investment if omitted. See Burgiss Help for more details.
 * @param [EndDate] End date that drives the measurement date for calculations. Actual measurement date can be affected by Roll Forward parameter. Default date is today if omitted.
 * @param [RollForward] Methodology for determining the measurement date of the calculation. Default is FALSE if omitted. See Burgiss Help for more details.
 * @param [Currency] Reflects the Currency of the amounts in which calculations are performed. Default is base if omitted.
 * @param [SelectionSetID] Selection set Filter applied to records denoted by the ID parameter. Default is no selection set if omitted.
 * @param [Filter] Filter applied to records denoted by the ID parameter. Filter is applied after selection set is applied. Default is no filter if omitted. See Burgiss Help for more details.
 * @param [ExcludeBalanceSheetItems] Option to exclude balance sheet items selection. Default is FALSE if omitted.
 * @param [ExcludeUndisclosedItems] Option to exclude undisclosed items from the selection. Default is FALSE if omitted.
 * @param [ExcludeUnknownEntities] Option to exclude unknown entities from the selection. Default is FALSE if omitted.
 * @param [ExcludRealizationspriorclosingdt] Option to exclude unknown entities from the selection. Default is FALSE if omitted.
 * @param {CustomFunctions.Invocation} invocation - The invocation context.
 * @helpUrl https://burgiss.docsend.com/view/4c3zt8i
 * @requiresAddress
 */
// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
function BurgissHoldingCalcDPI(
  ID?: any[][],
  IdType?: string,
  EndDate?: any,
  RollForward?: any,
  Currency?: string,
  SelectionSetID?: any,
  Filters?: any,
  ExcludeBalanceSheetItems?: any,
  ExcludeUndisclosedItems?: any,
  ExcludeUnknownEntities?: any,
  ExcludRealizationspriorclosingdt?: any,
  invocation?: any
) {
  const result = CheckProfile();
  if (result == true) {
    const uniqueCellId = generateUniqueCellId(invocation.address);
    return new HoldingsFunctions().fnBurgissHoldingCalcDPI(
      ID,
      EndDate,
      Currency,
      RollForward,
      IdType,
      SelectionSetID,
      Filters,
      ExcludeBalanceSheetItems,
      ExcludeUndisclosedItems,
      ExcludeUnknownEntities,
      ExcludRealizationspriorclosingdt,
      uniqueCellId
    );
  }
  return result;
}
/**
 * Calculates the modified internal rate of return for an investment or group of investments.
 * @CustomFunction PrivateiHoldingCalcRVPI
 * @param ID[][] ID(s) corresponding to the record type defined by the ID Type parameter. Use “All” to include all records. Default ID Type is Investment if ID Type is omitted.
 * @param [IdType] Describes the record type corresponding to values passed to the ID parameter. Default is Investment if omitted. See Burgiss Help for more details.
 * @param [EndDate] End date that drives the measurement date for calculations. Actual measurement date can be affected by Roll Forward parameter. Default date is today if omitted.
 * @param [RollForward] Methodology for determining the measurement date of the calculation. Default is FALSE if omitted. See Burgiss Help for more details.
 * @param [Currency] Reflects the Currency of the amounts in which calculations are performed. Default is base if omitted.
 * @param [SelectionSetID] Selection set filter applied to records denoted by the ID parameter. Default is no selection set if omitted.
 * @param [Filter] Filter applied to records denoted by the ID parameter. Filter is applied after selection set is applied. Default is no filter if omitted. See Burgiss Help for more details.
 * @param [ExcludeBalanceSheetItems] Option to exclude balance sheet items selection. Default is FALSE if omitted.
 * @param [ExcludeUndisclosedItems] Option to exclude undisclosed items from the selection. Default is FALSE if omitted.
 * @param [ExcludeUnknownEntities] Option to exclude unknown entities from the selection. Default is FALSE if omitted.
 * @param [ExcludRealizationspriorclosingdt] Option to exclude unknown entities from the selection. Default is FALSE if omitted.
 * @param {CustomFunctions.Invocation} invocation - The invocation context.
 * @helpUrl https://burgiss.docsend.com/view/4c3zt8i
 * @requiresAddress
 */
// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
function BurgissHoldingCalcRVPI(
  ID?: any[][],
  IdType?: string,
  EndDate?: any,
  RollForward?: any,
  Currency?: string,
  SelectionSetID?: any,
  Filters?: any,
  ExcludeBalanceSheetItems?: any,
  ExcludeUndisclosedItems?: any,
  ExcludeUnknownEntities?: any,
  ExcludRealizationspriorclosingdt?: any,
  invocation?: any
) {
  const result = CheckProfile();
  if (result == true) {
    const uniqueCellId = generateUniqueCellId(invocation.address);
    return new HoldingsFunctions().fnBurgissHoldingCalcRVPI(
      ID,
      EndDate,
      Currency,
      RollForward,
      IdType,
      SelectionSetID,
      Filters,
      ExcludeBalanceSheetItems,
      ExcludeUndisclosedItems,
      ExcludeUnknownEntities,
      ExcludRealizationspriorclosingdt,
      uniqueCellId
    );
  }
  return result;
}

/**
 * Calculates the modified internal rate of return for an investment or group of investments.
 * @CustomFunction PrivateiHoldingCalcTVPI
 * @param ID[][] ID(s) corresponding to the record type defined by the ID Type parameter. Use “All” to include all records. Default ID Type is Investment if ID Type is omitted.
 * @param [IdType] Describes the record type corresponding to values passed to the ID parameter. Default is Investment if omitted. See Burgiss Help for more details.
 * @param [EndDate] End date that drives the measurement date for calculations. Actual measurement date can be affected by Roll Forward parameter. Default date is today if omitted.
 * @param [RollForward] Methodology for determining the measurement date of the calculation. Default is FALSE if omitted. See Burgiss Help for more details.
 * @param [Currency] Reflects the Currency of the amounts in which calculations are performed. Default is base if omitted.
 * @param [SelectionSetID] Selection set filter applied to records denoted by the ID parameter. Default is no selection set if omitted.
 * @param [Filter] Filter applied to records denoted by the ID parameter. Filter is applied after selection set is applied. Default is no filter if omitted. See Burgiss Help for more details.
 * @param [ExcludeBalanceSheetItems] Option to exclude balance sheet items selection. Default is FALSE if omitted.
 * @param [ExcludeUndisclosedItems] Option to exclude undisclosed items from the selection. Default is FALSE if omitted.
 * @param [ExcludeUnknownEntities] Option to exclude unknown entities from the selection. Default is FALSE if omitted.
 * @param [ExcludRealizationspriorclosingdt] Option to exclude unknown entities from the selection. Default is FALSE if omitted.
 * @param {CustomFunctions.Invocation} invocation - The invocation context.
 * @helpUrl https://burgiss.docsend.com/view/4c3zt8i
 * @requiresAddress
 */
// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
function BurgissHoldingCalcTVPI(
  ID?: any[][],
  IdType?: string,
  EndDate?: any,
  RollForward?: any,
  Currency?: string,
  SelectionSetID?: any,
  Filters?: any,
  ExcludeBalanceSheetItems?: any,
  ExcludeUndisclosedItems?: any,
  ExcludeUnknownEntities?: any,
  ExcludRealizationspriorclosingdt?: any,
  invocation?: any
) {
  const result = CheckProfile();
  if (result == true) {
    const uniqueCellId = generateUniqueCellId(invocation.address);
    return new HoldingsFunctions().fnBurgissHoldingCalcTVPI(
      ID,
      EndDate,
      Currency,
      RollForward,
      IdType,
      SelectionSetID,
      Filters,
      ExcludeBalanceSheetItems,
      ExcludeUndisclosedItems,
      ExcludeUnknownEntities,
      ExcludRealizationspriorclosingdt,
      uniqueCellId
    );
  }
  return result;
}

/**
 * Calculates the pro-rata cost basis amount for holdings associated with an investment or group of investments.
 * @CustomFunction PrivateiHoldingCalcTotalInvested
 * @param ID[][] ID(s) corresponding to the record type defined by the ID Type parameter. Use “All” to include all records. Default ID Type is Investment if ID Type is omitted.
 * @param [IdType] Describes the record type corresponding to values passed to the ID parameter. Default is Investment if omitted. See Burgiss Help for more details.
 * @param [BeginDate] Begin date that drives the measurement period for calculations. Default is inception if omitted.
 * @param [EndDate] End date that drives the measurement date for calculations. Actual measurement date can be affected by Roll Forward parameter. Default date is today if omitted.
 * @param [Currency] Reflects the Currency of the amounts in which calculations are performed. Default is base if omitted.
 * @param [SelectionSetID] Selection set filter applied to records denoted by the ID parameter. Default is no selection set if omitted.
 * @param [Filter] Filter applied to records denoted by the ID parameter. Filter is applied after selection set is applied. Default is no filter if omitted. See Burgiss Help for more details.
 * @param [ExcludeBalanceSheetItems] Option to exclude balance sheet items selection. Default is FALSE if omitted.
 * @param [ExcludeUndisclosedItems] Option to exclude undisclosed items from the selection. Default is FALSE if omitted.
 * @param [ExcludeUnknownEntities] Option to exclude unknown entities from the selection. Default is FALSE if omitted.
 * @param [ExcludRealizationspriorclosingdt] Option to exclude unknown entities from the selection. Default is FALSE if omitted.
 * @param {CustomFunctions.Invocation} invocation - The invocation context.
 * @helpUrl https://burgiss.docsend.com/view/4c3zt8i
 * @requiresAddress
 */
// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
function BurgissHoldingCalcTotalInvested(
  ID?: any[][],
  IdType?: string,
  BeginDate?: any,
  EndDate?: any,
  Currency?: string,
  SelectionSetID?: any,
  Filters?: any,
  ExcludeBalanceSheetItems?: any,
  ExcludeUndisclosedItems?: any,
  ExcludeUnknownEntities?: any,
  ExcludRealizationspriorclosingdt?: any,
  invocation?: any
) {
  const result = CheckProfile();
  if (result == true) {
    const uniqueCellId = generateUniqueCellId(invocation.address);
    return new HoldingsFunctions().fnBurgissHoldingCalcTotalInvested(
      ID,
      EndDate,
      Currency,
      BeginDate,
      IdType,
      SelectionSetID,
      Filters,
      ExcludeBalanceSheetItems,
      ExcludeUndisclosedItems,
      ExcludeUnknownEntities,
      ExcludRealizationspriorclosingdt,
      uniqueCellId
    );
  }
  return result;
}
//#endregion Holdings
//#region Measures
/**
 * Calculates the time-Weighted rate of return for an investment or group of investments.
 * @CustomFunction PrivateiCalcCostBasisFXGainLoss
 * @param ID[][]
 * @param [BeginDate] Begin date that drives the measurement period for calculations. Default is inception if omitted.
 * @param [EndDate] End date that drives the measurement date for calculations. Default date is today if omitted.
 * @param [Currency] Reflects the Currency of the amounts in which calculations are performed. Default is base if omitted.
 * @param [RollForward] Methodology for determining the measurement date of the calculation. Default is FALSE if omitted. See Burgiss Help for more details.
 * @param [IdType] Describes the record type corresponding to values passed to the ID parameter. Default is Investment if omitted. See Burgiss Help for more details.
 * @param [SelectionSetID] Selection set filter applied to records denoted by the ID parameter. Default is no selection set if omitted
 * @param [Filters] Filter applied to records denoted by the ID parameter. Filter is applied after selection set is applied. Default is no filter if omitted. See Burgiss Help for more details.
 * @param [AdvancedParameter]  To enabled supported attributes for the formula.
 * @param {CustomFunctions.Invocation} invocation - The invocation context.
 * @helpUrl https://burgiss.docsend.com/view/4c3zt8i
 * @requiresAddress
 */
// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
function BurgissCalcCostBasisFXGainLoss(
  ID?: any[][],
  BeginDate?: any,
  EndDate?: any,
  Currency?: string,
  RollForward?: string,
  IdType?: string,
  SelectionSetID?: any,
  Filters?: any,
  AdvancedParameter?: string,
  invocation?: any
) {
  const result = CheckProfile();
  if (result == true) {
    const uniqueCellId = generateUniqueCellId(invocation.address);
    return new MeasuresFunctions().fnBurgissCalcCostBasisFXGainLoss(
      ID,
      BeginDate,
      EndDate,
      Currency,
      RollForward,
      IdType,
      SelectionSetID,
      Filters,
      AdvancedParameter,
      uniqueCellId
    );
  }
  return result;
}

/**
 * Calculates the time-Weighted rate of return for an investment or group of investments.
 * @CustomFunction PrivateiCalcTWRR
 * @param ID[][]
 * @param [EndDate] End date that drives the measurement date for calculations. Default date is today if omitted.
 * @param [Currency] Reflects the Currency of the amounts in which calculations are performed. Default is base if omitted.
 * @param [RollForward] Methodology for determining the measurement date of the calculation. Default is FALSE if omitted. See Burgiss Help for more details.
 * @param [BeginDate] Begin date that drives the measurement period for calculations. Default is inception if omitted.
 * @param [IdType] Describes the record type corresponding to values passed to the ID parameter. Default is Investment if omitted. See Burgiss Help for more details.
 * @param [SelectionSetID] Selection set filter applied to records denoted by the ID parameter. Default is no selection set if omitted
 * @param [Filters] Filter applied to records denoted by the ID parameter. Filter is applied after selection set is applied. Default is no filter if omitted. See Burgiss Help for more details.
 * @param [AdvancedParameter]  To enabled supported attributes for the formula.
 * @param {CustomFunctions.Invocation} invocation - The invocation context.
 * @helpUrl https://burgiss.docsend.com/view/4c3zt8i
 * @requiresAddress
 */
// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
function BurgissCalcTWRR(
  ID?: any[][],
  EndDate?: any,
  Currency?: string,
  RollForward?: string,
  BeginDate?: any,
  IdType?: string,
  SelectionSetID?: any,
  Filters?: any,
  AdvancedParameter?: string,
  invocation?: any
) {
  const result = CheckProfile();
  if (result == true) {
    const uniqueCellId = generateUniqueCellId(invocation.address);
    return new MeasuresFunctions().fnBurgissCalcTWRR(
      ID,
      EndDate,
      Currency,
      RollForward,
      BeginDate,
      IdType,
      SelectionSetID,
      Filters,
      AdvancedParameter,
      uniqueCellId
    );
  }
  return result;
}

/**
 * Advanced Analytics license required - Calculates the Kaplan-Schoar PME for an investment or group of investments.", Category = "BurgissIndex-Calculations
 * @CustomFunction PrivateiCalcCostBasis
 * @param ID[][]
 * @param [BeginDate] Begin date that drives the measurement period for calculations. Default is inception if omitted.
 * @param [EndDate] End date that drives the measurement date for calculations. Default date is today if omitted.
 * @param [Currency] Reflects the Currency of the amounts in which calculations are performed. Default is base if omitted.
 * @param [RollForward] Methodology for determining the measurement date of the calculation. Default is FALSE if omitted. See Burgiss Help for more details.
 * @param [IdType] Describes the record type corresponding to values passed to the ID parameter. Default is Investment if omitted. See Burgiss Help for more details.
 * @param [SelectionSetID] Selection set filter applied to records denoted by the ID parameter. Default is no selection set if omitted
 * @param [Filters] Filter applied to records denoted by the ID parameter. Filter is applied after selection set is applied. Default is no filter if omitted. See Burgiss Help for more details.
 * @param [AdvancedParameter]  To enabled supported attributes for the formula.
 * @param {CustomFunctions.Invocation} invocation - The invocation context.
 * @helpUrl https://burgiss.docsend.com/view/4c3zt8i
 * @requiresAddress
 */
// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
function BurgissCalcCostBasis(
  ID: any[][],
  BeginDate?: any,
  EndDate?: any,
  Currency?: string,
  RollForward?: string,
  IdType?: string,
  SelectionSetID?: any,
  Filters?: any,
  AdvancedParameter?: string,
  invocation?: any
) {
  const result = CheckProfile();
  if (result == true) {
    const uniqueCellId = generateUniqueCellId(invocation.address);
    return new MeasuresFunctions().fnBurgissCalcCostBasis(
      ID,
      BeginDate,
      EndDate,
      Currency,
      RollForward,
      IdType,
      SelectionSetID,
      Filters,
      AdvancedParameter,
      uniqueCellId
    );
  }
  return result;
}

/**
 * Calculates the total funding amount for an investment or group of investments.
 * @CustomFunction PrivateiCalcFunding
 * @param ID[][]
 * @param [EndDate] End date that drives the measurement date for calculations. Default date is today if omitted.
 * @param [Currency] Reflects the Currency of the amounts in which calculations are performed. Default is base if omitted.
 * @param [BeginDate] Begin date that drives the measurement period for calculations. Default is inception if omitted.
 * @param [IdType] Describes the record type corresponding to values passed to the ID parameter. Default is Investment if omitted. See Burgiss Help for more details.
 * @param [SelectionSetID] Selection set filter applied to records denoted by the ID parameter. Default is no selection set if omitted
 * @param [Filters] Filter applied to records denoted by the ID parameter. Filter is applied after selection set is applied. Default is no filter if omitted. See Burgiss Help for more details.
 * @param [AdvancedParameter] To enabled supported attributes for the formula.
 * @param {CustomFunctions.Invocation} invocation - The invocation context.
 * @helpUrl https://burgiss.docsend.com/view/4c3zt8i
 * @requiresAddress
 */

// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
function BurgissCalcFunding(
  ID?: any[][],
  EndDate?: any,
  Currency?: string,
  BeginDate?: any,
  IdType?: string,
  SelectionSetID?: any,
  Filters?: any,
  AdvancedParameter?: string,
  invocation?: any
) {
  const result = CheckProfile();
  if (result == true) {
    const uniqueCellId = generateUniqueCellId(invocation.address);
    return new MeasuresFunctions().fnBurgissCalcFunding(
      ID,
      EndDate,
      Currency,
      BeginDate,
      IdType,
      SelectionSetID,
      Filters,
      AdvancedParameter,
      uniqueCellId
    );
  }
  return result;
}

/**
 * Calculates the total paid-in amount for an investment or a group of investments.The calculation includes both funding and fee ledger entries.
 * @CustomFunction PrivateiCalcPaidIn
 * @param ID[][] Investment ID.
 * @param [EndDate] End date that drives the measurement date for calculations. Default date is today if omitted.
 * @param [Currency] Reflects the Currency of the amounts in which calculations are performed. Default is base if omitted.
 * @param [BeginDate] Begin date that drives the measurement period for calculations. Default is inception if omitted.
 * @param [IdType] Describes the record type corresponding to values passed to the ID parameter. Default is Investment if omitted. See Burgiss Help for more details.
 * @param [SelectionSetID] Selection set filter applied to records denoted by the ID parameter. Default is no selection set if omitted
 * @param [Filters]  Filter applied to records denoted by the ID parameter. Filter is applied after selection set is applied. Default is no filter if omitted. See Burgiss Help for more details.
 * @param [AdvancedParameter] To enabled supported attributes for the formula.
 * @param {CustomFunctions.Invocation} invocation - The invocation context.
 * @helpUrl https://burgiss.docsend.com/view/4c3zt8i
 * @requiresAddress
 */
// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
function BurgissCalcPaidIn(
  ID?: any[][],
  EndDate?: any,
  Currency?: string,
  BeginDate?: any,
  IdType?: string,
  SelectionSetID?: any,
  Filters?: any,
  AdvancedParameter?: string,
  invocation?: any
) {
  const result = CheckProfile();
  if (result == true) {
    const uniqueCellId = generateUniqueCellId(invocation.address);
    return new MeasuresFunctions().fnBurgissCalcPaidIn(
      ID,
      EndDate,
      Currency,
      BeginDate,
      IdType,
      SelectionSetID,
      Filters,
      AdvancedParameter,
      uniqueCellId
    );
  }
  return result;
}

/**
 * Returns the fund size amount for the asset associated with an investment.
 * @CustomFunction PrivateiCalcFundSize
 * @param ID[][] Investment ID.
 * @param [EndDate] End date that drives the measurement date for calculations. Date must be a valid BMU analysis date. Default date is the latest BMU analysis date if omitted.
 * @param [Currency] Reflects the Currency of the amounts in which calculations are performed. Default is base if omitted.
 * @param [UseAlternateFundSize] Denotes the alternate fund size entries set by users. Default is FALSE if omitted..
 * @param {CustomFunctions.Invocation} invocation - The invocation context.
 * @helpUrl https://burgiss.docsend.com/view/4c3zt8i
 * @requiresAddress
 */
// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
function BurgissCalcFundSize(
  ID?: any[][],
  EndDate?: any,
  Currency?: any,
  UseAlternateFundSize?: any,
  invocation?: any
) {
  const result = CheckProfile();
  if (result == true) {
    const uniqueCellId = generateUniqueCellId(invocation.address);
    return new MeasuresFunctions().fnBurgissCalcFundSize(ID, EndDate, Currency, UseAlternateFundSize, uniqueCellId);
  }
  return result;
}

/**
 * Calculates the total recallable capital portion of distributions for an investment or group of investments. The calculation includes both cash and stock ledger entries.
 * @CustomFunction PrivateiCalcDistributions.RecallableCapital
 * @param ID[][] ID(s) corresponding to the record type defined by the ID Type parameter. Use “All” to include all records. Default ID Type is Investment if ID Type is omitted.
 * @param [EndDate] End date that drives the measurement date for calculations. Default date is today if omitted.
 * @param [Currency] Reflects the Currency of the amounts in which calculations are performed. Default is base if omitted.
 * @param [BeginDate] Begin date that drives the measurement period for calculations. Default is inception if omitted.
 * @param [IdType] Describes the record type corresponding to values passed to the ID parameter. Default is Investment if omitted. See Burgiss Help for more details
 * @param [SelectionSetId] Selection set filter applied to records denoted by the ID parameter. Default is no selection set if omitted
 * @param[Filter] Filter applied to records denoted by the ID parameter. Filter is applied after selection set is applied. Default is no filter if omitted. See Burgiss Help for more details.
 * @param[AdvancedParameter] To enabled supported attributes for the formula.
 * @param {CustomFunctions.Invocation} invocation - The invocation context.
 * @helpUrl https://burgiss.docsend.com/view/4c3zt8i
 * @requiresAddress
 */
// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
function BurgissCalcDistributionsRecallableCapital(
  ID: any[][],
  EndDate?: any,
  Currency?: string,
  BeginDate?: any,
  IdType?: string,
  SelectionSetId?: any,
  Filter?: any,
  AdvancedParameter?: string,
  invocation?: any
) {
  const result = CheckProfile();
  if (result == true) {
    const uniqueCellId = generateUniqueCellId(invocation.address);
    return new MeasuresFunctions().fnBurgissCalcDistributionsRecallableCapital(
      ID,
      EndDate,
      Currency,
      BeginDate,
      IdType,
      SelectionSetId,
      Filter,
      AdvancedParameter,
      uniqueCellId
    );
  }
  return result;
}

/**
 * Calculates the total other portion of distributions for an investment or group of investments. The calculation includes both cash and stock ledger entries.
 * @CustomFunction PrivateiCalcDistributions.Other
 * @param ID[][] ID(s) corresponding to the record type defined by the ID Type parameter. Use “All” to include all records. Default ID Type is Investment if ID Type is omitted.
 * @param [EndDate] End date that drives the measurement date for calculations. Default date is today if omitted.
 * @param [Currency] Reflects the Currency of the amounts in which calculations are performed. Default is base if omitted.
 * @param [BeginDate] Begin date that drives the measurement period for calculations. Default is inception if omitted.
 * @param [IdType] Describes the record type corresponding to values passed to the ID parameter. Default is Investment if omitted. See Burgiss Help for more details
 * @param [SelectionSetID] Selection set filter applied to records denoted by the ID parameter. Default is no selection set if omitted
 * @param[Filter] Filter applied to records denoted by the ID parameter. Filter is applied after selection set is applied. Default is no filter if omitted. See Burgiss Help for more details.
 * @param[AdvancedParameter] To enabled supported attributes for the formula.
 * @param {CustomFunctions.Invocation} invocation - The invocation context.
 * @helpUrl https://burgiss.docsend.com/view/4c3zt8i
 * @requiresAddress
 */
// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
function BurgissCalcDistributionsOther(
  ID: any[][],
  EndDate?: any,
  Currency?: string,
  BeginDate?: any,
  IdType?: string,
  SelectionSetId?: any,
  Filter?: any,
  AdvancedParameter?: string,
  invocation?: any
) {
  const result = CheckProfile();
  if (result == true) {
    const uniqueCellId = generateUniqueCellId(invocation.address);
    return new MeasuresFunctions().fnBurgissCalcDistributionsOther(
      ID,
      EndDate,
      Currency,
      BeginDate,
      IdType,
      SelectionSetId,
      Filter,
      AdvancedParameter,
      uniqueCellId
    );
  }
  return result;
}

/**
 * Calculates cumulative return of capital portion of distributions for an investment or group of investments. The calculation includes both cash and stock ledger entries.
 * @CustomFunction PrivateiCalcDistributions.ReturnOfCapital
 * @param ID[][] ID(s) corresponding to the record type defined by the ID Type parameter. Use “All” to include all records. Default ID Type is Investment if ID Type is omitted.
 * @param [EndDate] End date that drives the measurement date for calculations. Default date is today if omitted.
 * @param [Currency] Reflects the Currency of the amounts in which calculations are performed. Default is base if omitted.
 * @param [BeginDate] Begin date that drives the measurement period for calculations. Default is inception if omitted.
 * @param [IdType] Describes the record type corresponding to values passed to the ID parameter. Default is Investment if omitted. See Burgiss Help for more details
 * @param [SelectionSetId] Selection set filter applied to records denoted by the ID parameter. Default is no selection set if omitted
 * @param[Filter] Filter applied to records denoted by the ID parameter. Filter is applied after selection set is applied. Default is no filter if omitted. See Burgiss Help for more details.
 * @param[AdvancedParameter] To enabled supported attributes for the formula.
 * @param {CustomFunctions.Invocation} invocation - The invocation context.
 * @helpUrl https://burgiss.docsend.com/view/4c3zt8i
 * @requiresAddress
 */
// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
function BurgissCalcDistributionsReturnofCapital(
  ID: any[][],
  EndDate?: any,
  Currency?: string,
  BeginDate?: any,
  IdType?: string,
  SelectionSetId?: any,
  Filter?: any,
  AdvancedParameter?: string,
  invocation?: any
) {
  const result = CheckProfile();
  if (result == true) {
    const uniqueCellId = generateUniqueCellId(invocation.address);
    return new MeasuresFunctions().fnBurgissCalcDistributionsReturnofCapital(
      ID,
      EndDate,
      Currency,
      BeginDate,
      IdType,
      SelectionSetId,
      Filter,
      AdvancedParameter,
      uniqueCellId
    );
  }
  return result;
}

/**
 * Calculates cumulative capital gains  for portion of distributions an investment or group of investments. The calculation includes both cash and stock ledger entries.
 * @CustomFunction PrivateiCalcDistributions.CapitalGains
 * @param ID[][] ID(s) corresponding to the record type defined by the ID Type parameter. Use “All” to include all records. Default ID Type is Investment if ID Type is omitted.
 * @param [EndDate] End date that drives the measurement date for calculations. Default date is today if omitted.
 * @param [Currency] Reflects the Currency of the amounts in which calculations are performed. Default is base if omitted.
 * @param [BeginDate] Begin date that drives the measurement period for calculations. Default is inception if omitted.
 * @param [IdType] Describes the record type corresponding to values passed to the ID parameter. Default is Investment if omitted. See Burgiss Help for more details
 * @param [SelectionSetId] Selection set filter applied to records denoted by the ID parameter. Default is no selection set if omitted
 * @param [Filter] Filter applied to records denoted by the ID parameter. Filter is applied after selection set is applied. Default is no filter if omitted. See Burgiss Help for more details
 * @param [AdvancedParameter] To enabled supported attributes for the formula
 * @param {CustomFunctions.Invocation} invocation - The invocation context.
 * @helpUrl https://burgiss.docsend.com/view/4c3zt8i
 * @requiresAddress
 */
// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
function BurgissCalcDistributionsCapitalgains(
  ID: any[][],
  EndDate?: any,
  Currency?: string,
  BeginDate?: any,
  IdType?: string,
  SelectionSetId?: any,
  Filter?: any,
  AdvancedParameter?: string,
  invocation?: any
) {
  const result = CheckProfile();
  if (result == true) {
    const uniqueCellId = generateUniqueCellId(invocation.address);
    return new MeasuresFunctions().fnBurgissCalcDistributionsCapitalgains(
      ID,
      EndDate,
      Currency,
      BeginDate,
      IdType,
      SelectionSetId,
      Filter,
      AdvancedParameter,
      uniqueCellId
    );
  }
  return result;
}

/**
 * Calculates cumulative income  for an portion of distributions investment or group of investments. The calculation includes both cash and stock ledger entry.
 * @CustomFunction PrivateiCalcDistributions.Income
 * @param ID[][] corresponding to the record type defined by the ID Type parameter. Use “All” to include all records. Default ID Type is Investment if ID Type is omitted.
 * @param [EndDate] End date that drives the measurement date for calculations. Default date is today if omitted.
 * @param [Currency] Reflects the Currency of the amounts in which calculations are performed. Default is base if omitted.
 * @param [BeginDate] Begin date that drives the measurement period for calculations. Default is inception if omitted.
 * @param [IdType] Describes the record type corresponding to values passed to the ID parameter. Default is Investment if omitted. See Burgiss Help for more details
 * @param [SelectionSetId] Selection set filter applied to records denoted by the ID parameter. Default is no selection set if omitted
 * @param [Filter] Filter applied to records denoted by the ID parameter. Filter is applied after selection set is applied. Default is no filter if omitted. See Burgiss Help for more details.
 * @param [AdvancedParameter] To enabled supported attributes for the formula.
 * @param {CustomFunctions.Invocation} invocation - The invocation context.
 * @helpUrl https://burgiss.docsend.com/view/4c3zt8i
 * @requiresAddress
 */
// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
function BurgissCalcDistributionsIncome(
  ID: any[][],
  EndDate?: any,
  Currency?: string,
  BeginDate?: any,
  IdType?: string,
  SelectionSetId?: any,
  Filter?: any,
  AdvancedParameter?: string,
  invocation?: any
) {
  const result = CheckProfile();
  if (result == true) {
    const uniqueCellId = generateUniqueCellId(invocation.address);
    return new MeasuresFunctions().fnBurgissCalcDistributionsIncome(
      ID,
      EndDate,
      Currency,
      BeginDate,
      IdType,
      SelectionSetId,
      Filter,
      AdvancedParameter,
      uniqueCellId
    );
  }
  return result;
}

/**
 * Calculates the total cash amount for an investment or group of investments.
 * @CustomFunction PrivateiCalcCash.Other
 * @param ID[][] Investment ID.
 * @param [EndDate] End date that drives the measurement date for calculations. Default date is today if omitted.
 * @param [Currency] Reflects the Currency of the amounts in which calculations are performed. Default is base if omitted.
 * @param [BeginDate] Begin date that drives the measurement period for calculations. Default is inception if omitted.
 * @param [IdType] Describes the record type corresponding to values passed to the ID parameter. Default is Investment if omitted. See Burgiss Help for more details.
 * @param [SelectionSetID] Selection set filter applied to records denoted by the ID parameter. Default is no selection set if omitted
 * @param [Filters]  Filter applied to records denoted by the ID parameter. Filter is applied after selection set is applied. Default is no filter if omitted. See Burgiss Help for more details.
 * @param [AdvancedParameter] To enabled supported attributes for the formula.
 * @param {CustomFunctions.Invocation} invocation - The invocation context.
 * @helpUrl https://burgiss.docsend.com/view/4c3zt8i
 * @requiresAddress
 */
// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
function BurgissCalcCashOther(
  ID?: any[][],
  EndDate?: any,
  Currency?: string,
  BeginDate?: any,
  IdType?: string,
  SelectionSetID?: any,
  Filters?: any,
  AdvancedParameter?: string,
  invocation?: any
) {
  const result = CheckProfile();
  if (result == true) {
    const uniqueCellId = generateUniqueCellId(invocation.address);
    return new MeasuresFunctions().fnBurgissCalcCashOther(
      ID,
      EndDate,
      Currency,
      BeginDate,
      IdType,
      SelectionSetID,
      Filters,
      AdvancedParameter,
      uniqueCellId
    );
  }
  return result;
}

/**
 * Calculates the total return of capital portion of cash distributions for an investment or group of investments.
 * @CustomFunction PrivateiCalcCash.ReturnOfCapital
 * @param ID[][] Investment ID.
 * @param [EndDate] End date that drives the measurement date for calculations. Default date is today if omitted.
 * @param [Currency] Reflects the Currency of the amounts in which calculations are performed. Default is base if omitted.
 * @param [BeginDate] Begin date that drives the measurement period for calculations. Default is inception if omitted.
 * @param [IdType] Describes the record type corresponding to values passed to the ID parameter. Default is Investment if omitted. See Burgiss Help for more details.
 * @param [SelectionSetID] Selection set filter applied to records denoted by the ID parameter. Default is no selection set if omitted
 * @param [Filters]  Filter applied to records denoted by the ID parameter. Filter is applied after selection set is applied. Default is no filter if omitted. See Burgiss Help for more details.
 * @param [AdvancedParameter] To enabled supported attributes for the formula.
 * @param {CustomFunctions.Invocation} invocation - The invocation context.
 * @helpUrl https://burgiss.docsend.com/view/4c3zt8i
 * @requiresAddress
 */
// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
function BurgissCalcCashReturnofCapital(
  ID?: any[][],
  EndDate?: any,
  Currency?: string,
  BeginDate?: any,
  IdType?: string,
  SelectionSetID?: any,
  Filters?: any,
  AdvancedParameter?: string,
  invocation?: any
) {
  const result = CheckProfile();
  if (result == true) {
    const uniqueCellId = generateUniqueCellId(invocation.address);
    return new MeasuresFunctions().fnBurgissCalcCashReturnofCapital(
      ID,
      EndDate,
      Currency,
      BeginDate,
      IdType,
      SelectionSetID,
      Filters,
      AdvancedParameter,
      uniqueCellId
    );
  }
  return result;
}

/**
 * Calculates the total cash amount for an investment or group of investments.
 * @CustomFunction PrivateiCalcCash.CapitalGains
 * @param ID[][] Investment ID.
 * @param [EndDate] End date that drives the measurement date for calculations. Default date is today if omitted.
 * @param [Currency] Reflects the Currency of the amounts in which calculations are performed. Default is base if omitted.
 * @param [BeginDate] Begin date that drives the measurement period for calculations. Default is inception if omitted.
 * @param [IdType] Describes the record type corresponding to values passed to the ID parameter. Default is Investment if omitted. See Burgiss Help for more details.
 * @param [SelectionSetID] Selection set filter applied to records denoted by the ID parameter. Default is no selection set if omitted
 * @param [Filters]  Filter applied to records denoted by the ID parameter. Filter is applied after selection set is applied. Default is no filter if omitted. See Burgiss Help for more details.
 * @param [AdvancedParameter] To enabled supported attributes for the formula.
 * @param {CustomFunctions.Invocation} invocation - The invocation context.
 * @helpUrl https://burgiss.docsend.com/view/4c3zt8i
 * @requiresAddress
 */
// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
function BurgissCashCapitalGains(
  ID?: any[][],
  EndDate?: any,
  Currency?: string,
  BeginDate?: any,
  IdType?: string,
  SelectionSetID?: any,
  Filters?: any,
  AdvancedParameter?: string,
  invocation?: any
) {
  const result = CheckProfile();
  if (result == true) {
    const uniqueCellId = generateUniqueCellId(invocation.address);
    return new MeasuresFunctions().fnBurgissCashCapitalGains(
      ID,
      EndDate,
      Currency,
      BeginDate,
      IdType,
      SelectionSetID,
      Filters,
      AdvancedParameter,
      uniqueCellId
    );
  }
  return result;
}

/**
 * Calculates the total cash amount for an investment or group of investments.
 * @CustomFunction PrivateiCalcCash.Income
 * @param ID[][] Investment ID.
 * @param [EndDate] End date that drives the measurement date for calculations. Default date is today if omitted.
 * @param [Currency] Reflects the Currency of the amounts in which calculations are performed. Default is base if omitted.
 * @param [BeginDate] Begin date that drives the measurement period for calculations. Default is inception if omitted.
 * @param [IdType] Describes the record type corresponding to values passed to the ID parameter. Default is Investment if omitted. See Burgiss Help for more details.
 * @param [SelectionSetID] Selection set filter applied to records denoted by the ID parameter. Default is no selection set if omitted
 * @param [Filters]  Filter applied to records denoted by the ID parameter. Filter is applied after selection set is applied. Default is no filter if omitted. See Burgiss Help for more details.
 * @param [AdvancedParameter] To enabled supported attributes for the formula.
 * @param {CustomFunctions.Invocation} invocation - The invocation context.
 * @helpUrl https://burgiss.docsend.com/view/4c3zt8i
 * @requiresAddress
 */
// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
function BurgissCalcCashIncome(
  ID?: any[][],
  EndDate?: any,
  Currency?: string,
  BeginDate?: any,
  IdType?: string,
  SelectionSetID?: any,
  Filters?: any,
  AdvancedParameter?: string,
  invocation?: any
) {
  const result = CheckProfile();
  if (result == true) {
    const uniqueCellId = generateUniqueCellId(invocation.address);
    return new MeasuresFunctions().fnBurgissCalcCashIncome(
      ID,
      EndDate,
      Currency,
      BeginDate,
      IdType,
      SelectionSetID,
      Filters,
      AdvancedParameter,
      uniqueCellId
    );
  }
  return result;
}

/**
 * Calculates the total return of capital portion of cash distributions for an investment or group of investments.
 * @CustomFunction PrivateiCalcCash.RecallableCapital

* @param ID[][] Investment ID.
 * @param [EndDate] End date that drives the measurement date for calculations. Default date is today if omitted.
 * @param [Currency] Reflects the Currency of the amounts in which calculations are performed. Default is base if omitted.
 * @param [BeginDate] Begin date that drives the measurement period for calculations. Default is inception if omitted.
 * @param [IdType] Describes the record type corresponding to values passed to the ID parameter. Default is Investment if omitted. See Burgiss Help for more details.
 * @param [SelectionSetID] Selection set filter applied to records denoted by the ID parameter. Default is no selection set if omitted
 * @param [Filters]  Filter applied to records denoted by the ID parameter. Filter is applied after selection set is applied. Default is no filter if omitted. See Burgiss Help for more details.
 * @param [AdvancedParameter] To enabled supported attributes for the formula.
 * @param {CustomFunctions.Invocation} invocation - The invocation context.
 * @helpUrl https://burgiss.docsend.com/view/4c3zt8i
 * @requiresAddress
 */
// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
function BurgissCalcCashRecallableCapital(
  ID?: any[][],
  EndDate?: any,
  Currency?: string,
  BeginDate?: any,
  IdType?: string,
  SelectionSetID?: any,
  Filters?: any,
  AdvancedParameter?: string,
  invocation?: any
) {
  const result = CheckProfile();
  if (result == true) {
    const uniqueCellId = generateUniqueCellId(invocation.address);
    return new MeasuresFunctions().fnBurgissCalcCashRecallableCapital(
      ID,
      EndDate,
      Currency,
      BeginDate,
      IdType,
      SelectionSetID,
      Filters,
      AdvancedParameter,
      uniqueCellId
    );
  }
  return result;
}

/**
 * Returns the date of the most recent valuation for an investment or a group of investments relative to the End Date.
 * @CustomFunction PrivateiCalcValuation.Date
 * @param ID[][] ID(s) corresponding to the record type defined by the ID Type parameter.  Use “All” in include all records. Default ID Type is Investment if ID Type is omitted..
 * @param [EndDate] End date that drives the measurement date for calculations. Actual measurement date can be affected by Roll Forward parameter. Default date is today if omitted.
 * @param [RollForward] Methodology for determining the measurement date of the calculation. Default is FALSE if omitted. See Burgiss Help for more details.
 * @param [IdType] Describes the record type corresponding to values passed to the ID parameter. Default is Investment if omitted. See Burgiss Help for more details.
 * @param [SelectionSetId] Selection set filter applied to records denoted by the ID parameter. Default is no selection set if omitted
 * @param [Filter]  Filter applied to records denoted by the ID parameter. Filter is applied after selection set is applied. Default is no filter if omitted. See Burgiss Help for more details.
 * @param [AdvancedParameter] To enabled supported attributes for the formula.
 * @param {CustomFunctions.Invocation} invocation - The invocation context.
 * @helpUrl https://burgiss.docsend.com/view/4c3zt8i
 * @requiresAddress
 */

// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
function BurgissCalcValuationDate(
  ID?: any[][],
  EndDate?: any,
  RollForward?: string,
  IdType?: string,
  SelectionSetId?: any,
  Filter?: any,
  AdvancedParameter?: string,
  invocation?: any
) {
  const result = CheckProfile();
  if (result == true) {
    const uniqueCellId = generateUniqueCellId(invocation.address);
    return new MeasuresFunctions().fnBurgissCalcValuationDate(
      ID,
      EndDate,
      RollForward,
      IdType,
      SelectionSetId,
      Filter,
      AdvancedParameter,
      uniqueCellId
    );
  }
  return result;
}

/**
 * Calculates the net capital contributed for an investment or group of investments..
 * @CustomFunction PrivateiCalcNetCapitalContributed
 * @param ID[][] ID(s) corresponding to the record type defined by the ID Type parameter. Use “All” to include all records. Default ID Type is Investment if ID Type is omitted..
 * @param [EndDate] End date that drives the measurement date for calculations. Default date is today if omitted.
 * @param [Currency] Reflects the Currency of the amounts in which calculations are performed. Default is base if omitted.
 * @param [BeginDate] Begin date that drives the measurement period for calculations. Default is inception if omitted..
 * @param [IdType] Describes the record type corresponding to values passed to the ID parameter. Default is Investment if omitted. See Burgiss Help for more details.
 * @param [SelectionSetId] Selection set filter applied to records denoted by the ID parameter. Default is no selection set if omitted
 * @param [Filter]  Filter applied to records denoted by the ID parameter. Filter is applied after selection set is applied. Default is no filter if omitted. See Burgiss Help for more details.
 * @param [AdvancedParameter] To enabled supported attributes for the formula.
 * @param {CustomFunctions.Invocation} invocation - The invocation context.
 * @helpUrl https://burgiss.docsend.com/view/4c3zt8i
 * @requiresAddress
 */

// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
function BurgissCalcNetCapitalContributed(
  ID?: any[][],
  EndDate?: any,
  Currency?: string,
  BeginDate?: any,
  IdType?: string,
  SelectionSetId?: any,
  Filter?: any,
  AdvancedParameter?: string,
  invocation?: any
) {
  const result = CheckProfile();
  if (result == true) {
    const uniqueCellId = generateUniqueCellId(invocation.address);
    return new MeasuresFunctions().fnBurgissCalcNetCapitalContributed(
      ID,
      EndDate,
      Currency,
      BeginDate,
      IdType,
      SelectionSetId,
      Filter,
      AdvancedParameter,
      uniqueCellId
    );
  }
  return result;
}
/**
 * Calculates the adjusted commitment amount for an investment or group of investments.
 * @CustomFunction PrivateiCalcValuation.RollForward
 * @param ID[][] Investment ID.
 * @param [EndDate] End date that drives the measurement date for calculations. Default date is today if omitted.
 * @param [Currency] Reflects the Currency of the amounts in which calculations are performed. Default is base if omitted.
 * @param [IdType] Describes the record type corresponding to values passed to the ID parameter. Default is Investment if omitted. See Burgiss Help for more details.
 * @param [SelectionSetId] Selection set filter applied to records denoted by the ID parameter. Default is no selection set if omitted
 * @param [Filter]  Filter applied to records denoted by the ID parameter. Filter is applied after selection set is applied. Default is no filter if omitted. See Burgiss Help for more details.
 * @param [AdvancedParameter] To enabled supported attributes for the formula.
 * @param {CustomFunctions.Invocation} invocation - The invocation context.
 * @helpUrl https://burgiss.docsend.com/view/4c3zt8i
 * @requiresAddress
 */

// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
function BurgissCalcValuationRollForward(
  ID?: any[][],
  EndDate?: any,
  Currency?: string,
  IdType?: string,
  SelectionSetId?: any,
  Filter?: any,
  AdvancedParameter?: string,
  invocation?: any
) {
  const result = CheckProfile();
  if (result == true) {
    const uniqueCellId = generateUniqueCellId(invocation.address);
    return new MeasuresFunctions().fnBurgissCalcValuationRollForward(
      ID,
      EndDate,
      Currency,
      IdType,
      SelectionSetId,
      Filter,
      AdvancedParameter,
      uniqueCellId
    );
  }
  return result;
}
/**
 * Calculates the modified internal rate of return for an investment or group of investments.
 * @CustomFunction PrivateiCalcMIRR
 * @param ID[][] Index ID associated with the discount rate. Default is default index and premium associated with the record if omitted.
 * @param [FinanceIndexId] Index ID associated with the discount rate. Default is default index and premium associated with the record if omitted.
 * @param [FinanceIndexPremium] Annual premium applied to index return. Parameter expects unscaled amount (e.g. enter 0.05 for 5%). Default is no premium if Index ID is provided. See Burgiss Help for more details.
 * @param [ReinvestmentIIndexId] Index ID associated with the reinvestment rate. Default is default index and premium associated with the record if omitted.
 * @param [ReinvestmentIIndexPremium] Annual premium applied to index return. Parameter expects unscaled amount (e.g. enter 0.05 for 5%). Default is no premium if Index ID is provided. See Burgiss Help for more details.
 * @param [EndDate] End date that drives the measurement date for calculations. Actual measurement date can be affected by Roll Forward parameter. Default date is today if omitted.
 * @param [Currency] Reflects the Currency of the amounts in which calculations are performed. Default is base if omitted.
 * @param [RollForward] Methodology for determining the measurement date of the calculation. Default is FALSE if omitted. See Burgiss Help for more details.
 * @param [BeginDate]  Begin date that drives the measurement period for calculations. Default is inception if omitted.
 * @param [IdType] Describes the record type corresponding to values passed to the ID parameter. Default is Investment if omitted. See Burgiss Help for more details.
 * @param [SelectionSetId] Selection set filter applied to records denoted by the ID parameter. Default is no selection set if omitted.
 * @param [Filter] Filter applied to records denoted by the ID parameter. Filter is applied after selection set is applied. Default is no filter if omitted. See Burgiss Help for more details.
 * @param [AdvancedParameter] To enabled supported attributes for the formula.
 * @param {CustomFunctions.Invocation} invocation - The invocation context.
 * @helpUrl https://burgiss.docsend.com/view/4c3zt8i
 * @requiresAddress
 */
// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
function BurgissCalcMIRR(
  ID?: any[][],
  FinanceIndexId?: any,
  FinanceIndexPremium?: any,
  ReinvestmentIIndexId?: any,
  ReinvestmentIIndexPremium?: any,
  EndDate?: any,
  Currency?: string,
  RollForward?: any,
  BeginDate?: any,
  IdType?: string,
  SelectionSetId?: any,
  Filter?: any,
  AdvancedParameter?: string,
  invocation?: any
) {
  const result = CheckProfile();
  if (result == true) {
    const uniqueCellId = generateUniqueCellId(invocation.address);
    return new MeasuresFunctions().fnBurgissCalcMIRR(
      ID,
      FinanceIndexId,
      FinanceIndexPremium,
      ReinvestmentIIndexId,
      ReinvestmentIIndexPremium,
      EndDate,
      Currency,
      RollForward,
      BeginDate,
      IdType,
      SelectionSetId,
      Filter,
      AdvancedParameter,
      uniqueCellId
    );
  }
  return result;
}
/**
 * Advanced Analytics license required - Calculates the Long-Nickels ICM IRR for an investment or group of investments.
 * @CustomFunction PrivateiCalcLN_ICMIRR
 * @param ID[][] ID(s) corresponding to the record type defined by the ID Type parameter.  Use “All” in include all records. Default ID Type is Investment if ID Type is omitted.
 * @param [IndexID] Default is default index and premium associated with the record if omitted.
 * @param [Premium] Annual premium applied to index return. Parameter expects unscaled amount (e.g. enter 0.05 for 5%). Default is no premium if Index ID is provided. See Burgiss Help for more details.
 * @param [EndDate] End date that drives the measurement date for calculations. Actual measurement date can be affected by Roll Forward parameter. Default date is today if omitted.
 * @param [Currency] Reflects the Currency of the amounts in which calculations are performed. Default is base if omitted.
 * @param [RollForward] Methodology for determining the measurement date of the calculation. Default is FALSE if omitted. See Burgiss Help for more details.
 * @param [BeginDate]  Begin date that drives the measurement period for calculations. Default is inception if omitted.
 * @param [IdType] Describes the record type corresponding to values passed to the ID parameter. Default is Investment if omitted. See Burgiss Help for more details
 * @param [SelectionSetId] Selection set filter applied to records denoted by the ID parameter. Default is no selection set if omitted.
 * @param [Filter] Filter applied to records denoted by the ID parameter. Filter is applied after selection set is applied. Default is no filter if omitted. See Burgiss Help for more details.
 * @param [AdvancedParameter] To enabled supported attributes for the formula.
 * @param {CustomFunctions.Invocation} invocation - The invocation context.
 * @helpUrl https://burgiss.docsend.com/view/4c3zt8i
 * @requiresAddress
 */
// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
function BurgissCalcLN_ICMIRR(
  ID?: any[][],
  IndexID?: any,
  Premium?: any,
  EndDate?: any,
  Currency?: string,
  RollForward?: any,
  BeginDate?: any,
  IdType?: string,
  SelectionSetId?: any,
  Filter?: any,
  AdvancedParameter?: string,
  invocation?: any
) {
  const result = CheckProfile();
  if (result == true) {
    const uniqueCellId = generateUniqueCellId(invocation.address);
    return new MeasuresFunctions().fnBurgissCalcLN_ICMIRR(
      ID,
      IndexID,
      Premium,
      EndDate,
      Currency,
      RollForward,
      BeginDate,
      IdType,
      SelectionSetId,
      Filter,
      AdvancedParameter,
      uniqueCellId
    );
  }
  return result;
}

/**
 * Advanced Analytics license required - Calculates the Long-Nickels ICM IRR Spread for an investment or group of investment.
 * @CustomFunction PrivateiCalcLN_ICMIRRSpread
 * @param ID[][] ID(s) corresponding to the record type defined by the ID Type parameter.  Use “All” in include all records. Default ID Type is Investment if ID Type is omitted.
 * @param [IndexID] Default is default index and premium associated with the record if omitted.
 * @param [Premium] Annual premium applied to index return. Parameter expects unscaled amount (e.g. enter 0.05 for 5%). Default is no premium if Index ID is provided. See Burgiss Help for more details.
 * @param [EndDate] End date that drives the measurement date for calculations. Actual measurement date can be affected by Roll Forward parameter. Default date is today if omitted.
 * @param [Currency] Reflects the Currency of the amounts in which calculations are performed. Default is base if omitted.
 * @param [RollForward] Methodology for determining the measurement date of the calculation. Default is FALSE if omitted. See Burgiss Help for more details.
 * @param [BeginDate]  Begin date that drives the measurement period for calculations. Default is inception if omitted.
 * @param [IdType] Describes the record type corresponding to values passed to the ID parameter. Default is Investment if omitted. See Burgiss Help for more details
 * @param [SelectionSetId] Selection set filter applied to records denoted by the ID parameter. Default is no selection set if omitted.
 * @param [Filter] Filter applied to records denoted by the ID parameter. Filter is applied after selection set is applied. Default is no filter if omitted. See Burgiss Help for more details.
 * @param [AdvancedParameter] To enabled supported attributes for the formula.
 * @param {CustomFunctions.Invocation} invocation - The invocation context.
 * @helpUrl https://burgiss.docsend.com/view/4c3zt8i
 * @requiresAddress
 */
// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
function BurgissCalcLN_ICMIRRSpread(
  ID?: any[][],
  IndexID?: any,
  Premium?: any,
  EndDate?: any,
  Currency?: string,
  RollForward?: any,
  BeginDate?: any,
  IdType?: string,
  SelectionSetId?: any,
  Filter?: any,
  AdvancedParameter?: string,
  invocation?: any
) {
  const result = CheckProfile();
  if (result == true) {
    const uniqueCellId = generateUniqueCellId(invocation.address);
    return new MeasuresFunctions().fnBurgissCalcLN_ICMIRRSpread(
      ID,
      IndexID,
      Premium,
      EndDate,
      Currency,
      RollForward,
      BeginDate,
      IdType,
      SelectionSetId,
      Filter,
      AdvancedParameter,
      uniqueCellId
    );
  }
  return result;
}

/**
 * Advanced Analytics license required - Calculates the Long-Nickels ICM Valuation for an investment or group of investments..
 * @CustomFunction PrivateiCalcLN_ICMValuation
 * @param ID[][] ID(s) corresponding to the record type defined by the ID Type parameter.  Use “All” in include all records. Default ID Type is Investment if ID Type is omitted.
 * @param [IndexID] Default is default index and premium associated with the record if omitted.
 * @param [Premium] Annual premium applied to index return. Parameter expects unscaled amount (e.g. enter 0.05 for 5%). Default is no premium if Index ID is provided. See Burgiss Help for more details.
 * @param [EndDate] End date that drives the measurement date for calculations. Actual measurement date can be affected by Roll Forward parameter. Default date is today if omitted.
 * @param [Currency] Reflects the Currency of the amounts in which calculations are performed. Default is base if omitted.
 * @param [RollForward] Methodology for determining the measurement date of the calculation. Default is FALSE if omitted. See Burgiss Help for more details.
 * @param [BeginDate]  Begin date that drives the measurement period for calculations. Default is inception if omitted.
 * @param [IdType] Describes the record type corresponding to values passed to the ID parameter. Default is Investment if omitted. See Burgiss Help for more details
 * @param [SelectionSetId] Selection set filter applied to records denoted by the ID parameter. Default is no selection set if omitted.
 * @param [Filter] Filter applied to records denoted by the ID parameter. Filter is applied after selection set is applied. Default is no filter if omitted. See Burgiss Help for more details.
 * @param [AdvancedParameter] To enabled supported attributes for the formula.
 * @param {CustomFunctions.Invocation} invocation - The invocation context.
 * @helpUrl https://burgiss.docsend.com/view/4c3zt8i
 * @requiresAddress
 */
// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
function BurgissCalcLN_ICMValuation(
  ID?: any[][],
  IndexID?: any,
  Premium?: any,
  EndDate?: any,
  Currency?: string,
  RollForward?: any,
  BeginDate?: any,
  IdType?: string,
  SelectionSetId?: any,
  Filter?: any,
  AdvancedParameter?: string,
  invocation?: any
) {
  const result = CheckProfile();
  if (result == true) {
    const uniqueCellId = generateUniqueCellId(invocation.address);
    return new MeasuresFunctions().fnBurgissCalcLN_ICMValuation(
      ID,
      IndexID,
      Premium,
      EndDate,
      Currency,
      RollForward,
      BeginDate,
      IdType,
      SelectionSetId,
      Filter,
      AdvancedParameter,
      uniqueCellId
    );
  }
  return result;
}

/**
 * Advanced Analytics license required - Calculates the Long-Nickels ICM IRR Spread for an investment or group of investment.
 * @CustomFunction PrivateiCalcKS_PME
 * @param ID[][] ID(s) corresponding to the record type defined by the ID Type parameter.  Use “All” in include all records. Default ID Type is Investment if ID Type is omitted.
 * @param [IndexID] Default is default index and premium associated with the record if omitted.
 * @param [Premium] Annual premium applied to index return. Parameter expects unscaled amount (e.g. enter 0.05 for 5%). Default is no premium if Index ID is provided. See Burgiss Help for more details.
 * @param [EndDate] End date that drives the measurement date for calculations. Actual measurement date can be affected by Roll Forward parameter. Default date is today if omitted.
 * @param [Currency] Reflects the Currency of the amounts in which calculations are performed. Default is base if omitted.
 * @param [RollForward] Methodology for determining the measurement date of the calculation. Default is FALSE if omitted. See Burgiss Help for more details.
 * @param [BeginDate]  Begin date that drives the measurement period for calculations. Default is inception if omitted.
 * @param [IdType] Describes the record type corresponding to values passed to the ID parameter. Default is Investment if omitted. See Burgiss Help for more details
 * @param [SelectionSetId] Selection set filter applied to records denoted by the ID parameter. Default is no selection set if omitted.
 * @param [Filter] Filter applied to records denoted by the ID parameter. Filter is applied after selection set is applied. Default is no filter if omitted. See Burgiss Help for more details.
 * @param [AdvancedParameter] To enabled supported attributes for the formula.
 * @param {CustomFunctions.Invocation} invocation - The invocation context.
 * @helpUrl https://burgiss.docsend.com/view/4c3zt8i
 * @requiresAddress
 */
// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
function BurgissCalcKS_PME(
  ID?: any[][],
  IndexID?: any,
  Premium?: any,
  EndDate?: any,
  Currency?: string,
  RollForward?: any,
  BeginDate?: any,
  IdType?: string,
  SelectionSetId?: any,
  Filter?: any,
  AdvancedParameter?: string,
  invocation?: any
) {
  const result = CheckProfile();
  if (result == true) {
    const uniqueCellId = generateUniqueCellId(invocation.address);
    return new MeasuresFunctions().fnBurgissCalcKS_PME(
      ID,
      IndexID,
      Premium,
      EndDate,
      Currency,
      RollForward,
      BeginDate,
      IdType,
      SelectionSetId,
      Filter,
      AdvancedParameter,
      uniqueCellId
    );
  }
  return result;
}
/**
 * Advanced Analytics license required - Calculates the Gredil-Griffiths-Stucke Direct Alpha for an investment or group of investments.
 * @CustomFunction PrivateiCalcGGS_DirectAlpha
 * @param ID[][] ID(s) corresponding to the record type defined by the ID Type parameter.  Use “All” in include all records. Default ID Type is Investment if ID Type is omitted.
 * @param [IndexID] Default is default index and premium associated with the record if omitted.
 * @param [Premium] Annual premium applied to index return. Parameter expects unscaled amount (e.g. enter 0.05 for 5%). Default is no premium if Index ID is provided. See Burgiss Help for more details.
 * @param [EndDate] End date that drives the measurement date for calculations. Actual measurement date can be affected by Roll Forward parameter. Default date is today if omitted.
 * @param [Currency] Reflects the Currency of the amounts in which calculations are performed. Default is base if omitted.
 * @param [RollForward] Methodology for determining the measurement date of the calculation. Default is FALSE if omitted. See Burgiss Help for more details.
 * @param [BeginDate]  Begin date that drives the measurement period for calculations. Default is inception if omitted.
 * @param [IdType] Describes the record type corresponding to values passed to the ID parameter. Default is Investment if omitted. See Burgiss Help for more details
 * @param [SelectionSetId] Selection set filter applied to records denoted by the ID parameter. Default is no selection set if omitted.
 * @param [Filter] Filter applied to records denoted by the ID parameter. Filter is applied after selection set is applied. Default is no filter if omitted. See Burgiss Help for more details.
 * @param [AdvancedParameter] To enabled supported attributes for the formula.
 * @param {CustomFunctions.Invocation} invocation - The invocation context.
 * @helpUrl https://burgiss.docsend.com/view/4c3zt8i
 * @requiresAddress
 */
// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
function BurgissCalcGGS_DirectAlpha(
  ID?: any[][],
  IndexID?: any,
  Premium?: any,
  EndDate?: any,
  Currency?: string,
  RollForward?: any,
  BeginDate?: any,
  IdType?: string,
  SelectionSetId?: any,
  Filter?: any,
  AdvancedParameter?: string,
  invocation?: any
) {
  const result = CheckProfile();
  if (result == true) {
    const uniqueCellId = generateUniqueCellId(invocation.address);
    return new MeasuresFunctions().fnBurgissCalcGGS_DirectAlpha(
      ID,
      IndexID,
      Premium,
      EndDate,
      Currency,
      RollForward,
      BeginDate,
      IdType,
      SelectionSetId,
      Filter,
      AdvancedParameter,
      uniqueCellId
    );
  }
  return result;
}

/**
 * Calculates the total capital gains portion of stock distributions for an investment or group of investments.
 * @CustomFunction PrivateiCalcStock.CapitalGains
 * @param ID[][]
 * @param [EndDate] End date that drives the measurement date for calculations. Default date is today if omitted.
 * @param [Currency] Reflects the Currency of the amounts in which calculations are performed. Default is base if omitted.
 * @param [BeginDate] Begin date that drives the measurement period for calculations. Default is inception if omitted.
 * @param [IdType] Describes the record type corresponding to values passed to the ID parameter. Default is Investment if omitted. See Burgiss Help for more details.
 * @param [SelectionSetID] Selection set filter applied to records denoted by the ID parameter. Default is no selection set if omitted
 * @param [Filters] Filter applied to records denoted by the ID parameter. Filter is applied after selection set is applied. Default is no filter if omitted. See Burgiss Help for more details.
 * @param [AdvancedParameter]  To enabled supported attributes for the formula.
 * @param {CustomFunctions.Invocation} invocation - The invocation context.
 * @helpUrl https://burgiss.docsend.com/view/4c3zt8i
 * @requiresAddress
 */
// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
function BurgissCalcStockCapitalGains(
  ID?: any[][],
  EndDate?: any,
  Currency?: string,
  BeginDate?: any,
  IdType?: string,
  SelectionSetID?: any,
  Filters?: any,
  AdvancedParameter?: string,
  invocation?: any
) {
  const result = CheckProfile();
  if (result == true) {
    const uniqueCellId = generateUniqueCellId(invocation.address);
    return new MeasuresFunctions().fnBurgissCalcStockCapitalGains(
      ID,
      EndDate,
      Currency,
      BeginDate,
      IdType,
      SelectionSetID,
      Filters,
      AdvancedParameter,
      uniqueCellId
    );
  }
  return result;
}
/**
 *Calculates the total cost portion of stock distributions for an investment or group of investments.
 * @CustomFunction PrivateiCalcStock.Cost
 * @param ID[][]
 * @param [EndDate] End date that drives the measurement date for calculations. Default date is today if omitted.
 * @param [Currency] Reflects the Currency of the amounts in which calculations are performed. Default is base if omitted.
 * @param [BeginDate] Begin date that drives the measurement period for calculations. Default is inception if omitted.
 * @param [IdType] Describes the record type corresponding to values passed to the ID parameter. Default is Investment if omitted. See Burgiss Help for more details.
 * @param [SelectionSetID] Selection set filter applied to records denoted by the ID parameter. Default is no selection set if omitted
 * @param [Filters] Filter applied to records denoted by the ID parameter. Filter is applied after selection set is applied. Default is no filter if omitted. See Burgiss Help for more details.
 * @param [AdvancedParameter]  To enabled supported attributes for the formula.
 * @param {CustomFunctions.Invocation} invocation - The invocation context.
 * @helpUrl https://burgiss.docsend.com/view/4c3zt8i
 * @requiresAddress
 */
// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
function BurgissCalcStockCost(
  ID?: any[][],
  EndDate?: any,
  Currency?: string,
  BeginDate?: any,
  IdType?: string,
  SelectionSetID?: any,
  Filters?: any,
  AdvancedParameter?: string,
  invocation?: any
) {
  const result = CheckProfile();
  if (result == true) {
    const uniqueCellId = generateUniqueCellId(invocation.address);
    return new MeasuresFunctions().fnBurgissCalcStockCost(
      ID,
      EndDate,
      Currency,
      BeginDate,
      IdType,
      SelectionSetID,
      Filters,
      AdvancedParameter,
      uniqueCellId
    );
  }
  return result;
}
/**
 * Calculates the total recallable capital portion of stock distributions for an investment or group of investments.
 * @CustomFunction PrivateiCalcStock.RecallableCapital
 * @param ID[][]
 * @param [EndDate] End date that drives the measurement date for calculations. Default date is today if omitted.
 * @param [Currency] Reflects the Currency of the amounts in which calculations are performed. Default is base if omitted.
 * @param [BeginDate] Begin date that drives the measurement period for calculations. Default is inception if omitted.
 * @param [IdType] Describes the record type corresponding to values passed to the ID parameter. Default is Investment if omitted. See Burgiss Help for more details.
 * @param [SelectionSetID] Selection set filter applied to records denoted by the ID parameter. Default is no selection set if omitted
 * @param [Filters] Filter applied to records denoted by the ID parameter. Filter is applied after selection set is applied. Default is no filter if omitted. See Burgiss Help for more details.
 * @param [AdvancedParameter]  To enabled supported attributes for the formula.
 * @param {CustomFunctions.Invocation} invocation - The invocation context.
 * @helpUrl https://burgiss.docsend.com/view/4c3zt8i
 * @requiresAddress
 */
// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
function BurgissCalcStockRecallableCapital(
  ID?: any[][],
  EndDate?: any,
  Currency?: string,
  BeginDate?: any,
  IdType?: string,
  SelectionSetID?: any,
  Filters?: any,
  AdvancedParameter?: string,
  invocation?: any
) {
  const result = CheckProfile();
  if (result == true) {
    const uniqueCellId = generateUniqueCellId(invocation.address);
    return new MeasuresFunctions().fnBurgissCalcStockRecallableCapital(
      ID,
      EndDate,
      Currency,
      BeginDate,
      IdType,
      SelectionSetID,
      Filters,
      AdvancedParameter,
      uniqueCellId
    );
  }
  return result;
}
/**
 * Calculates the total return of capital portion of stock distributions for an investment or group of investments.
 * @CustomFunction PrivateiCalcStock.ReturnOfCapital
 * @param ID[][]
 * @param [EndDate] End date that drives the measurement date for calculations. Default date is today if omitted.
 * @param [Currency] Reflects the Currency of the amounts in which calculations are performed. Default is base if omitted.
 * @param [BeginDate] Begin date that drives the measurement period for calculations. Default is inception if omitted.
 * @param [IdType] Describes the record type corresponding to values passed to the ID parameter. Default is Investment if omitted. See Burgiss Help for more details.
 * @param [SelectionSetID] Selection set filter applied to records denoted by the ID parameter. Default is no selection set if omitted
 * @param [Filters] Filter applied to records denoted by the ID parameter. Filter is applied after selection set is applied. Default is no filter if omitted. See Burgiss Help for more details.
 * @param [AdvancedParameter]  To enabled supported attributes for the formula.
 * @param {CustomFunctions.Invocation} invocation - The invocation context.
 * @helpUrl https://burgiss.docsend.com/view/4c3zt8i
 * @requiresAddress
 */
// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
function BurgissCalcStockReturnOfCapital(
  ID?: any[][],
  EndDate?: any,
  Currency?: string,
  BeginDate?: any,
  IdType?: string,
  SelectionSetID?: any,
  Filters?: any,
  AdvancedParameter?: string,
  invocation?: any
) {
  const result = CheckProfile();
  if (result == true) {
    const uniqueCellId = generateUniqueCellId(invocation.address);
    return new MeasuresFunctions().fnBurgissCalcStockReturnOfCapital(
      ID,
      EndDate,
      Currency,
      BeginDate,
      IdType,
      SelectionSetID,
      Filters,
      AdvancedParameter,
      uniqueCellId
    );
  }
  return result;
}

/**
 * Calculates the adjusted commitment amount for an investment or group of investments

 @CustomFunction PrivateiCalcAdjustedCommitment
 *@param ID[][] ID(s) corresponding to the record type defined by the ID Type parameter.Use “All” in include all records. Default ID Type is Investment if ID Type is omitted.
 * @param [EndDate] End date that drives the measurement date for calculations. Default date is today if omitted..
 * @param [Currency] Reflects the Currency of the amounts in which calculations are performed. Default is base if omitted.
 * @param [IdType] Describes the record type corresponding to values passed to the ID parameter. Default is Investment if omitted. See Burgiss Help for more details.
 * @param [SelectionSetId] Selection set filter applied to records denoted by the ID parameter. Default is no selection set if omitted
 * @param [Filter]  Filter applied to records denoted by the ID parameter. Filter is applied after selection set is applied. Default is no filter if omitted. See Burgiss Help for more details.
 * @param [AdvancedParameter] To enabled supported attributes for the formula.
 * @param {CustomFunctions.Invocation} invocation - The invocation context.
 * @helpUrl https://burgiss.docsend.com/view/4c3zt8i
 * @requiresAddress
 */
// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
function BurgissCalcAdjustedCommitment(
  ID?: any[][],
  EndDate?: any,
  Currency?: string,
  IdType?: string,
  SelectionSetId?: any,
  Filter?: any,
  AdvancedParameter?: string,
  invocation?: any
) {
  const result = CheckProfile();
  if (result == true) {
    const uniqueCellId = generateUniqueCellId(invocation.address);
    return new MeasuresFunctions().fnBurgissCalcAdjustedCommitment(
      ID,
      EndDate,
      Currency,
      IdType,
      SelectionSetId,
      Filter,
      AdvancedParameter,
      uniqueCellId
    );
  }
  return result;
}

/**
 * Calculates the total cash amount for an investment or group of investments.
 * @CustomFunction PrivateiCalcCash
 * @param ID[][] ID(s) corresponding to the record type defined by the ID Type parameter. Use “All” to include all records. Default ID Type is Investment if ID Type is omitted.
 * @param [EndDate] End date that drives the measurement date for calculations. Default date is today if omitted.
 * @param [Currency] Reflects the Currency of the amounts in which calculations are performed. Default is base if omitted.
 * @param [BeginDate] Begin date that drives the measurement period for calculations. Default is inception if omitted.
 * @param [IdType] Describes the record type corresponding to values passed to the ID parameter. Default is Investment if omitted. See Burgiss Help for more details.
 * @param [SelectionSetId] Selection set filter applied to records denoted by the ID parameter. Default is no selection set if omitted.
 * @param [Filters] Filter applied to records denoted by the ID parameter. Filter is applied after selection set is applied. Default is no filter if omitted. See Burgiss Help for more details.
 * @param [AdvancedParameter] To enabled supported attributes for the formula.
 * @param {CustomFunctions.Invocation} invocation - The invocation context.
 * @helpUrl https://burgiss.docsend.com/view/4c3zt8i
 * @requiresAddress
 */
// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
function BurgissCalcCash(
  ID: any[][],
  EndDate?: any,
  Currency?: string,
  BeginDate?: any,
  IdType?: string,
  SelectionSetId?: any,
  Filters?: any,
  AdvancedParameter?: any,
  invocation?: any
) {
  const result = CheckProfile();
  if (result == true) {
    const uniqueCellId = generateUniqueCellId(invocation.address);
    return new MeasuresFunctions().fnBurgissCalcCash(
      ID,
      EndDate,
      Currency,
      BeginDate,
      IdType,
      SelectionSetId,
      Filters,
      AdvancedParameter,
      uniqueCellId
    );
  }
  return result;
}

/**
 * Calculates the internal rate of return for an investment or group of investments.
 * @CustomFunction PrivateiCalcIRR
 * @param ID[][] ID(s) corresponding to the record type defined by the ID Type parameter.  Use “All” in include all records. Default ID Type is Investment if ID Type is omitted.
 * @param [EndDate] End date that drives the measurement date for calculations. Actual measurement date can be affected by Roll Forward parameter. Default date is today if omitted.
 * @param [Currency] Reflects the Currency of the amounts in which calculations are performed. Default is base if omitted.
 * @param [RollForward] Methodology for determining the measurement date of the calculation. Default is FALSE if omitted. See Burgiss Help for more details.
 * @param [BeginDate] Begin date that drives the measurement period for calculations. Default is inception if omitted.
 * @param [IdType] Describes the record type corresponding to values passed to the ID parameter. Default is Investment if omitted. See Burgiss Help for more details.
 * @param [SelectionSetId] Selection set filter applied to records denoted by the ID parameter. Default is no selection set if omitted
 * @param [Filters] Filter applied to records denoted by the ID parameter. Filter is applied after selection set is applied. Default is no filter if omitted. See Burgiss Help for more details.
 * @param [AdvancedParameter] To enabled supported attributes for the formula.
 * @param {CustomFunctions.Invocation} invocation - The invocation context.
 * @helpUrl https://burgiss.docsend.com/view/4c3zt8i
 * @requiresAddress
 */
// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
function BurgissCalcIRR(
  ID: any[][],
  EndDate?: any,
  Currency?: string,
  RollForward?: any,
  BeginDate?: any,
  IdType?: string,
  SelectionSetId?: any,
  Filters?: any,
  AdvancedParameter?: any,
  invocation?: any
) {
  const result = CheckProfile();
  if (result == true) {
    const uniqueCellId = generateUniqueCellId(invocation.address);
    return new MeasuresFunctions().fnBurgissCalcIRR(
      ID,
      EndDate,
      Currency,
      RollForward,
      BeginDate,
      IdType,
      SelectionSetId,
      Filters,
      AdvancedParameter,
      uniqueCellId
    );
  }
  return result;
}

/**
 * Calculates the realized internal rate of return for an investment or group of investments. Results are unscaled.
 * @CustomFunction PrivateiCalcRIRR
 * @param ID[][] ID(s) corresponding to the record type defined by the ID Type parameter.  Use “All” in include all records.
 * @param [EndDate] End date that drives the measurement date for calculations. Actual measurement date can be affected by Roll Forward parameter. Default date is today if omitted.
 * @param [Currency] Denotes the currency of the amounts in which calculations are performed. Default is base if omitted.
 * @param [RollForward] Denotes the methodology used to determine the measurement date of the calculation. Uses roll forward valuations where required. Default is FALSE if omitted.
 * @param [BeginDate] Begin date that drives the measurement period for calculations. Default is inception if omitted.
 * @param [IdType] Describes the record type corresponding to the values passed to the ID parameter. Default is Investment if omitted.
 * @param [SelectionSetId] Selection set filter applied to records denoted by the ID parameter. Default is no selection set if omitted
 * @param [Filters] Filter applied to records denoted by the ID parameter. Filter is applied after the Selection Set is applied. Default is no filter if omitted.
 * @param [AdvancedParameter] To enabled supported attributes for the formula.
 * @param {CustomFunctions.Invocation} invocation - The invocation context.
 * @helpUrl https://burgiss.docsend.com/view/4c3zt8i
 * @requiresAddress
 */
// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
function BurgissCalcRIRR(
  ID: any[][],
  EndDate?: any,
  Currency?: string,
  RollForward?: any,
  BeginDate?: any,
  IdType?: string,
  SelectionSetId?: any,
  Filters?: any,
  AdvancedParameter?: any,
  invocation?: any
) {
  const result = CheckProfile();
  if (result == true) {
    const uniqueCellId = generateUniqueCellId(invocation.address);
    return new MeasuresFunctions().fnBurgissCalcRIRR(
      ID,
      EndDate,
      Currency,
      RollForward,
      BeginDate,
      IdType,
      SelectionSetId,
      Filters,
      AdvancedParameter,
      uniqueCellId
    );
  }
  return result;
}

/**
 * Calculates the DPI for an investment or group of investments.
 * @CustomFunction PrivateiCalcDPI
 * @param ID[][] ID(s) corresponding to the record type defined by the ID Type parameter.  Use “All” in include all records. Default ID Type is Investment if ID Type is omitted.
 * @param [EndDate] End date that drives the measurement date for calculations. Actual measurement date can be affected by Roll Forward parameter. Default date is today if omitted.
 * @param [Currency] Reflects the Currency of the amounts in which calculations are performed. Default is base if omitted.
 * @param [RollForward] Methodology for determining the measurement date of the calculation. Default is FALSE if omitted. See Burgiss Help for more details.
 * @param [IdType] Describes the record type corresponding to values passed to the ID parameter. Default is Investment if omitted. See Burgiss Help for more details.
 * @param [SelectionSetId] Selection set filter applied to records denoted by the ID parameter. Default is no selection set if omitted
 * @param [Filters] Filter applied to records denoted by the ID parameter. Filter is applied after selection set is applied. Default is no filter if omitted. See Burgiss Help for more details.
 * @param [AdvancedParameter] To enabled supported attributes for the formula.
 * @param {CustomFunctions.Invocation} invocation - The invocation context.
 * @helpUrl https://burgiss.docsend.com/view/4c3zt8i
 * @requiresAddress
 */
// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
function BurgissCalcDPI(
  ID?: any[][],
  EndDate?: any,
  Currency?: string,
  RollForward?: any,
  IdType?: string,
  SelectionSetId?: any,
  Filters?: any,
  AdvancedParameter?: any,
  invocation?: any
) {
  const result = CheckProfile();
  if (result == true) {
    const uniqueCellId = generateUniqueCellId(invocation.address);
    return new MeasuresFunctions().fnBurgissCalcDPI(
      ID,
      EndDate,
      Currency,
      RollForward,
      IdType,
      SelectionSetId,
      Filters,
      AdvancedParameter,
      uniqueCellId
    );
  }
  return result;
}

/**
 * Calculates the TVPI for an investment or group of investments.
 * @CustomFunction PrivateiCalcTVPI
 * @param ID[][] ID(s) corresponding to the record type defined by the ID Type parameter.  Use “All” in include all records. Default ID Type is Investment if ID Type is omitted.
 * @param [EndDate] End date that drives the measurement date for calculations. Actual measurement date can be affected by Roll Forward parameter. Default date is today if omitted.
 * @param [Currency] Reflects the Currency of the amounts in which calculations are performed. Default is base if omitted.
 * @param [RollForward] Methodology for determining the measurement date of the calculation. Default is FALSE if omitted. See Burgiss Help for more details.
 * @param [IdType] Describes the record type corresponding to values passed to the ID parameter. Default is Investment if omitted. See Burgiss Help for more details.
 * @param [SelectionSetId] Selection set filter applied to records denoted by the ID parameter. Default is no selection set if omitted
 * @param [Filters] Filter applied to records denoted by the ID parameter. Filter is applied after selection set is applied. Default is no filter if omitted. See Burgiss Help for more details.
 * @param [AdvancedParameter] To enabled supported attributes for the formula.
 * @param {CustomFunctions.Invocation} invocation - The invocation context.
 * @helpUrl https://burgiss.docsend.com/view/4c3zt8i
 * @requiresAddress
 */
// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
function BurgissCalcTVPI(
  ID: any[][],
  EndDate?: any,
  Currency?: string,
  RollForward?: any,
  IdType?: string,
  SelectionSetId?: any,
  Filters?: any,
  AdvancedParameter?: any,
  invocation?: any
) {
  const result = CheckProfile();
  if (result == true) {
    const uniqueCellId = generateUniqueCellId(invocation.address);
    return new MeasuresFunctions().fnBurgissCalcTVPI(
      ID,
      EndDate,
      Currency,
      RollForward,
      IdType,
      SelectionSetId,
      Filters,
      AdvancedParameter,
      uniqueCellId
    );
  }
  return result;
}

/**
 * Calculates the RVPI for an investment or group of investments.
 * @CustomFunction PrivateiCalcRVPI
 * @param ID[][] ID(s) corresponding to the record type defined by the ID Type parameter.  Use “All” in include all records. Default ID Type is Investment if ID Type is omitted.
 * @param [EndDate] End date that drives the measurement date for calculations. Actual measurement date can be affected by Roll Forward parameter. Default date is today if omitted.
 * @param [Currency] Reflects the Currency of the amounts in which calculations are performed. Default is base if omitted.
 * @param [RollForward] Methodology for determining the measurement date of the calculation. Default is FALSE if omitted. See Burgiss Help for more details.
 * @param [IdType] Describes the record type corresponding to values passed to the ID parameter. Default is Investment if omitted. See Burgiss Help for more details.
 * @param [SelectionSetId] Selection set filter applied to records denoted by the ID parameter. Default is no selection set if omitted
 * @param [Filters] Filter applied to records denoted by the ID parameter. Filter is applied after selection set is applied. Default is no filter if omitted. See Burgiss Help for more details.
 * @param [AdvancedParameter] To enabled supported attributes for the formula.
 * @param {CustomFunctions.Invocation} invocation - The invocation context.
 * @helpUrl https://burgiss.docsend.com/view/4c3zt8i
 * @requiresAddress
 */
// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
function BurgissCalcRVPI(
  ID: any[][],
  EndDate?: any,
  Currency?: string,
  RollForward?: any,
  IdType?: string,
  SelectionSetId?: any,
  Filters?: any,
  AdvancedParameter?: any,
  invocation?: any
) {
  const result = CheckProfile();
  if (result == true) {
    const uniqueCellId = generateUniqueCellId(invocation.address);
    return new MeasuresFunctions().fnBurgissCalcRVPI(
      ID,
      EndDate,
      Currency,
      RollForward,
      IdType,
      SelectionSetId,
      Filters,
      AdvancedParameter,
      uniqueCellId
    );
  }
  return result;
}
/**
 * Calculates the remaining unfunded amount for an investment or group of investments.
 * @CustomFunction PrivateiCalcUnfunded
 * @param ID[][] ID(s) corresponding to the record type defined by the ID Type parameter.  Use “All” in include all records. Default ID Type is Investment if ID Type is omitted.
 * @param [EndDate] End date that drives the measurement date for calculations. Default date is today if omitted.
 * @param [Currency] Reflects the Currency of the amounts in which calculations are performed. Default is base if omitted.
 * @param [IdType] Describes the record type corresponding to values passed to the ID parameter. Default is Investment if omitted. See Burgiss Help for more details
 * @param [SelectionSetID] Selection set filter applied to records denoted by the ID parameter. Default is no selection set if omitted
 * @param [Filter] Filter applied to records denoted by the ID parameter. Filter is applied after selection set is applied. Default is no filter if omitted. See Burgiss Help for more details.
 * @param [AdvancedParameter] To enabled supported attributes for the formula.
 * @param {CustomFunctions.Invocation} invocation - The invocation context.
 * @helpUrl https://burgiss.docsend.com/view/4c3zt8i
 * @requiresAddress
 */

// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
function BurgissCalcUnfunded(
  ID?: any[][],
  EndDate?: any,
  Currency?: string,
  IdType?: string,
  SelectionSetId?: any,
  Filter?: any,
  AdvancedParameter?: string,
  invocation?: any
) {
  const result = CheckProfile();
  if (result == true) {
    const uniqueCellId = generateUniqueCellId(invocation.address);
    return new MeasuresFunctions().fnBurgissCalcUnfunded(
      ID,
      EndDate,
      Currency,
      IdType,
      SelectionSetId,
      Filter,
      AdvancedParameter,
      uniqueCellId
    );
  }
  return result;
}

/**
 * Returns the valuation for an investment or the aggregate of valuations for a group of investments. Uses roll forward valuations where necessary.
 * @CustomFunction PrivateiCalcValuation
 * @param ID[][] ID(s) corresponding to the record type defined by the ID Type parameter.  Use “All” in include all records. Default ID Type is Investment if ID Type is omitted.
 * @param [EndDate] End date that drives the measurement date for calculations. Default date is today if omitted.
 * @param [Currency] Reflects the Currency of the amounts in which calculations are performed. Default is base if omitted.
 * @param [RollForward] Methodology for determining the measurement date of the calculation. Default is FALSE if omitted. See Burgiss Help for more details.
 * @param [IdType] Describes the record type corresponding to values passed to the ID parameter. Default is Investment if omitted. See Burgiss Help for more details
 * @param [SelectionSetId] Selection set filter applied to records denoted by the ID parameter. Default is no selection set if omitted
 * @param [Filter] Filter applied to records denoted by the ID parameter. Filter is applied after selection set is applied. Default is no filter if omitted. See Burgiss Help for more details.
 * @param [AdvancedParameter] To enabled supported attributes for the formula.
 * @param {CustomFunctions.Invocation} invocation - The invocation context.
 * @helpUrl https://burgiss.docsend.com/view/4c3zt8i
 * @requiresAddress
 */
// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
function BurgissCalcValuation(
  ID?: any[][],
  EndDate?: any,
  Currency?: string,
  RollForward?: string,
  IdType?: string,
  SelectionSetId?: any,
  Filter?: any,
  AdvancedParameter?: string,
  invocation?: any
) {
  const result = CheckProfile();
  if (result == true) {
    const uniqueCellId = generateUniqueCellId(invocation.address);
    return new MeasuresFunctions().fnBurgissCalcValuation(
      ID,
      EndDate,
      Currency,
      RollForward,
      IdType,
      SelectionSetId,
      Filter,
      AdvancedParameter,
      uniqueCellId
    );
  }
  return result;
}
/**
 * Returns the commitment amount for an investment or group of investments.
 * @CustomFunction PrivateiCalcDistributions
 * @param ID[][] ID(s) corresponding to the record type defined by the ID Type parameter.  Use “All” in include all records. Default ID Type is Investment if ID Type is omitted.
 * @param [EndDate] End date that drives the measurement date for calculations. Default date is today if omitted.
 * @param [Currency] Reflects the Currency of the amounts in which calculations are performed. Default is base if omitted.
 * @param [IdType] Describes the record type corresponding to values passed to the ID parameter. Default is Investment if omitted. See Burgiss Help for more details
 * @param [SelectionSetID] Selection set filter applied to records denoted by the ID parameter. Default is no selection set if omitted
 * @param [Filter] Filter applied to records denoted by the ID parameter. Filter is applied after selection set is applied. Default is no filter if omitted. See Burgiss Help for more details.
 * @param [AdvancedParameter] To enabled supported attributes for the formula.
 * @param {CustomFunctions.Invocation} invocation - The invocation context.
 * @helpUrl https://burgiss.docsend.com/view/4c3zt8i
 * @requiresAddress
 */
// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
function BurgissCalcDistributions(
  ID: any[][],
  EndDate?: any,
  Currency?: string,
  IdType?: string,
  SelectionSetId?: any,
  Filter?: any,
  AdvancedParameter?: string,
  invocation?: any
) {
  const result = CheckProfile();
  if (result == true) {
    const uniqueCellId = generateUniqueCellId(invocation.address);
    return new MeasuresFunctions().fnBurgissCalcDistributions(
      ID,
      EndDate,
      Currency,
      IdType,
      SelectionSetId,
      Filter,
      AdvancedParameter,
      uniqueCellId
    );
  }
  return result;
}

/**
 * Returns the commitment amount for an investment or group of investments.
 * @CustomFunction PrivateiCalcCommitment
 * @param ID[][] ID(s) corresponding to the record type defined by the ID Type parameter.  Use “All” in include all records. Default ID Type is Investment if ID Type is omitted.
 * @param [EndDate] End date that drives the measurement date for calculations. Default date is today if omitted.
 * @param [Currency] Reflects the Currency of the amounts in which calculations are performed. Default is base if omitted.
 * @param [IdType] Describes the record type corresponding to values passed to the ID parameter. Default is Investment if omitted. See Burgiss Help for more details
 * @param [SelectionSetId] Selection set filter applied to records denoted by the ID parameter. Default is no selection set if omitted
 * @param [Filters] Filter applied to records denoted by the ID parameter. Filter is applied after selection set is applied. Default is no filter if omitted. See Burgiss Help for more details.
 * @param [AdvancedParameter] To enabled supported attributes for the formula.
 * @param {CustomFunctions.Invocation} invocation - The invocation context.
 * @helpUrl https://burgiss.docsend.com/view/4c3zt8i
 * @requiresAddress
 */
// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
function BurgissCalcCommitment(
  ID: any[][],
  EndDate?: any,
  Currency?: string,
  IdType?: string,
  Filter?: any,
  SelectionSetId?: any,
  AdvancedParameter?: string,
  invocation?: any
) {
  const result = CheckProfile();
  if (result == true) {
    const uniqueCellId = generateUniqueCellId(invocation.address);
    return new MeasuresFunctions().fnBurgissCalcCommitment(
      ID,
      EndDate,
      Currency,
      Filter,
      IdType,
      SelectionSetId,
      AdvancedParameter,
      uniqueCellId
    );
  }
  return result;
}

/**
 * Calculates the total stock distribution amount for an investment or group of investments.
 * @CustomFunction PrivateiCalcStock
 * @param ID[][] ID(s) corresponding to the record type defined by the ID Type parameter.  Use “All” in include all records. Default ID Type is Investment if ID Type is omitted.
 * @param [EndDate] End date that drives the measurement date for calculations. Default date is today if omitted.
 * @param [Currency] Reflects the Currency of the amounts in which calculations are performed. Default is base if omitted.
 * @param [BeginDate] Begin date that drives the measurement period for calculations. Default is inception if omitted.
 * @param [IdType] Describes the record type corresponding to values passed to the ID parameter. Default is Investment if omitted. See Burgiss Help for more details
 * @param [SelectionSetId] Selection set filter applied to records denoted by the ID parameter. Default is no selection set if omitted
 * @param [Filters] Filter applied to records denoted by the ID parameter. Filter is applied after selection set is applied. Default is no filter if omitted. See Burgiss Help for more details.
 * @param [AdvancedParameter] To enabled supported attributes for the formula.
 * @param {CustomFunctions.Invocation} invocation - The invocation context.
 * @helpUrl https://burgiss.docsend.com/view/4c3zt8i
 * @requiresAddress
 */
// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
function BurgissCalcStock(
  ID: any[][],
  EndDate?: any,
  Currency?: string,
  BeginDate?: any,
  IdType?: string,
  SelectionSetId?: any,
  Filters?: any,
  AdvancedParameter?: any,
  invocation?: any
) {
  const result = CheckProfile();
  if (result == true) {
    const uniqueCellId = generateUniqueCellId(invocation.address);
    return new MeasuresFunctions().fnBurgissCalcStock(
      ID,
      EndDate,
      Currency,
      BeginDate,
      IdType,
      SelectionSetId,
      Filters,
      AdvancedParameter,
      uniqueCellId
    );
  }
  return result;
}

/**
 * Calculates the total fee amount for an investment or group of investments.
 * @CustomFunction PrivateiCalcFees
 * @param ID[][] ID(s) corresponding to the record type defined by the ID Type parameter.  Use “All” in include all records. Default ID Type is Investment if ID Type is omitted.
 * @param [EndDate] End date that drives the measurement date for calculations. Default date is today if omitted.
 * @param [Currency] Reflects the Currency of the amounts in which calculations are performed. Default is base if omitted.
 * @param [BeginDate] Begin date that drives the measurement period for calculations. Default is inception if omitted.
 * @param [IdType] Describes the record type corresponding to values passed to the ID parameter. Default is Investment if omitted. See Burgiss Help for more details
 * @param [SelectionSetId] Selection set filter applied to records denoted by the ID parameter. Default is no selection set if omitted
 * @param [Filters] Filter applied to records denoted by the ID parameter. Filter is applied after selection set is applied. Default is no filter if omitted. See Burgiss Help for more details.
 * @param [AdvancedParameter] To enabled supported attributes for the formula.
 * @param {CustomFunctions.Invocation} invocation - The invocation context.
 * @helpUrl https://burgiss.docsend.com/view/4c3zt8i
 * @requiresAddress
 */
// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
function BurgissCalcFees(
  ID: any[][],
  EndDate?: any,
  Currency?: string,
  BeginDate?: any,
  IdType?: string,
  SelectionSetId?: any,
  Filters?: any,
  AdvancedParameter?: any,
  invocation?: any
) {
  const result = CheckProfile();
  if (result == true) {
    const uniqueCellId = generateUniqueCellId(invocation.address);
    return new MeasuresFunctions().fnBurgissCalcFees(
      ID,
      EndDate,
      Currency,
      BeginDate,
      IdType,
      SelectionSetId,
      Filters,
      AdvancedParameter,
      uniqueCellId
    );
  }
  return result;
}

//#endregion Measures
//#region PeerGroupCapitalization
/**
 * Calculates the combined fund size of the specified peer group.
 * @CustomFunction PrivateiCalcPeerGroup.Capitalization
 * @param ID[][] ID(s) corresponding to the record type defined by the ID Type parameter. Use “All” to include all records. Default ID Type is Investment if ID Type is omitted.
 * @param [PeerGroupId] Peer Group ID. Default is default peer group associated with the investment if omitted..
 * @param [EndDate] End date that drives the measurement date for calculations and comparison date for peer group comparison. Default date is today if omitted. See Burgiss Help for more details.
 * @param [Currency]  Individual results Currency. Default is user preference for Individual Results Currency if omitted.
 * @param [ReturnConstituentCount]  If true, returns constituent count for the analysis. Default is false if omitted.
 * @param [AdvancedParameter]  To enabled supported attributes for the formula.
 * @param {CustomFunctions.Invocation} invocation - The invocation context.
 * @helpUrl https://burgiss.docsend.com/view/4c3zt8i
 * @requiresAddress
 */
// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
function BurgissCalcPeerGroupCapitalization(
  ID: any[][],
  PeerGroupId: any[][],
  EndDate?: any,
  Currency?: any,
  ReturnConstituentCount?: any,
  AdvancedParameter?: any,
  invocation?: any
) {
  const result = CheckProfile();
  if (result == true) {
    const uniqueCellId = generateUniqueCellId(invocation.address);
    return new PeerGroupCapitalizationFunctions().fnBurgissCalcPeerGroupCapitalization(
      ID,
      PeerGroupId,
      EndDate,
      Currency,
      ReturnConstituentCount,
      AdvancedParameter,
      uniqueCellId
    );
  }
  return result;
}
//#endregion PeerGroupCapitalization
//#region PercentileRank
/**
 * Calculates the internal rate of return for an investment as of the most recent valuation date relative to the end date then compares the result against a peer group. Returns the percent rank.
 * @CustomFunction PrivateiCalcIRR.PercentRank
 * @param ID[][] Investment ID.
 * @param [PeerGroupId] Peer Group ID. Default is default peer group associated with the investment if omitted.
 * @param [EndDate] End date that drives the measurement date for calculations and comparison date for peer group comparison. Default date is today if omitted. See Burgiss Help for more details.
 * @param [ReturnConstituentCount] If true, returns constituent count for the analysis. Default is false if omitted.
 * @param [AdvancedParameter] To enabled supported attributes for the formula.
 * @param {CustomFunctions.Invocation} invocation - The invocation context.
 * @helpUrl https://burgiss.docsend.com/view/4c3zt8i
 * @requiresAddress
 */
// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
function BurgissUniverseRankIRR(
  ID?: any[][],
  PeerGroupId?: any,
  EndDate?: any,
  ReturnConstituentCount?: any,
  AdvancedParameter?: any,
  invocation?: any
) {
  const result = CheckProfile();
  if (result == true) {
    const uniqueCellId = generateUniqueCellId(invocation.address);
    return new PercentileRankFunctions().fnBurgissUniverseRankIRR(
      ID,
      PeerGroupId,
      EndDate,
      ReturnConstituentCount,
      AdvancedParameter,
      uniqueCellId
    );
  }
  return result;
}
/**
 * Calculates the DPI for an investment as of the most recent valuation date relative to the end date then compares the result against a peer group. Returns the percent rank.
 * @CustomFunction PrivateiCalcDPI.PercentRank
 * @param ID[][] Investment ID.
 * @param [PeerGroupId] Peer Group ID. Default is default peer group associated with the investment if omitted.
 * @param [EndDate] End date that drives the measurement date for calculations and comparison date for peer group comparison. Default date is today if omitted. See Burgiss Help for more details.
 * @param [ReturnConstituentCount] If true, returns constituent count for the analysis. Default is false if omitted.
 * @param [AdvancedParameter] To enabled supported attributes for the formula.
 * @param {CustomFunctions.Invocation} invocation - The invocation context.
 * @helpUrl https://burgiss.docsend.com/view/4c3zt8i
 * @requiresAddress
 */
// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
function BurgissUniverseRankDPI(
  ID?: any[][],
  PeerGroupId?: any,
  EndDate?: any,
  ReturnConstituentCount?: any,
  AdvancedParameter?: any,
  invocation?: any
) {
  const result = CheckProfile();
  if (result == true) {
    const uniqueCellId = generateUniqueCellId(invocation.address);
    return new PercentileRankFunctions().fnBurgissUniverseRankDPI(
      ID,
      PeerGroupId,
      EndDate,
      ReturnConstituentCount,
      AdvancedParameter,
      uniqueCellId
    );
  }
  return result;
}
/**
 * Calculates the TVPI for an investment as of the most recent valuation date relative to the end date then compares the result against a peer group. Returns the percent rank.
 * @CustomFunction PrivateiCalcTVPI.PercentRank
 * @param ID[][] Investment ID.
 * @param [PeerGroupId] Peer Group ID. Default is default peer group associated with the investment if omitted.
 * @param [EndDate] End date that drives the measurement date for calculations and comparison date for peer group comparison. Default date is today if omitted. See Burgiss Help for more details.
 * @param [ReturnConstituentCount] If true, returns constituent count for the analysis. Default is false if omitted.
 * @param [AdvancedParameter] To enabled supported attributes for the formula.
 * @param {CustomFunctions.Invocation} invocation - The invocation context.
 * @helpUrl https://burgiss.docsend.com/view/4c3zt8i
 * @requiresAddress
 */
// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
function BurgissUniverseRankTVPI(
  ID?: any[][],
  PeerGroupId?: any,
  EndDate?: any,
  ReturnConstituentCount?: any,
  AdvancedParameter?: any,
  invocation?: any
) {
  const result = CheckProfile();
  if (result == true) {
    const uniqueCellId = generateUniqueCellId(invocation.address);
    return new PercentileRankFunctions().fnBurgissUniverseRankTVPI(
      ID,
      PeerGroupId,
      EndDate,
      ReturnConstituentCount,
      AdvancedParameter,
      uniqueCellId
    );
  }
  return result;
}
/**
 * Calculates the RVPI for an investment as of the most recent valuation date relative to the end date then compares the result against a peer group. Returns the percent rank.
 * @CustomFunction PrivateiCalcRVPI.PercentRank
 * @param ID[][] Investment ID.
 * @param [PeerGroupId] Peer Group ID. Default is default peer group associated with the investment if omitted.
 * @param [EndDate] End date that drives the measurement date for calculations and comparison date for peer group comparison. Default date is today if omitted. See Burgiss Help for more details.
 * @param [ReturnConstituentCount] If true, returns constituent count for the analysis. Default is false if omitted.
 * @param [AdvancedParameter] To enabled supported attributes for the formula.
 * @param {CustomFunctions.Invocation} invocation - The invocation context.
 * @helpUrl https://burgiss.docsend.com/view/4c3zt8i
 * @requiresAddress
 */
// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
function BurgissUniverseRankRVPI(
  ID?: any[][],
  PeerGroupId?: any,
  EndDate?: any,
  ReturnConstituentCount?: any,
  AdvancedParameter?: any,
  invocation?: any
) {
  const result = CheckProfile();
  if (result == true) {
    const uniqueCellId = generateUniqueCellId(invocation.address);
    return new PercentileRankFunctions().fnBurgissUniverseRankRVPI(
      ID,
      PeerGroupId,
      EndDate,
      ReturnConstituentCount,
      AdvancedParameter,
      uniqueCellId
    );
  }
  return result;
}
//#endregion PercentileRank
//#region PercentRankMeasures
/**
 * Calculates the pooled value of the measure for the specified peer group.
 * @CustomFunction PrivateiUniverseCalcIRR.PercentRank
 * @param [ID] Peer Group ID. Use 'Filter' to filter BMU using 'Filter' parameter.
 * @param [Value] Input value of the measure for comparison. Parameter expects unscaled amount (e. g. enter 0.1025 for 10.25%).
 * @param [Currency]  Individual results Currency. Default is user preference for Individual Results Currency if omitted.
 * @param [EndDate] End date of the analysis. Default is the most recent available quarter end date if omitted.
 * @param [ReturnConstituentCount]  If true, returns constituent count for the analysis. Default is false if omitted.
 * @param [Filter] Filter applied to BMU.
 * @param {CustomFunctions.Invocation} invocation - The invocation context.
 * @helpUrl https://burgiss.docsend.com/view/4c3zt8i
 * @requiresAddress
 */
// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
function BurgissUniverseCalcIRRPercentRank(
  ID: any[][],
  Value: any,
  Currency: any,
  EndDate?: any,
  ReturnConstituentCount?: any,
  Filter?: any,
  invocation?: any
) {
  const result = CheckProfile();
  if (result == true) {
    const uniqueCellId = generateUniqueCellId(invocation.address);
    return new PercentRankMeasuresFunctions().fnBurgissUniverseCalcIRRPercentRank(
      ID,
      Value,
      Currency,
      EndDate,
      ReturnConstituentCount,
      Filter,
      uniqueCellId
    );
  }
  return result;
}

/**
 * Calculates the pooled value of the measure for the specified peer group.
 * @CustomFunction PrivateiUniverseCalcIRR_QTD.PercentRank
 * @param [ID] Peer Group ID. Use 'Filter' to filter BMU using 'Filter' parameter.
 * @param [Value] Input value of the measure for comparison. Parameter expects unscaled amount (e. g. enter 0.1025 for 10.25%).
 * @param [Currency]  Individual results Currency. Default is user preference for Individual Results Currency if omitted.
 * @param [EndDate] End date of the analysis. Default is the most recent available quarter end date if omitted.
 * @param [ReturnConstituentCount]  If true, returns constituent count for the analysis. Default is false if omitted.
 * @param [Filter] Filter applied to BMU.
 * @param {CustomFunctions.Invocation} invocation - The invocation context.
 * @helpUrl https://burgiss.docsend.com/view/4c3zt8i
 * @requiresAddress
 */
// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
function BurgissUniverseCalcIRR_QTDPercentRank(
  ID: any[][],
  Value: any,
  Currency: any,
  EndDate?: any,
  ReturnConstituentCount?: any,
  Filter?: any,
  invocation?: any
) {
  const result = CheckProfile();
  if (result == true) {
    const uniqueCellId = generateUniqueCellId(invocation.address);
    return new PercentRankMeasuresFunctions().fnBurgissUniverseCalcIRR_QTDPercentRank(
      ID,
      Value,
      Currency,
      EndDate,
      ReturnConstituentCount,
      Filter,
      uniqueCellId
    );
  }
  return result;
}

/**
 * Calculates the pooled value of the measure for the specified peer group.
 * @CustomFunction PrivateiUniverseCalcIRR_YTD.PercentRank
 * @param [ID] Peer Group ID. Use 'Filter' to filter BMU using 'Filter' parameter.
 * @param [Value] Input value of the measure for comparison. Parameter expects unscaled amount (e. g. enter 0.1025 for 10.25%).
 * @param [Currency]  Individual results Currency. Default is user preference for Individual Results Currency if omitted.
 * @param [EndDate] End date of the analysis. Default is the most recent available quarter end date if omitted.
 * @param [ReturnConstituentCount]  If true, returns constituent count for the analysis. Default is false if omitted.
 * @param [Filter] Filter applied to BMU.
 * @param {CustomFunctions.Invocation} invocation - The invocation context.
 * @helpUrl https://burgiss.docsend.com/view/4c3zt8i
 * @requiresAddress
 */
// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
function BurgissUniverseCalcIRR_YTDPercentRank(
  ID: any[][],
  Value: any,
  Currency: any,
  EndDate?: any,
  ReturnConstituentCount?: any,
  Filter?: any,
  invocation?: any
) {
  const result = CheckProfile();
  if (result == true) {
    const uniqueCellId = generateUniqueCellId(invocation.address);
    return new PercentRankMeasuresFunctions().fnBurgissUniverseCalcIRR_YTDPercentRank(
      ID,
      Value,
      Currency,
      EndDate,
      ReturnConstituentCount,
      Filter,
      uniqueCellId
    );
  }
  return result;
}

/**
 * Calculates the pooled value of the measure for the specified peer group.
 * @CustomFunction PrivateiUniverseCalcIRR_1Year.PercentRank
 * @param [ID] Peer Group ID. Use 'Filter' to filter BMU using 'Filter' parameter.
 * @param [Value] Input value of the measure for comparison. Parameter expects unscaled amount (e. g. enter 0.1025 for 10.25%).
 * @param [Currency]  Individual results Currency. Default is user preference for Individual Results Currency if omitted.
 * @param [EndDate] End date of the analysis. Default is the most recent available quarter end date if omitted.
 * @param [ReturnConstituentCount]  If true, returns constituent count for the analysis. Default is false if omitted.
 * @param [Filter] Filter applied to BMU.
 * @param {CustomFunctions.Invocation} invocation - The invocation context.
 * @helpUrl https://burgiss.docsend.com/view/4c3zt8i
 * @requiresAddress
 * */
// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
function BurgissUniverseCalcIRR_1YearPercentRank(
  ID: any[][],
  Value: any,
  Currency: any,
  EndDate?: any,
  ReturnConstituentCount?: any,
  Filter?: any,
  invocation?: any
) {
  const result = CheckProfile();
  if (result == true) {
    const uniqueCellId = generateUniqueCellId(invocation.address);
    return new PercentRankMeasuresFunctions().fnBurgissUniverseCalcIRR_1YearPercentRank(
      ID,
      Value,
      Currency,
      EndDate,
      ReturnConstituentCount,
      Filter,
      uniqueCellId
    );
  }
  return result;
}

/**
 * Calculates the pooled value of the measure for the specified peer group.
 * @CustomFunction PrivateiUniverseCalcIRR_3Year.PercentRank
 * @param [ID] Peer Group ID. Use 'Filter' to filter BMU using 'Filter' parameter.
 * @param [Value] Input value of the measure for comparison. Parameter expects unscaled amount (e. g. enter 0.1025 for 10.25%).
 * @param [Currency]  Individual results Currency. Default is user preference for Individual Results Currency if omitted.
 * @param [EndDate] End date of the analysis. Default is the most recent available quarter end date if omitted.
 * @param [ReturnConstituentCount]  If true, returns constituent count for the analysis. Default is false if omitted.
 * @param [Filter] Filter applied to BMU.
 * @param {CustomFunctions.Invocation} invocation - The invocation context.
 * @helpUrl https://burgiss.docsend.com/view/4c3zt8i
 * @requiresAddress
 */
// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
function BurgissUniverseCalcIRR_3YearPercentRank(
  ID: any[][],
  Value: any,
  Currency: any,
  EndDate?: any,
  ReturnConstituentCount?: any,
  Filter?: any,
  invocation?: any
) {
  const result = CheckProfile();
  if (result == true) {
    const uniqueCellId = generateUniqueCellId(invocation.address);
    return new PercentRankMeasuresFunctions().fnBurgissUniverseCalcIRR_3YearPercentRank(
      ID,
      Value,
      Currency,
      EndDate,
      ReturnConstituentCount,
      Filter,
      uniqueCellId
    );
  }
  return result;
}

/**
 * Calculates the pooled value of the measure for the specified peer group.
 * @CustomFunction PrivateiUniverseCalcIRR_5Year.PercentRank
 * @param [ID] Peer Group ID. Use 'Filter' to filter BMU using 'Filter' parameter.
 * @param [Value] Input value of the measure for comparison. Parameter expects unscaled amount (e. g. enter 0.1025 for 10.25%).
 * @param [Currency]  Individual results Currency. Default is user preference for Individual Results Currency if omitted.
 * @param [EndDate] End date of the analysis. Default is the most recent available quarter end date if omitted.
 * @param [ReturnConstituentCount]  If true, returns constituent count for the analysis. Default is false if omitted.
 * @param [Filter] Filter applied to BMU.
 * @param {CustomFunctions.Invocation} invocation - The invocation context.
 * @helpUrl https://burgiss.docsend.com/view/4c3zt8i
 * @requiresAddress
 */
// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
function BurgissUniverseCalcIRR_5YearPercentRank(
  ID: any[][],
  Value: any,
  Currency: any,
  EndDate?: any,
  ReturnConstituentCount?: any,
  Filter?: any,
  invocation?: any
) {
  const result = CheckProfile();
  if (result == true) {
    const uniqueCellId = generateUniqueCellId(invocation.address);
    return new PercentRankMeasuresFunctions().fnBurgissUniverseCalcIRR_5YearPercentRank(
      ID,
      Value,
      Currency,
      EndDate,
      ReturnConstituentCount,
      Filter,
      uniqueCellId
    );
  }
  return result;
}

/**
 * Calculates the pooled value of the measure for the specified peer group.
 * @CustomFunction PrivateiUniverseCalcIRR_10Year.PercentRank
 * @param [ID] Peer Group ID. Use 'Filter' to filter BMU using 'Filter' parameter.
 * @param [Value] Input value of the measure for comparison. Parameter expects unscaled amount (e. g. enter 0.1025 for 10.25%).
 * @param [Currency]  Individual results Currency. Default is user preference for Individual Results Currency if omitted.
 * @param [EndDate] End date of the analysis. Default is the most recent available quarter end date if omitted.
 * @param [ReturnConstituentCount]  If true, returns constituent count for the analysis. Default is false if omitted.
 * @param [Filter] Filter applied to BMU.
 * @param {CustomFunctions.Invocation} invocation - The invocation context.
 * @helpUrl https://burgiss.docsend.com/view/4c3zt8i
 * @requiresAddress
 */
// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
function BurgissUniverseCalcIRR_10YearPercentRank(
  ID: any[][],
  Value: any,
  Currency: any,
  EndDate?: any,
  ReturnConstituentCount?: any,
  Filter?: any,
  invocation?: any
) {
  const result = CheckProfile();
  if (result == true) {
    const uniqueCellId = generateUniqueCellId(invocation.address);
    return new PercentRankMeasuresFunctions().fnBurgissUniverseCalcIRR_10YearPercentRank(
      ID,
      Value,
      Currency,
      EndDate,
      ReturnConstituentCount,
      Filter,
      uniqueCellId
    );
  }
  return result;
}

/**
 * Calculates the pooled value of the measure for the specified peer group.
 * @CustomFunction PrivateiUniverseCalcIRR_15Year.PercentRank
 * @param [ID] Peer Group ID. Use 'Filter' to filter BMU using 'Filter' parameter.
 * @param [Value] Input value of the measure for comparison. Parameter expects unscaled amount (e. g. enter 0.1025 for 10.25%).
 * @param [Currency]  Individual results Currency. Default is user preference for Individual Results Currency if omitted.
 * @param [EndDate] End date of the analysis. Default is the most recent available quarter end date if omitted.
 * @param [ReturnConstituentCount]  If true, returns constituent count for the analysis. Default is false if omitted.
 * @param [Filter] Filter applied to BMU.
 * @param {CustomFunctions.Invocation} invocation - The invocation context.
 * @helpUrl https://burgiss.docsend.com/view/4c3zt8i
 * @requiresAddress
 */
// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
function BurgissUniverseCalcIRR_15YearPercentRank(
  ID: any[][],
  Value: any,
  Currency: any,
  EndDate?: any,
  ReturnConstituentCount?: any,
  Filter?: any,
  invocation?: any
) {
  const result = CheckProfile();
  if (result == true) {
    const uniqueCellId = generateUniqueCellId(invocation.address);
    return new PercentRankMeasuresFunctions().fnBurgissUniverseCalcIRR_15YearPercentRank(
      ID,
      Value,
      Currency,
      EndDate,
      ReturnConstituentCount,
      Filter,
      uniqueCellId
    );
  }
  return result;
}

/**
 * Calculates the pooled value of the measure for the specified peer group.
 * @CustomFunction PrivateiUniverseCalcIRR_20Year.PercentRank
 * @param [ID] Peer Group ID. Use 'Filter' to filter BMU using 'Filter' parameter.
 * @param [Value] Input value of the measure for comparison. Parameter expects unscaled amount (e. g. enter 0.1025 for 10.25%).
 * @param [Currency]  Individual results Currency. Default is user preference for Individual Results Currency if omitted.
 * @param [EndDate] End date of the analysis. Default is the most recent available quarter end date if omitted.
 * @param [ReturnConstituentCount]  If true, returns constituent count for the analysis. Default is false if omitted.
 * @param [Filter] Filter applied to BMU.
 * @param {CustomFunctions.Invocation} invocation - The invocation context.
 * @helpUrl https://burgiss.docsend.com/view/4c3zt8i
 * @requiresAddress
 */
// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
function BurgissUniverseCalcIRR_20YearPercentRank(
  ID: any[][],
  Value: any,
  Currency: any,
  EndDate?: any,
  ReturnConstituentCount?: any,
  Filter?: any,
  invocation?: any
) {
  const result = CheckProfile();
  if (result == true) {
    const uniqueCellId = generateUniqueCellId(invocation.address);
    return new PercentRankMeasuresFunctions().fnBurgissUniverseCalcIRR_20YearPercentRank(
      ID,
      Value,
      Currency,
      EndDate,
      ReturnConstituentCount,
      Filter,
      uniqueCellId
    );
  }
  return result;
}

/**
 * Calculates the pooled value of the measure for the specified peer group.
 * @CustomFunction PrivateiUniverseCalcDPI.PercentRank
 * @param [ID] Peer Group ID. Use 'Filter' to filter BMU using 'Filter' parameter.
 * @param [Value] Input value of the measure for comparison. Parameter expects unscaled amount (e. g. enter 0.1025 for 10.25%).
 * @param [Currency]  Individual results Currency. Default is user preference for Individual Results Currency if omitted.
 * @param [EndDate] End date of the analysis. Default is the most recent available quarter end date if omitted.
 * @param [ReturnConstituentCount]  If true, returns constituent count for the analysis. Default is false if omitted.
 * @param [Filter] Filter applied to BMU.
 * @param {CustomFunctions.Invocation} invocation - The invocation context.
 * @helpUrl https://burgiss.docsend.com/view/4c3zt8i
 * @requiresAddress
 */
// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
function BurgissUniverseCalcDPIPercentRank(
  ID: any[][],
  Value: any,
  Currency: any,
  EndDate?: any,
  ReturnConstituentCount?: any,
  Filter?: any,
  invocation?: any
) {
  const result = CheckProfile();
  if (result == true) {
    const uniqueCellId = generateUniqueCellId(invocation.address);
    return new PercentRankMeasuresFunctions().fnBurgissUniverseCalcDPIPercentRank(
      ID,
      Value,
      Currency,
      EndDate,
      ReturnConstituentCount,
      Filter,
      uniqueCellId
    );
  }
  return result;
}

/**
 * Calculates the pooled value of the measure for the specified peer group.
 * @CustomFunction PrivateiUniverseCalcRVPI.PercentRank
 * @param [ID] Peer Group ID. Use 'Filter' to filter BMU using 'Filter' parameter.
 * @param [Value] Input value of the measure for comparison. Parameter expects unscaled amount (e. g. enter 0.1025 for 10.25%).
 * @param [Currency]  Individual results Currency. Default is user preference for Individual Results Currency if omitted.
 * @param [EndDate] End date of the analysis. Default is the most recent available quarter end date if omitted.
 * @param [ReturnConstituentCount]  If true, returns constituent count for the analysis. Default is false if omitted.
 * @param [Filter] Filter applied to BMU.
 * @param {CustomFunctions.Invocation} invocation - The invocation context.
 * @helpUrl https://burgiss.docsend.com/view/4c3zt8i
 * @requiresAddress
 */
// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
function BurgissUniverseCalcRVPIPercentRank(
  ID: any[][],
  Value: any,
  Currency: any,
  EndDate?: any,
  ReturnConstituentCount?: any,
  Filter?: any,
  invocation?: any
) {
  const result = CheckProfile();
  if (result == true) {
    const uniqueCellId = generateUniqueCellId(invocation.address);
    return new PercentRankMeasuresFunctions().fnBurgissUniverseCalcRVPIPercentRank(
      ID,
      Value,
      Currency,
      EndDate,
      ReturnConstituentCount,
      Filter,
      uniqueCellId
    );
  }
  return result;
}

/**
 * Calculates the pooled value of the measure for the specified peer group.
 * @CustomFunction PrivateiUniverseCalcTVPI.PercentRank
 * @param [ID] Peer Group ID. Use 'Filter' to filter BMU using 'Filter' parameter.
 * @param [Value] Input value of the measure for comparison. Parameter expects unscaled amount (e. g. enter 0.1025 for 10.25%).
 * @param [Currency]  Individual results Currency. Default is user preference for Individual Results Currency if omitted.
 * @param [EndDate] End date of the analysis. Default is the most recent available quarter end date if omitted.
 * @param [ReturnConstituentCount]  If true, returns constituent count for the analysis. Default is false if omitted.
 * @param [Filter] Filter applied to BMU.
 * @param {CustomFunctions.Invocation} invocation - The invocation context.
 * @helpUrl https://burgiss.docsend.com/view/4c3zt8i
 * @requiresAddress
 */
// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
function BurgissUniverseCalcTVPIPercentRank(
  ID: any[][],
  Value: any,
  Currency: any,
  EndDate?: any,
  ReturnConstituentCount?: any,
  Filter?: any,
  invocation?: any
) {
  const result = CheckProfile();
  if (result == true) {
    const uniqueCellId = generateUniqueCellId(invocation.address);
    return new PercentRankMeasuresFunctions().fnBurgissUniverseCalcTVPIPercentRank(
      ID,
      Value,
      Currency,
      EndDate,
      ReturnConstituentCount,
      Filter,
      uniqueCellId
    );
  }
  return result;
}
/**
 * Calculates the percent rank of a specified value for a measure in relation to specified peer group.
 * @CustomFunction PrivateiUniverseCalcValuation.PercentRank
 * @param ID[][] Peer Group ID. Use 'Filter' to filter BMU using 'Filter' parameter..
 * @param [Value] Input value of the measure for comparison. Parameter expects unscaled amount (e. g. enter 0.1025 for 10.25%).
 * @param [Currency]  Individual results Currency. Default is user preference for Individual Results Currency if omitted.
 * @param [EndDate] End date of the analysis. Default is the most recent available quarter end date if omitted.
 * @param [ReturnConstituentCount] If true, returns constituent count for the analysis. Default is false if omitted.
 * @param [Filter] Filter applied to BMU.
 * @param {CustomFunctions.Invocation} invocation - The invocation context.
 * @helpUrl https://burgiss.docsend.com/view/4c3zt8i
 * @requiresAddress
 */
// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
function BurgissUniverseCalcValuationPercentRank(
  ID: any[][],
  Value: any,
  Currency: any,
  EndDate?: any,
  ReturnConstituentCount?: any,
  Filter?: any,
  invocation?: any
) {
  const result = CheckProfile();
  if (result == true) {
    const uniqueCellId = generateUniqueCellId(invocation.address);
    return new PercentRankMeasuresFunctions().fnBurgissUniverseCalcValuationPercentRank(
      ID,
      Value,
      Currency,
      EndDate,
      ReturnConstituentCount,
      Filter,
      uniqueCellId
    );
  }
  return result;
}

/**
 * Calculates the percent rank of a specified value for a measure in relation to specified peer group.
 * @CustomFunction PrivateiUniverseCalcMIRR.PercentRank
 * @param ID[][] Peer Group ID. Use 'Filter' to filter BMU using 'Filter' parameter..
 * @param [Value] Input value of the measure for comparison. Parameter expects unscaled amount (e. g. enter 0.1025 for 10.25%).
 * @param [Currency]  Individual results Currency. Default is user preference for Individual Results Currency if omitted.
 * @param [EndDate] End date of the analysis. Default is the most recent available quarter end date if omitted.
 * @param [IndexID] Index ID. Default is user preference for Index if omitted.
 * @param [ReturnConstituentCount] If true, returns constituent count for the analysis. Default is false if omitted.
 * @param [Filter] Filter applied to BMU.
 * @param {CustomFunctions.Invocation} invocation - The invocation context.
 * @helpUrl https://burgiss.docsend.com/view/4c3zt8i
 * @requiresAddress
 */
// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
function BurgissUniverseCalcMIRRPercentRank(
  ID: any[][],
  Value: any,
  Currency: any,
  EndDate?: any,
  IndexID?: any,
  ReturnConstituentCount?: any,
  Filter?: any,
  invocation?: any
) {
  const result = CheckProfile();
  if (result == true) {
    const uniqueCellId = generateUniqueCellId(invocation.address);
    return new PercentRankMeasuresFunctions().fnBurgissUniverseCalcMIRRPercentRank(
      ID,
      Value,
      Currency,
      EndDate,
      IndexID,
      ReturnConstituentCount,
      Filter,
      uniqueCellId
    );
  }
  return result;
}
/**
 * Calculates the percent rank of a specified value for a measure in relation to specified peer group.
 * @CustomFunction PrivateiUniverseCalcRIRR.PercentRank
 * @param ID[][] Peer Group ID. Use 'Filter' to filter BMU using 'Filter' parameter..
 * @param [Value] Input value of the measure for comparison. Parameter expects unscaled amount (e. g. enter 0.1025 for 10.25%).
 * @param [Currency]  Individual results Currency. Default is user preference for Individual Results Currency if omitted.
 * @param [EndDate] End date of the analysis. Default is the most recent available quarter end date if omitted.
 * @param [ReturnConstituentCount] If true, returns constituent count for the analysis. Default is false if omitted.
 * @param [Filter] Filter applied to BMU.
 * @param {CustomFunctions.Invocation} invocation - The invocation context.
 * @helpUrl https://burgiss.docsend.com/view/4c3zt8i
 * @requiresAddress
 */
// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
function BurgissUniverseCalcRIRRPercentRank(
  ID: any[][],
  Value: any,
  Currency: any,
  EndDate?: any,
  ReturnConstituentCount?: any,
  Filter?: any,
  invocation?: any
) {
  const result = CheckProfile();
  if (result == true) {
    const uniqueCellId = generateUniqueCellId(invocation.address);
    return new PercentRankMeasuresFunctions().fnBurgissUniverseCalcRIRRPercentRank(
      ID,
      Value,
      Currency,
      EndDate,
      ReturnConstituentCount,
      Filter,
      uniqueCellId
    );
  }
  return result;
}
/**
 * Calculates the percent rank of a specified value for a measure in relation to specified peer group.
 * @CustomFunction PrivateiUniverseCalcDistributions.PercentRank
 * @param ID[][] Peer Group ID. Use 'Filter' to filter BMU using 'Filter' parameter..
 * @param [Value] Input value of the measure for comparison. Parameter expects unscaled amount (e. g. enter 0.1025 for 10.25%).
 * @param [Currency]  Individual results Currency. Default is user preference for Individual Results Currency if omitted.
 * @param [EndDate] End date of the analysis. Default is the most recent available quarter end date if omitted.
 * @param [ReturnConstituentCount] If true, returns constituent count for the analysis. Default is false if omitted.
 * @param [Filter] Filter applied to BMU.
 * @param {CustomFunctions.Invocation} invocation - The invocation context.
 * @requiresAddress
 */
// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
function BurgissUniverseCalcDistributionsPercentRank(
  ID: any[][],
  Value: any,
  Currency: any,
  EndDate?: any,
  ReturnConstituentCount?: any,
  Filter?: any,
  invocation?: any
) {
  const result = CheckProfile();
  if (result == true) {
    const uniqueCellId = generateUniqueCellId(invocation.address);
    return new PercentRankMeasuresFunctions().fnBurgissUniverseCalcDistributionsPercentRank(
      ID,
      Value,
      Currency,
      EndDate,
      ReturnConstituentCount,
      Filter,
      uniqueCellId
    );
  }
  return result;
}
/**
 * Calculates the percent rank of a specified value for a measure in relation to specified peer group.
 * @CustomFunction PrivateiUniverseCalcNetCashFlow.PercentRank
 * @param ID[][] Peer Group ID. Use 'Filter' to filter BMU using 'Filter' parameter..
 * @param [Value] Input value of the measure for comparison. Parameter expects unscaled amount (e. g. enter 0.1025 for 10.25%).
 * @param [Currency]  Individual results Currency. Default is user preference for Individual Results Currency if omitted.
 * @param [EndDate] End date of the analysis. Default is the most recent available quarter end date if omitted.
 * @param [ReturnConstituentCount] If true, returns constituent count for the analysis. Default is false if omitted.
 * @param [Filter] Filter applied to BMU.
 * @param {CustomFunctions.Invocation} invocation - The invocation context.
 * @requiresAddress
 */
// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
function BurgissUniverseCalcNetCashFlowPercentRank(
  ID: any[][],
  Value: any,
  Currency: any,
  EndDate?: any,
  ReturnConstituentCount?: any,
  Filter?: any,
  invocation?: any
) {
  const result = CheckProfile();
  if (result == true) {
    const uniqueCellId = generateUniqueCellId(invocation.address);
    return new PercentRankMeasuresFunctions().fnBurgissUniverseCalcNetCashFlowPercentRank(
      ID,
      Value,
      Currency,
      EndDate,
      ReturnConstituentCount,
      Filter,
      uniqueCellId
    );
  }
  return result;
}
/**
 * Calculates the percent rank of a specified value for a measure in relation to specified peer group.
 * @CustomFunction PrivateiUniverseCalcPaidInCapital.PercentRank
 * @param ID[][] Peer Group ID. Use 'Filter' to filter BMU using 'Filter' parameter..
 * @param [Value] Input value of the measure for comparison. Parameter expects unscaled amount (e. g. enter 0.1025 for 10.25%).
 * @param [Currency]  Individual results Currency. Default is user preference for Individual Results Currency if omitted.
 * @param [EndDate] End date of the analysis. Default is the most recent available quarter end date if omitted.
 * @param [ReturnConstituentCount] If true, returns constituent count for the analysis. Default is false if omitted.
 * @param [Filter] Filter applied to BMU.
 * @param {CustomFunctions.Invocation} invocation - The invocation context.
 * @helpUrl https://burgiss.docsend.com/view/4c3zt8i
 * @requiresAddress
 */
// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
function BurgissUniverseCalcPaidInCapitalPercentRank(
  ID: any[][],
  Value: any,
  Currency: any,
  EndDate?: any,
  ReturnConstituentCount?: any,
  Filter?: any,
  invocation?: any
) {
  const result = CheckProfile();
  if (result == true) {
    const uniqueCellId = generateUniqueCellId(invocation.address);
    return new PercentRankMeasuresFunctions().fnBurgissUniverseCalcPaidInCapitalPercentRank(
      ID,
      Value,
      Currency,
      EndDate,
      ReturnConstituentCount,
      Filter,
      uniqueCellId
    );
  }
  return result;
}
/**
 * Calculates the percent rank of a specified value for a measure in relation to specified peer group.
 * @CustomFunction PrivateiUniverseCalcGGS_DirectAlpha.PercentRank
 * @param ID[][] Peer Group ID. Use 'Filter' to filter BMU using 'Filter' parameter..
 * @param [Value] Input value of the measure for comparison. Parameter expects unscaled amount (e. g. enter 0.1025 for 10.25%).
 * @param [Currency]  Individual results Currency. Default is user preference for Individual Results Currency if omitted.
 * @param [EndDate] End date of the analysis. Default is the most recent available quarter end date if omitted.
 * @param [IndexID] Index ID. Default is user preference for Index if omitted.
 * @param [ReturnConstituentCount] If true, returns constituent count for the analysis. Default is false if omitted.
 * @param [Filter] Filter applied to BMU.
 * @param {CustomFunctions.Invocation} invocation - The invocation context.
 * @helpUrl https://burgiss.docsend.com/view/4c3zt8i
 * @requiresAddress
 */
// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
function BurgissUniverseCalcGSS_DirectAlphaPercentRank(
  ID: any[][],
  Value: any,
  Currency: any,
  EndDate?: any,
  IndexID?: any,
  ReturnConstituentCount?: any,
  Filter?: any,
  invocation?: any
) {
  const result = CheckProfile();
  if (result == true) {
    const uniqueCellId = generateUniqueCellId(invocation.address);
    return new PercentRankMeasuresFunctions().fnBurgissUniverseCalcGGS_DirectAlphaPercentRank(
      ID,
      Value,
      Currency,
      EndDate,
      IndexID,
      ReturnConstituentCount,
      Filter,
      uniqueCellId
    );
  }
  return result;
}

/**
 * Calculates the percent rank of a specified value for a measure in relation to specified peer group.
 * @CustomFunction PrivateiUniverseCalcKS_PME.PercentRank
 * @param ID[][] Peer Group ID. Use 'Filter' to filter BMU using 'Filter' parameter..
 * @param [Value] Input value of the measure for comparison. Parameter expects unscaled amount (e. g. enter 0.1025 for 10.25%).
 * @param [Currency]  Individual results Currency. Default is user preference for Individual Results Currency if omitted.
 * @param [EndDate] End date of the analysis. Default is the most recent available quarter end date if omitted.
 * @param [IndexID] Index ID. Default is user preference for Index if omitted.
 * @param [ReturnConstituentCount] If true, returns constituent count for the analysis. Default is false if omitted.
 * @param [Filter] Filter applied to BMU.
 * @param {CustomFunctions.Invocation} invocation - The invocation context.
 * @helpUrl https://burgiss.docsend.com/view/4c3zt8i
 * @requiresAddress
 */
// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
function BurgissUniverseCalcKS_PMEPercentRank(
  ID: any[][],
  Value: any,
  Currency: any,
  EndDate?: any,
  IndexID?: any,
  ReturnConstituentCount?: any,
  Filter?: any,
  invocation?: any
) {
  const result = CheckProfile();
  if (result == true) {
    const uniqueCellId = generateUniqueCellId(invocation.address);
    return new PercentRankMeasuresFunctions().fnBurgissUniverseCalcKS_PMEPercentRank(
      ID,
      Value,
      Currency,
      EndDate,
      IndexID,
      ReturnConstituentCount,
      Filter,
      uniqueCellId
    );
  }
  return result;
}

/**
 * Calculates the percent rank of a specified value for a measure in relation to specified peer group.
 * @CustomFunction PrivateiUniverseCalcLN_ICMIRR.PercentRank
 * @param ID[][] Peer Group ID. Use 'Filter' to filter BMU using 'Filter' parameter..
 * @param [Value] Input value of the measure for comparison. Parameter expects unscaled amount (e. g. enter 0.1025 for 10.25%).
 * @param [Currency]  Individual results Currency. Default is user preference for Individual Results Currency if omitted.
 * @param [EndDate] End date of the analysis. Default is the most recent available quarter end date if omitted.
 * @param [IndexID] Index ID. Default is user preference for Index if omitted.
 * @param [ReturnConstituentCount] If true, returns constituent count for the analysis. Default is false if omitted.
 * @param [Filter] Filter applied to BMU.
 * @param {CustomFunctions.Invocation} invocation - The invocation context.
 * @helpUrl https://burgiss.docsend.com/view/4c3zt8i
 * @requiresAddress
 */
// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
function BurgissUniverseCalcLN_ICMIRRPercentRank(
  ID: any[][],
  Value: any,
  Currency: any,
  EndDate?: any,
  IndexID?: any,
  ReturnConstituentCount?: any,
  Filter?: any,
  invocation?: any
) {
  const result = CheckProfile();
  if (result == true) {
    const uniqueCellId = generateUniqueCellId(invocation.address);
    return new PercentRankMeasuresFunctions().fnBurgissUniverseCalcLN_ICMIRRPercentRank(
      ID,
      Value,
      Currency,
      EndDate,
      IndexID,
      ReturnConstituentCount,
      Filter,
      uniqueCellId
    );
  }
  return result;
}

/**
 * Calculates the percent rank of a specified value for a measure in relation to specified peer group.
 * @CustomFunction PrivateiUniverseCalcLN_ICMIRRSpread.PercentRank
 * @param ID[][] Peer Group ID. Use 'Filter' to filter BMU using 'Filter' parameter..
 * @param [Value] Input value of the measure for comparison. Parameter expects unscaled amount (e. g. enter 0.1025 for 10.25%).
 * @param [Currency]  Individual results Currency. Default is user preference for Individual Results Currency if omitted.
 * @param [EndDate] End date of the analysis. Default is the most recent available quarter end date if omitted.
 * @param [IndexID] Index ID. Default is user preference for Index if omitted.
 * @param [ReturnConstituentCount] If true, returns constituent count for the analysis. Default is false if omitted.
 * @param [Filter] Filter applied to BMU.
 * @param {CustomFunctions.Invocation} invocation - The invocation context.
 * @helpUrl https://burgiss.docsend.com/view/4c3zt8i
 * @requiresAddress
 */
// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
function BurgissUniverseCalcLN_ICMIRRSpreadPercentRank(
  ID: any[][],
  Value: any,
  Currency: any,
  EndDate?: any,
  IndexID?: any,
  ReturnConstituentCount?: any,
  Filter?: any,
  invocation?: any
) {
  const result = CheckProfile();
  if (result == true) {
    const uniqueCellId = generateUniqueCellId(invocation.address);
    return new PercentRankMeasuresFunctions().fnBurgissUniverseCalcLN_ICMIRRSpreadPercentRank(
      ID,
      Value,
      Currency,
      EndDate,
      IndexID,
      ReturnConstituentCount,
      Filter,
      uniqueCellId
    );
  }
  return result;
}

/**
 * Calculates the percent rank of a specified value for a measure in relation to specified peer group.
 * @CustomFunction PrivateiUniverseCalcLN_ICMValuationSpread.PercentRank
 * @param ID[][] Peer Group ID. Use 'Filter' to filter BMU using 'Filter' parameter..
 * @param [Value] Input value of the measure for comparison. Parameter expects unscaled amount (e. g. enter 0.1025 for 10.25%).
 * @param [Currency]  Individual results Currency. Default is user preference for Individual Results Currency if omitted.
 * @param [EndDate] End date of the analysis. Default is the most recent available quarter end date if omitted.
 * @param [IndexID] Index ID. Default is user preference for Index if omitted.
 * @param [ReturnConstituentCount] If true, returns constituent count for the analysis. Default is false if omitted.
 * @param [Filter] Filter applied to BMU.
 * @param {CustomFunctions.Invocation} invocation - The invocation context.
 * @helpUrl https://burgiss.docsend.com/view/4c3zt8i
 * @requiresAddress
 */
// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
function BurgissUniverseCalcLN_ICMValuationSpreadPercentRank(
  ID: any[][],
  Value: any,
  Currency: any,
  EndDate?: any,
  IndexID?: any,
  ReturnConstituentCount?: any,
  Filter?: any,
  invocation?: any
) {
  const result = CheckProfile();
  if (result == true) {
    const uniqueCellId = generateUniqueCellId(invocation.address);
    return new PercentRankMeasuresFunctions().fnBurgissUniverseCalcLN_ICMValuationSpreadPercentRank(
      ID,
      Value,
      Currency,
      EndDate,
      IndexID,
      ReturnConstituentCount,
      Filter,
      uniqueCellId
    );
  }
  return result;
}

//#endregion PercentRankMeasures
//#region Pooledbenchmark
/**
 * Calculates the pooled portfolio benchmark IRR.
 * @CustomFunction PrivateiCalcPooledBenchmarkIRR
 * @param ID[][] ID(s) corresponding to the record type defined by the ID Type parameter.  Use “All” in include all records. Default ID Type is Investment if ID Type is omitted.
 * @param [EndDate] End date that drives the measurement date for calculations. Actual measurement date can be affected by Roll Forward parameter. Default date is today if omitted.
 * @param [IdType] Describes the record type corresponding to values passed to the ID parameter. Default is Investment if omitted. See Burgiss Help for more details
 * @param [SelectionSetId] Selection set filter applied to records denoted by the ID parameter. Default is no selection set if omitted.
 * @param [WeightingMethod] Weighting Methodology applied to funds selected from the Burgiss Manager Universe. Default is Capitalization if omitted. See Burgiss help for more details
 * @param [Filter] Filter applied to records denoted by the ID parameter. Filter is applied after selection set is applied. Default is no filter if omitted. See Burgiss Help for more details.
 * @param [Currency] Reflects the Currency of the amounts in which calculations are performed. Default is base if omitted.
 * @param [StartDate]  Start Date that drives the measurement date for calculations. Date must be a valid BMU Start Date. Default date is inception if omitted.
 * @param [RequireReportedValuation] Denotes whether or not to only include BMU funds that have a reported valuation as of the Start Date and/or End Date. Default is false if omitted.
 * @param {CustomFunctions.Invocation} invocation - The invocation context.
 * @helpUrl https://burgiss.docsend.com/view/4c3zt8i
 * @requiresAddress
 */
// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
function BurgissCalcPooledBenchmarkIRR(
  ID?: any[][],
  EndDate?: any,
  IdType?: string,
  SelectionSetId?: any,
  WeightingMethod?: any,
  Filter?: any,
  Currency?: string,
  StartDate?: any,
  RequireReportedValuation?: any,
  invocation?: any
) {
  const result = CheckProfile();
  if (result == true) {
    const uniqueCellId = generateUniqueCellId(invocation.address);
    return new PooledBenchmarkFunctions().fnBurgissCalcPooledBenchmarkIRR(
      ID,
      EndDate,
      IdType,
      SelectionSetId,
      WeightingMethod,
      Filter,
      Currency,
      StartDate,
      RequireReportedValuation,
      uniqueCellId
    );
  }
  return result;
}

/**
 * Calculates the pooled portfolio benchmark TWRR.
 * @CustomFunction PrivateiCalcPooledBenchmarkTWRR
 * @param ID[][] ID(s) corresponding to the record type defined by the ID Type parameter.  Use “All” in include all records. Default ID Type is Investment if ID Type is omitted.
 * @param [EndDate] End date that drives the measurement date for calculations. Actual measurement date can be affected by Roll Forward parameter. Default date is today if omitted.
 * @param [IdType] Describes the record type corresponding to values passed to the ID parameter. Default is Investment if omitted. See Burgiss Help for more details
 * @param [SelectionSetId] Selection set filter applied to records denoted by the ID parameter. Default is no selection set if omitted.
 * @param [WeightingMethod] Weighting Methodology applied to funds selected from the Burgiss Manager Universe. Default is Capitalization if omitted. See Burgiss help for more details
 * @param [Filter] Filter applied to records denoted by the ID parameter. Filter is applied after selection set is applied. Default is no filter if omitted. See Burgiss Help for more details.
 * @param [Currency] Reflects the Currency of the amounts in which calculations are performed. Default is base if omitted.
 * @param [StartDate]  Start Date that drives the measurement date for calculations. Date must be a valid BMU Start Date. Default date is inception if omitted.
 * @param [RequireReportedValuation] Denotes whether or not to only include BMU funds that have a reported valuation as of the Start Date and/or End Date. Default is false if omitted.
 * @param [AdvancedParameter] To enabled supported attributes for the formula.
 * @param {CustomFunctions.Invocation} invocation - The invocation context.
 * @helpUrl https://burgiss.docsend.com/view/4c3zt8i
 * @requiresAddress
 */
// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
function BurgissCalcPooledBenchmarkTWRR(
  ID?: any[][],
  EndDate?: any,
  IdType?: string,
  SelectionSetId?: any,
  WeightingMethod?: any,
  Filter?: any,
  Currency?: string,
  StartDate?: any,
  RequireReportedValuation?: any,
  AdvancedParameter?: any,
  invocation?: any
) {
  const result = CheckProfile();
  if (result == true) {
    const uniqueCellId = generateUniqueCellId(invocation.address);
    return new PooledBenchmarkFunctions().fnBurgissCalcPooledBenchmarkTWRR(
      ID,
      EndDate,
      IdType,
      SelectionSetId,
      WeightingMethod,
      Filter,
      Currency,
      StartDate,
      RequireReportedValuation,
      AdvancedParameter,
      uniqueCellId
    );
  }
  return result;
}
//#endregion Pooledbenchmark
//#region Pooledbenchmarkphase2
/**
 * Calculates the pooled portfolio benchmark DPI.
 * @CustomFunction PrivateiCalcPooledBenchmarkDPI
 * @param ID[][] ID(s) corresponding to the record type defined by the ID Type parameter.  Use “All” in include all records. Default ID Type is Investment if ID Type is omitted.
 * @param [EndDate] End date that drives the measurement date for calculations. Actual measurement date can be affected by Roll Forward parameter. Default date is today if omitted.
 * @param [IdType] Describes the record type corresponding to values passed to the ID parameter. Default is Investment if omitted. See Burgiss Help for more details
 * @param [SelectionSetId] Selection set filter applied to records denoted by the ID parameter. Default is no selection set if omitted.
 * @param [WeightingMethod] Weighting Methodology applied to funds selected from the Burgiss Manager Universe. Default is Capitalization if omitted. See Burgiss help for more details
 * @param [Filter] Filter applied to records denoted by the ID parameter. Filter is applied after selection set is applied. Default is no filter if omitted. See Burgiss Help for more details.
 * @param [Currency] Reflects the Currency of the amounts in which calculations are performed. Default is base if omitted.
 * @param [RequireReportedValuation] Denotes whether or not to only include BMU funds that have a reported valuation as of the Start Date and/or End Date. Default is false if omitted.
 * @param {CustomFunctions.Invocation} invocation - The invocation context.
 * @helpUrl https://burgiss.docsend.com/view/4c3zt8i
 * @requiresAddress
 */
// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
function BurgissCalcPooledBenchmarkDPI(
  ID?: any[][],
  EndDate?: any,
  IdType?: string,
  SelectionSetId?: any,
  WeightingMethod?: any,
  Filter?: any,
  Currency?: string,
  RequireReportedValuation?: any,
  invocation?: any
) {
  const result = CheckProfile();
  if (result == true) {
    const uniqueCellId = generateUniqueCellId(invocation.address);
    return new PooledBenchMarkPhase2().fnBurgissCalcPooledBenchmarkDPI(
      ID,
      EndDate,
      IdType,
      SelectionSetId,
      WeightingMethod,
      Filter,
      Currency,
      RequireReportedValuation,
      uniqueCellId
    );
  }
  return result;
}

/**
 * Calculates the pooled portfolio benchmark TVPI.
 * @CustomFunction PrivateiCalcPooledBenchmarkTVPI
 * @param ID[][] ID(s) corresponding to the record type defined by the ID Type parameter.  Use “All” in include all records. Default ID Type is Investment if ID Type is omitted.
 * @param [EndDate] End date that drives the measurement date for calculations. Actual measurement date can be affected by Roll Forward parameter. Default date is today if omitted.
 * @param [IdType] Describes the record type corresponding to values passed to the ID parameter. Default is Investment if omitted. See Burgiss Help for more details
 * @param [SelectionSetId] Selection set filter applied to records denoted by the ID parameter. Default is no selection set if omitted.
 * @param [WeightingMethod] Weighting Methodology applied to funds selected from the Burgiss Manager Universe. Default is Capitalization if omitted. See Burgiss help for more details
 * @param [Filter] Filter applied to records denoted by the ID parameter. Filter is applied after selection set is applied. Default is no filter if omitted. See Burgiss Help for more details.
 * @param [Currency] Reflects the Currency of the amounts in which calculations are performed. Default is base if omitted.
 * @param [RequireReportedValuation] Denotes whether or not to only include BMU funds that have a reported valuation as of the Start Date and/or End Date. Default is false if omitted.
 * @param {CustomFunctions.Invocation} invocation - The invocation context.
 * @helpUrl https://burgiss.docsend.com/view/4c3zt8i
 * @requiresAddress
 */
// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
function BurgissCalcPooledBenchmarkTVPI(
  ID?: any[][],
  EndDate?: any,
  IdType?: string,
  SelectionSetId?: any,
  WeightingMethod?: any,
  Filter?: any,
  Currency?: string,
  RequireReportedValuation?: any,
  invocation?: any
) {
  const result = CheckProfile();
  if (result == true) {
    const uniqueCellId = generateUniqueCellId(invocation.address);
    return new PooledBenchMarkPhase2().fnBurgissCalcPooledBenchmarkTVPI(
      ID,
      EndDate,
      IdType,
      SelectionSetId,
      WeightingMethod,
      Filter,
      Currency,
      RequireReportedValuation,
      uniqueCellId
    );
  }
  return result;
}

/**
 * Calculates the pooled portfolio benchmark RVPI.
 * @CustomFunction PrivateiCalcPooledBenchmarkRVPI
 * @param ID[][] ID(s) corresponding to the record type defined by the ID Type parameter.  Use “All” in include all records. Default ID Type is Investment if ID Type is omitted.
 * @param [EndDate] End date that drives the measurement date for calculations. Actual measurement date can be affected by Roll Forward parameter. Default date is today if omitted.
 * @param [IdType] Describes the record type corresponding to values passed to the ID parameter. Default is Investment if omitted. See Burgiss Help for more details
 * @param [SelectionSetId] Selection set filter applied to records denoted by the ID parameter. Default is no selection set if omitted.
 * @param [WeightingMethod] Weighting Methodology applied to funds selected from the Burgiss Manager Universe. Default is Capitalization if omitted. See Burgiss help for more details
 * @param [Filter] Filter applied to records denoted by the ID parameter. Filter is applied after selection set is applied. Default is no filter if omitted. See Burgiss Help for more details.
 * @param [Currency] Reflects the Currency of the amounts in which calculations are performed. Default is base if omitted.
 * @param [RequireReportedValuation] Denotes whether or not to only include BMU funds that have a reported valuation as of the Start Date and/or End Date. Default is false if omitted.
 * @param {CustomFunctions.Invocation} invocation - The invocation context.
 * @helpUrl https://burgiss.docsend.com/view/4c3zt8i
 * @requiresAddress
 */
// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
function BurgissCalcPooledBenchmarkRVPI(
  ID?: any[][],
  EndDate?: any,
  IdType?: string,
  SelectionSetId?: any,
  WeightingMethod?: any,
  Filter?: any,
  Currency?: string,
  RequireReportedValuation?: any,
  invocation?: any
) {
  const result = CheckProfile();
  if (result == true) {
    const uniqueCellId = generateUniqueCellId(invocation.address);
    return new PooledBenchMarkPhase2().fnBurgissCalcPooledBenchmarkRVPI(
      ID,
      EndDate,
      IdType,
      SelectionSetId,
      WeightingMethod,
      Filter,
      Currency,
      RequireReportedValuation,
      uniqueCellId
    );
  }
  return result;
}
//#endregion Pooledbenchmarkphase2
//#region  Pooledbenchmarkphase2Univ
/**
 * Calculates the pooled portfolio benchmark DPI.
 * @CustomFunction PrivateiCalcPooledBenchmarkDPI.ByWeight
 * @param [Universe] Peer group ID or Filter appliedto Burgiss Manager Universe.
 * @param [Weight] Weights for each group.
 * @param [EndDate] End date that drives the measurement date for calculations. Date must be a valid Burgiss Manager Universe (BMU) analysis date. Default date is the latest BMU analysis date if omitted.
 * @param [WeightingMethod] Weighting Methodology applied to funds selected from the Burgiss Manager Universe. Default is Capitalization if omitted. See Burgiss help for more details
 * @param [Currency] Denotes the Currency of the amounts in which calculations are performed. Default is base if omitted.
 * @param [RequirReportedValuation] Denotes whether or not to only include BMU funds that have a reported valuation as of the Start Date and/or End Date. Default is false if omitted.
 * @param {CustomFunctions.Invocation} invocation - The invocation context.
 * @helpUrl https://burgiss.docsend.com/view/4c3zt8i
 * @requiresAddress
 */
// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
function BurgissCalcPooledBenchmarkDPIByWeight(
  Universe?: string,
  Weight?: string,
  EndDate?: any,
  WeightingMethod?: string,
  Currency?: string,
  RequirReportedValuation?: any,
  invocation?: any
) {
  const result = CheckProfile();
  if (result == true) {
    const uniqueCellId = generateUniqueCellId(invocation.address);
    return new PooledBenchmarkPhase2UnivFunctions().fnBurgissCalcPooledBenchmarkDPIByWeight(
      Universe,
      Weight,
      EndDate,
      WeightingMethod,
      Currency,
      RequirReportedValuation,
      uniqueCellId
    );
  }
  return result;
}

/**
 *Calculates the pooled portfolio benchmark TVPI.
 * @CustomFunction PrivateiCalcPooledBenchmarkTVPI.ByWeight
 * @param [Universe] Peer group ID or Filter appliedto Burgiss Manager Universe.
 * @param [Weight] Weights for each group.
 * @param [EndDate] End date that drives the measurement date for calculations. Date must be a valid Burgiss Manager Universe (BMU) analysis date. Default date is the latest BMU analysis date if omitted.
 * @param [WeightingMethod] Weighting Methodology applied to funds selected from the Burgiss Manager Universe. Default is Capitalization if omitted. See Burgiss help for more details
 * @param [Currency] Denotes the Currency of the amounts in which calculations are performed. Default is base if omitted.
 * @param [RequirReportedValuation] Denotes whether or not to only include BMU funds that have a reported valuation as of the Start Date and/or End Date. Default is false if omitted.
 * @param {CustomFunctions.Invocation} invocation - The invocation context.
 * @helpUrl https://burgiss.docsend.com/view/4c3zt8i
 * @requiresAddress
 */
// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
function BurgissCalcPooledBenchmarkTVPIByWeight(
  Universe?: string,
  Weight?: string,
  EndDate?: any,
  WeightingMethod?: string,
  Currency?: string,
  RequirReportedValuation?: any,
  invocation?: any
) {
  const result = CheckProfile();
  if (result == true) {
    const uniqueCellId = generateUniqueCellId(invocation.address);
    return new PooledBenchmarkPhase2UnivFunctions().fnBurgissCalcPooledBenchmarkTVPIByWeight(
      Universe,
      Weight,
      EndDate,
      WeightingMethod,
      Currency,
      RequirReportedValuation,
      uniqueCellId
    );
  }
  return result;
}

/**
 * Calculates the pooled portfolio benchmark RVPI.
 * @CustomFunction PrivateiCalcPooledBenchmarkRVPI.ByWeight
 * @param [Universe] Peer group ID or Filter appliedto Burgiss Manager Universe.
 * @param [Weight] Weights for each group.
 * @param [EndDate] End date that drives the measurement date for calculations. Date must be a valid Burgiss Manager Universe (BMU) analysis date. Default date is the latest BMU analysis date if omitted.
 * @param [WeightingMethod] Weighting Methodology applied to funds selected from the Burgiss Manager Universe. Default is Capitalization if omitted. See Burgiss help for more details
 * @param [Currency] Denotes the Currency of the amounts in which calculations are performed. Default is base if omitted.
 * @param [RequirReportedValuation] Denotes whether or not to only include BMU funds that have a reported valuation as of the Start Date and/or End Date. Default is false if omitted.
 * @param {CustomFunctions.Invocation} invocation - The invocation context.
 * @helpUrl https://burgiss.docsend.com/view/4c3zt8i
 * @requiresAddress
 */
// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
function BurgissCalcPooledBenchmarkRVPIByWeight(
  Universe?: string,
  Weight?: string,
  EndDate?: any,
  WeightingMethod?: string,
  Currency?: string,
  RequirReportedValuation?: any,
  invocation?: any
) {
  const result = CheckProfile();
  if (result == true) {
    const uniqueCellId = generateUniqueCellId(invocation.address);
    return new PooledBenchmarkPhase2UnivFunctions().fnBurgissCalcPooledBenchmarkRVPIByWeight(
      Universe,
      Weight,
      EndDate,
      WeightingMethod,
      Currency,
      RequirReportedValuation,
      uniqueCellId
    );
  }
  return result;
}

/**
 * Calculates the pooled portfolio benchmark DPI.
 * @CustomFunction PrivateiCalcPooledBenchmarkDPI.ByCommitment
 * @param [Universe] Peer group ID or Filter appliedto Burgiss Manager Universe.
 * @param [Commitment] Commitment for each group.
 * @param [EndDate] End date that drives the measurement date for calculations. Date must be a valid Burgiss Manager Universe (BMU) analysis date. Default date is the latest BMU analysis date if omitted.
 * @param [WeightingMethod] Weighting Methodology applied to funds selected from the Burgiss Manager Universe. Default is Capitalization if omitted. See Burgiss help for more details
 * @param [Currency] Denotes the Currency of the amounts in which calculations are performed. Default is base if omitted.
 * @param [RequirReportedValuation] Denotes whether or not to only include BMU funds that have a reported valuation as of the Start Date and/or End Date. Default is false if omitted.
 * @param {CustomFunctions.Invocation} invocation - The invocation context.
 * @helpUrl https://burgiss.docsend.com/view/4c3zt8i
 * @requiresAddress
 */
// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
function BurgissCalcPooledBenchmarkDPIByCommitment(
  Universe?: string,
  Commitment?: string,
  EndDate?: any,
  WeightingMethod?: string,
  Currency?: string,
  RequirReportedValuation?: any,
  invocation?: any
) {
  const result = CheckProfile();
  if (result == true) {
    const uniqueCellId = generateUniqueCellId(invocation.address);
    return new PooledBenchmarkPhase2UnivFunctions().fnBurgissCalcPooledBenchmarkDPIByCommitment(
      Universe,
      Commitment,
      EndDate,
      WeightingMethod,
      Currency,
      RequirReportedValuation,
      uniqueCellId
    );
  }
  return result;
}

/**
 * Calculates the pooled portfolio benchmark TVPI.
 * @CustomFunction PrivateiCalcPooledBenchmarkTVPI.ByCommitment
 * @param [Universe] Peer group ID or Filter appliedto Burgiss Manager Universe.
 * @param [Commitment] Commitment for each group.
 * @param [EndDate] End date that drives the measurement date for calculations. Date must be a valid Burgiss Manager Universe (BMU) analysis date. Default date is the latest BMU analysis date if omitted.
 * @param [WeightingMethod] Weighting Methodology applied to funds selected from the Burgiss Manager Universe. Default is Capitalization if omitted. See Burgiss help for more details
 * @param [Currency] Denotes the Currency of the amounts in which calculations are performed. Default is base if omitted.
 * @param [RequirReportedValuation] Denotes whether or not to only include BMU funds that have a reported valuation as of the Start Date and/or End Date. Default is false if omitted.
 * @param {CustomFunctions.Invocation} invocation - The invocation context.
 * @helpUrl https://burgiss.docsend.com/view/4c3zt8i
 * @requiresAddress
 */
// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
function BurgissCalcPooledBenchmarkTVPIByCommitment(
  Universe?: string,
  Commitment?: string,
  EndDate?: any,
  WeightingMethod?: string,
  Currency?: string,
  RequirReportedValuation?: any,
  invocation?: any
) {
  const result = CheckProfile();
  if (result == true) {
    const uniqueCellId = generateUniqueCellId(invocation.address);
    return new PooledBenchmarkPhase2UnivFunctions().fnBurgissCalcPooledBenchmarkTVPIByCommitment(
      Universe,
      Commitment,
      EndDate,
      WeightingMethod,
      Currency,
      RequirReportedValuation,
      uniqueCellId
    );
  }
  return result;
}

/**
 * Calculates the pooled portfolio benchmark RVPI.
 * @CustomFunction PrivateiCalcPooledBenchmarkRVPI.ByCommitment
 * @param [Universe] Peer group ID or Filter appliedto Burgiss Manager Universe.
 * @param [Commitment] Commitment for each group.
 * @param [EndDate] End date that drives the measurement date for calculations. Date must be a valid Burgiss Manager Universe (BMU) analysis date. Default date is the latest BMU analysis date if omitted.
 * @param [WeightingMethod] Weighting Methodology applied to funds selected from the Burgiss Manager Universe. Default is Capitalization if omitted. See Burgiss help for more details
 * @param [Currency] Denotes the Currency of the amounts in which calculations are performed. Default is base if omitted.
 * @param [RequirReportedValuation] Denotes whether or not to only include BMU funds that have a reported valuation as of the Start Date and/or End Date. Default is false if omitted.
 * @param {CustomFunctions.Invocation} invocation - The invocation context.
 * @helpUrl https://burgiss.docsend.com/view/4c3zt8i
 * @requiresAddress
 */
// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
function BurgissCalcPooledBenchmarkRVPIByCommitment(
  Universe?: string,
  Commitment?: string,
  EndDate?: any,
  WeightingMethod?: string,
  Currency?: string,
  RequirReportedValuation?: any,
  invocation?: any
) {
  const result = CheckProfile();
  if (result == true) {
    const uniqueCellId = generateUniqueCellId(invocation.address);
    return new PooledBenchmarkPhase2UnivFunctions().fnBurgissCalcPooledBenchmarkRVPIByCommitment(
      Universe,
      Commitment,
      EndDate,
      WeightingMethod,
      Currency,
      RequirReportedValuation,
      uniqueCellId
    );
  }
  return result;
}

//#endregion Pooledbenchmarkphase2Univ
//#region  PooledbenchmarkUniv
/**
 * Calculates the pooled portfolio benchmark TWRR.
 * @CustomFunction PrivateiCalcPooledBenchmarkTWRR.ByWeight
 * @param [Universe] Peer group ID or Filter appliedto Burgiss Manager Universe.
 * @param [Weight] Weights for each group.
 * @param [StartDate] Start Date that drives the measurement date for calculations. Date must be a valid BMU Start Date. Default date is inception if omitted.
 * @param [EndDate] End date that drives the measurement date for calculations. Date must be a valid Burgiss Manager Universe (BMU) analysis date. Default date is the latest BMU analysis date if omitted.
 * @param [WeightingMethod] Weighting Methodology applied to funds selected from the Burgiss Manager Universe. Default is Capitalization if omitted. See Burgiss help for more details
 * @param [Currency] Denotes the Currency of the amounts in which calculations are performed. Default is base if omitted.
 * @param [RequirReportedValuation] Denotes whether or not to only include BMU funds that have a reported valuation as of the Start Date and/or End Date. Default is false if omitted.
 * @param [AdvancedParameter] To enabled supported attributes for the formula.
 * @param {CustomFunctions.Invocation} invocation - The invocation context.
 * @helpUrl https://burgiss.docsend.com/view/4c3zt8i
 * @requiresAddress
 */
// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
function BurgissCalcPooledBenchmarkTWRRByWeight(
  Universe?: string,
  Weight?: string,
  StartDate?: any,
  EndDate?: any,
  WeightingMethod?: string,
  Currency?: string,
  RequirReportedValuation?: any,

  AdvancedParameter?: string,
  invocation?: any
) {
  const result = CheckProfile();
  if (result == true) {
    const uniqueCellId = generateUniqueCellId(invocation.address);
    return new PooledBenchmarkUnivFunctions().fnBurgissCalcPooledBenchmarkTWRRByWeight(
      Universe,
      Weight,
      StartDate,
      EndDate,
      WeightingMethod,
      Currency,
      RequirReportedValuation,
      uniqueCellId,
      AdvancedParameter
    );
  }
  return result;
}

/**
 * Calculates the pooled portfolio benchmark IRR.
 * @CustomFunction PrivateiCalcPooledBenchmarkIRR.ByWeight
 * @param [Universe] Peer group ID or Filter appliedto Burgiss Manager Universe.
 * @param [Weight] Weights for each group.
 * @param [StartDate] Start Date that drives the measurement date for calculations. Date must be a valid BMU Start Date. Default date is inception if omitted.
 * @param [EndDate] End date that drives the measurement date for calculations. Date must be a valid Burgiss Manager Universe (BMU) analysis date. Default date is the latest BMU analysis date if omitted.
 * @param [WeightingMethod] Weighting Methodology applied to funds selected from the Burgiss Manager Universe. Default is Capitalization if omitted. See Burgiss help for more details
 * @param [Currency] Denotes the Currency of the amounts in which calculations are performed. Default is base if omitted.
 * @param [RequirReportedValuation] Denotes whether or not to only include BMU funds that have a reported valuation as of the Start Date and/or End Date. Default is false if omitted.
 * @param {CustomFunctions.Invocation} invocation - The invocation context.
 * @helpUrl https://burgiss.docsend.com/view/4c3zt8i
 * @requiresAddress
 */
// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
function BurgissCalcPooledBenchmarkIRRByWeight(
  Universe?: string,
  Weight?: string,
  StartDate?: any,
  EndDate?: any,
  WeightingMethod?: string,
  Currency?: string,
  RequirReportedValuation?: any,
  invocation?: any
) {
  const result = CheckProfile();
  if (result == true) {
    const uniqueCellId = generateUniqueCellId(invocation.address);
    return new PooledBenchmarkUnivFunctions().fnBurgissCalcPooledBenchmarkIRRByWeight(
      Universe,
      Weight,
      StartDate,
      EndDate,
      WeightingMethod,
      Currency,
      RequirReportedValuation,
      uniqueCellId
    );
  }
  return result;
}

/**
 * Calculates the pooled portfolio benchmark IRR.
 * @CustomFunction PrivateiCalcPooledBenchmarkIRR.ByCommitment
 * @param [Universe] Peer group ID or Filter appliedto Burgiss Manager Universe.
 * @param [Commitment] Commitment for each group.
 * @param [StartDate] Start Date that drives the measurement date for calculations. Date must be a valid BMU Start Date. Default date is inception if omitted.
 * @param [EndDate] End date that drives the measurement date for calculations. Date must be a valid Burgiss Manager Universe (BMU) analysis date. Default date is the latest BMU analysis date if omitted.
 * @param [WeightingMethod] Weighting Methodology applied to funds selected from the Burgiss Manager Universe. Default is Capitalization if omitted. See Burgiss help for more details
 * @param [Currency] Denotes the Currency of the amounts in which calculations are performed. Default is base if omitted.
 * @param [RequirReportedValuation] Denotes whether or not to only include BMU funds that have a reported valuation as of the Start Date and/or End Date. Default is false if omitted.
 * @param {CustomFunctions.Invocation} invocation - The invocation context.
 * @helpUrl https://burgiss.docsend.com/view/4c3zt8i
 * @requiresAddress
 */
// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
function BurgissCalcPooledBenchmarkIRRByCommitment(
  Universe?: string,
  Commitment?: string,
  StartDate?: any,
  EndDate?: any,
  WeightingMethod?: string,
  Currency?: string,
  RequirReportedValuation?: any,
  invocation?: any
) {
  const result = CheckProfile();
  if (result == true) {
    const uniqueCellId = generateUniqueCellId(invocation.address);
    return new PooledBenchmarkUnivFunctions().fnBurgissCalcPooledBenchmarkIRRByCommitment(
      Universe,
      Commitment,
      StartDate,
      EndDate,
      WeightingMethod,
      Currency,
      RequirReportedValuation,
      uniqueCellId
    );
  }
  return result;
}

/**
 * Calculates the pooled portfolio benchmark TWRR.
 * @CustomFunction PrivateiCalcPooledBenchmarkTWRR.ByCommitment
 * @param [Universe] Peer group ID or Filter appliedto Burgiss Manager Universe.
 * @param [Commitment] Commitment for each group.
 * @param [StartDate] Start Date that drives the measurement date for calculations. Date must be a valid BMU Start Date. Default date is inception if omitted.
 * @param [EndDate] End date that drives the measurement date for calculations. Date must be a valid Burgiss Manager Universe (BMU) analysis date. Default date is the latest BMU analysis date if omitted.
 * @param [WeightingMethod] Weighting Methodology applied to funds selected from the Burgiss Manager Universe. Default is Capitalization if omitted. See Burgiss help for more details
 * @param [Currency] Denotes the Currency of the amounts in which calculations are performed. Default is base if omitted.
 * @param [RequirReportedValuation] Denotes whether or not to only include BMU funds that have a reported valuation as of the Start Date and/or End Date. Default is false if omitted.
 * @param [AdvancedParameter] To enabled supported attributes for the formula.
 * @param {CustomFunctions.Invocation} invocation - The invocation context.
 * @helpUrl https://burgiss.docsend.com/view/4c3zt8i
 * @requiresAddress
 */
// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
function BurgissCalcPooledBenchmarkTWRRByCommitment(
  Universe?: string,
  Commitment?: string,
  StartDate?: any,
  EndDate?: any,
  WeightingMethod?: string,
  Currency?: string,
  RequirReportedValuation?: any,
  AdvancedParameter?: string,
  invocation?: any
) {
  const result = CheckProfile();
  if (result == true) {
    const uniqueCellId = generateUniqueCellId(invocation.address);
    return new PooledBenchmarkUnivFunctions().fnBurgissCalcPooledBenchmarkTWRRByCommitment(
      Universe,
      Commitment,
      StartDate,
      EndDate,
      WeightingMethod,
      Currency,
      RequirReportedValuation,
      uniqueCellId,
      AdvancedParameter
    );
  }
  return result;
}
//#endregion PooledbenchmarkUniv
//#region  min and max date
/**
 * Returns the date of the latest funding record for an investment or group of investments..
 * @CustomFunction PrivateiCalcFunding.MaxDate
 * @param ID[][] ID(s) corresponding to the record type defined by the ID Type parameter. Use “All” to include all records. Default ID Type is Investment if ID Type is omitted.
 * @param [IdType] Describes the record type corresponding to values passed to the ID parameter. Default is Investment if omitted. See Burgiss Help for more details
 * @param [BeginDate] Begin date that drives the measurement period for calculations. Default is inception if omitted.
 * @param [EndDate] End date that drives the measurement date for calculations. Default date is today if omitted.
 * @param [SelectionSetId] Selection set filter applied to records denoted by the ID parameter. Default is no selection set if omitted
 * @param[Filter] Filter applied to records denoted by the ID parameter. Filter is applied after selection set is applied. Default is no filter if omitted. See Burgiss Help for more details.
 * @helpUrl https://burgiss.docsend.com/view/4c3zt8i
 */
// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
function BurgissCalcFundingMaxDate(
  ID: any[][],
  IdType?: string,
  BeginDate?: any,
  EndDate?: any,
  SelectionSetId?: any,
  Filter?: any
) {
  const result = CheckProfile();
  if (result == true) {
    return new MinAndMaxDateFunctions().fnBurgissCalcFundingMaxDate(
      ID,
      IdType,
      BeginDate,
      EndDate,
      SelectionSetId,
      Filter
    );
  }
  return result;
}

/**
 * Returns the date of the earliest funding record for an investment or group of investments..
 * @CustomFunction PrivateiCalcFunding.MinDate
 * @param ID[][] ID(s) corresponding to the record type defined by the ID Type parameter. Use “All” to include all records. Default ID Type is Investment if ID Type is omitted.
 * @param [IdType] Describes the record type corresponding to values passed to the ID parameter. Default is Investment if omitted. See Burgiss Help for more details
 * @param [BeginDate] Begin date that drives the measurement period for calculations. Default is inception if omitted.
 * @param [EndDate] End date that drives the measurement date for calculations. Default date is today if omitted.
 * @param [SelectionSetId] Selection set filter applied to records denoted by the ID parameter. Default is no selection set if omitted
 * @param[Filter] Filter applied to records denoted by the ID parameter. Filter is applied after selection set is applied. Default is no filter if omitted. See Burgiss Help for more details.
 * @helpUrl https://burgiss.docsend.com/view/4c3zt8i
 */
// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
function BurgissCalcFundingMinDate(
  ID: any[][],
  IdType?: string,
  BeginDate?: any,
  EndDate?: any,
  SelectionSetId?: any,
  Filter?: any
) {
  const result = CheckProfile();
  if (result == true) {
    return new MinAndMaxDateFunctions().fnBurgissCalcFundingMinDate(
      ID,
      IdType,
      BeginDate,
      EndDate,
      SelectionSetId,
      Filter
    );
  }
  return result;
}

/**
 * Returns the date of the latest cash record for an investment or group of investments..
 * @CustomFunction PrivateiCalcCash.MaxDate
 * @param ID[][] ID(s) corresponding to the record type defined by the ID Type parameter. Use “All” to include all records. Default ID Type is Investment if ID Type is omitted.
 * @param [IdType] Describes the record type corresponding to values passed to the ID parameter. Default is Investment if omitted. See Burgiss Help for more details
 * @param [BeginDate] Begin date that drives the measurement period for calculations. Default is inception if omitted.
 * @param [EndDate] End date that drives the measurement date for calculations. Default date is today if omitted.
 * @param [SelectionSetId] Selection set filter applied to records denoted by the ID parameter. Default is no selection set if omitted
 * @param[Filter] Filter applied to records denoted by the ID parameter. Filter is applied after selection set is applied. Default is no filter if omitted. See Burgiss Help for more details.
 * @helpUrl https://burgiss.docsend.com/view/4c3zt8i
 */
// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
function BurgissCalcCashMaxDate(
  ID: any[][],
  IdType?: string,
  BeginDate?: any,
  EndDate?: any,
  SelectionSetId?: any,
  Filter?: any
) {
  const result = CheckProfile();
  if (result == true) {
    return new MinAndMaxDateFunctions().fnBurgissCalcCashMaxDate(
      ID,
      IdType,
      BeginDate,
      EndDate,
      SelectionSetId,
      Filter
    );
  }
  return result;
}

/**
 * Returns the date of the earliest cash record for an investment or group of investments.
 * @CustomFunction PrivateiCalcCash.MinDate
 * @param ID[][] ID(s) corresponding to the record type defined by the ID Type parameter. Use “All” to include all records. Default ID Type is Investment if ID Type is omitted.
 * @param [IdType] Describes the record type corresponding to values passed to the ID parameter. Default is Investment if omitted. See Burgiss Help for more details
 * @param [BeginDate] Begin date that drives the measurement period for calculations. Default is inception if omitted.
 * @param [EndDate] End date that drives the measurement date for calculations. Default date is today if omitted.
 * @param [SelectionSetId] Selection set filter applied to records denoted by the ID parameter. Default is no selection set if omitted
 * @param[Filter] Filter applied to records denoted by the ID parameter. Filter is applied after selection set is applied. Default is no filter if omitted. See Burgiss Help for more details.
 * @helpUrl https://burgiss.docsend.com/view/4c3zt8i
 */
// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
function BurgissCalcCashMinDate(
  ID: any[][],
  IdType?: string,
  BeginDate?: any,
  EndDate?: any,
  SelectionSetId?: any,
  Filter?: any
) {
  const result = CheckProfile();
  if (result == true) {
    return new MinAndMaxDateFunctions().fnBurgissCalcCashMinDate(
      ID,
      IdType,
      BeginDate,
      EndDate,
      SelectionSetId,
      Filter
    );
  }
  return result;
}

/**
 * Returns the date of the latest fee record for an investment or group of investments..
 * @CustomFunction PrivateiCalcFees.MaxDate
 * @param ID[][] ID(s) corresponding to the record type defined by the ID Type parameter. Use “All” to include all records. Default ID Type is Investment if ID Type is omitted.
 * @param [IdType] Describes the record type corresponding to values passed to the ID parameter. Default is Investment if omitted. See Burgiss Help for more details
 * @param [BeginDate] Begin date that drives the measurement period for calculations. Default is inception if omitted.
 * @param [EndDate] End date that drives the measurement date for calculations. Default date is today if omitted.
 * @param [SelectionSetId] Selection set filter applied to records denoted by the ID parameter. Default is no selection set if omitted
 * @param [Filter] Filter applied to records denoted by the ID parameter. Filter is applied after selection set is applied. Default is no filter if omitted. See Burgiss Help for more details.
 * @helpUrl https://burgiss.docsend.com/view/4c3zt8i
 */
// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
function BurgissCalcFeesMaxDate(
  ID: any[][],
  IdType?: string,
  BeginDate?: any,
  EndDate?: any,
  SelectionSetId?: any,
  Filter?: any
) {
  const result = CheckProfile();
  if (result == true) {
    return new MinAndMaxDateFunctions().fnBurgissCalcFeesMaxDate(
      ID,
      IdType,
      BeginDate,
      EndDate,
      SelectionSetId,
      Filter
    );
  }
  return result;
}
/**
 * Returns the date of the earliest fee record for an investment or group of investments.
 * @CustomFunction PrivateiCalcFees.MinDate
 * @param ID[][] ID(s) corresponding to the record type defined by the ID Type parameter. Use “All” to include all records. Default ID Type is Investment if ID Type is omitted.
 * @param [IdType] Describes the record type corresponding to values passed to the ID parameter. Default is Investment if omitted. See Burgiss Help for more details
 * @param [BeginDate] Begin date that drives the measurement period for calculations. Default is inception if omitted.
 * @param [EndDate] End date that drives the measurement date for calculations. Default date is today if omitted.
 * @param [SelectionSetId] Selection set filter applied to records denoted by the ID parameter. Default is no selection set if omitted
 * @param [Filter] Filter applied to records denoted by the ID parameter. Filter is applied after selection set is applied. Default is no filter if omitted. See Burgiss Help for more details.
 * @helpUrl https://burgiss.docsend.com/view/4c3zt8i
 */
// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
function BurgissCalcFeesMinDate(
  ID: any[][],
  IdType?: string,
  BeginDate?: any,
  EndDate?: any,
  SelectionSetId?: any,
  Filter?: any
) {
  const result = CheckProfile();
  if (result == true) {
    return new MinAndMaxDateFunctions().fnBurgissCalcFeesMinDate(
      ID,
      IdType,
      BeginDate,
      EndDate,
      SelectionSetId,
      Filter
    );
  }
  return result;
}
/**
 * Returns the date of the latest valuation record for an investment or group of investments..
 * @CustomFunction PrivateiCalcValuation.MaxDate
 * @param ID[][] ID(s) corresponding to the record type defined by the ID Type parameter. Use “All” to include all records. Default ID Type is Investment if ID Type is omitted.
 * @param [IdType] Describes the record type corresponding to values passed to the ID parameter. Default is Investment if omitted. See Burgiss Help for more details
 * @param [BeginDate] Begin date that drives the measurement period for calculations. Default is inception if omitted.
 * @param [EndDate] End date that drives the measurement date for calculations. Default date is today if omitted.
 * @param [SelectionSetId] Selection set filter applied to records denoted by the ID parameter. Default is no selection set if omitted
 * @param [Filter] Filter applied to records denoted by the ID parameter. Filter is applied after selection set is applied. Default is no filter if omitted. See Burgiss Help for more details.
 * @helpUrl https://burgiss.docsend.com/view/4c3zt8i
 */
// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
function BurgissCalcValuationMaxDate(
  ID: any[][],
  IdType?: string,
  BeginDate?: any,
  EndDate?: any,
  SelectionSetId?: any,
  Filter?: any
) {
  const result = CheckProfile();
  if (result == true) {
    return new MinAndMaxDateFunctions().fnBurgissCalcValuationMaxDate(
      ID,
      IdType,
      BeginDate,
      EndDate,
      SelectionSetId,
      Filter
    );
  }
  return result;
}
/**
 * Returns the date of the earliest valuation record for an investment or group of investments..
 * @CustomFunction PrivateiCalcValuation.MinDate
 * @param ID[][] ID(s) corresponding to the record type defined by the ID Type parameter. Use “All” to include all records. Default ID Type is Investment if ID Type is omitted.
 * @param [IdType]  the record type corresponding to values passed to the ID parameter. Default is Investment if omitted. See Burgiss Help for more details
 * @param [BeginDate] Begin date that drives the measurement period for calculations. Default is inception if omitted.
 * @param [EndDate] End date that drives the measurement date for calculations. Default date is today if omitted.
 * @param [SelectionSetId] Selection set filter applied to records denoted by the ID parameter. Default is no selection set if omitted
 * @param [Filter] Filter applied to records denoted by the ID parameter. Filter is applied after selection set is applied. Default is no filter if omitted. See Burgiss Help for more details.
 * @helpUrl https://burgiss.docsend.com/view/4c3zt8i
 */
// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
function BurgissCalcValuationMinDate(
  ID: any[][],
  IdType?: string,
  BeginDate?: any,
  EndDate?: any,
  SelectionSetId?: any,
  Filter?: any
) {
  const result = CheckProfile();
  if (result == true) {
    return new MinAndMaxDateFunctions().fnBurgissCalcValuationMinDate(
      ID,
      IdType,
      BeginDate,
      EndDate,
      SelectionSetId,
      Filter
    );
  }
  return result;
}
/**
 * Returns the date of the earliest stock record for an investment or group of investments..
 * @CustomFunction PrivateiCalcStock.MinDate
 * @param ID[][] ID(s) corresponding to the record type defined by the ID Type parameter. Use “All” to include all records. Default ID Type is Investment if ID Type is omitted.
 * @param [IdType] Describes the record type corresponding to values passed to the ID parameter. Default is Investment if omitted. See Burgiss Help for more details
 * @param [BeginDate] Begin date that drives the measurement period for calculations. Default is inception if omitted.
 * @param [EndDate] End date that drives the measurement date for calculations. Default date is today if omitted.
 * @param [SelectionSetId] Selection set filter applied to records denoted by the ID parameter. Default is no selection set if omitted
 * @param [Filter] Filter applied to records denoted by the ID parameter. Filter is applied after selection set is applied. Default is no filter if omitted. See Burgiss Help for more details.
 * @helpUrl https://burgiss.docsend.com/view/4c3zt8i
 */
// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
function BurgissCalcStockMinDate(
  ID: any[][],
  IdType?: string,
  BeginDate?: any,
  EndDate?: any,
  SelectionSetId?: any,
  Filter?: any
) {
  const result = CheckProfile();
  if (result == true) {
    return new MinAndMaxDateFunctions().fnBurgissCalcStockMinDate(
      ID,
      IdType,
      BeginDate,
      EndDate,
      SelectionSetId,
      Filter
    );
  }
  return result;
}
/**
 * Returns the date of the latest stock record for an investment or group of investments.
 * @CustomFunction PrivateiCalcStock.MaxDate
 * @param ID[][] ID(s) corresponding to the record type defined by the ID Type parameter. Use “All” to include all records. Default ID Type is Investment if ID Type is omitted.
 * @param [IdType] Describes the record type corresponding to values passed to the ID parameter. Default is Investment if omitted. See Burgiss Help for more details
 * @param [BeginDate] Begin date that drives the measurement period for calculations. Default is inception if omitted.
 * @param [EndDate] End date that drives the measurement date for calculations. Default date is today if omitted.
 * @param [SelectionSetId] Selection set filter applied to records denoted by the ID parameter. Default is no selection set if omitted
 * @param [Filter] Filter applied to records denoted by the ID parameter. Filter is applied after selection set is applied. Default is no filter if omitted. See Burgiss Help for more details.
 * @helpUrl https://burgiss.docsend.com/view/4c3zt8i
 */
// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
function BurgissCalcStockMaxDate(
  ID: any[][],
  IdType?: string,
  BeginDate?: any,
  EndDate?: any,
  SelectionSetId?: any,
  Filter?: any
) {
  const result = CheckProfile();
  if (result == true) {
    return new MinAndMaxDateFunctions().fnBurgissCalcStockMaxDate(
      ID,
      IdType,
      BeginDate,
      EndDate,
      SelectionSetId,
      Filter
    );
  }
  return result;
}

//#endregion min and max date
//#region  Profiler
/**
 * Calculates the requested percentile of the measure for the specified peer group.
 * @CustomFunction PrivateiUniverseCalcIRR.Profiler
 * @param ID[][] Peer Group ID. Use 'Filter' to filter BMU using 'Filter' parameter.
 * @param [Percentile] The percentile value. Available values: 0.05, 0.25, 0.50, 0.75, 0.95.
 * @param [Year] The anniversary year. Available values include: 1-15.
 * @param [Currency] Individual results Currency. Default is user preference for Individual Results Currency if omitted.
 * @param [ReturnConstituentCount] If true, returns constituent count for the analysis. Default is false if omitted.
 * @param [Filter] Filter applied to BMU.
 * @param {CustomFunctions.Invocation} invocation - The invocation context.
 * @helpUrl https://burgiss.docsend.com/view/4c3zt8i
 * @requiresAddress
 */
// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
function BurgissUniverseCalcIRRProfiler(
  ID?: any[][],
  Percentile?: any,
  Year?: any,
  Currency?: string,
  ReturnConstituentCount?: any,
  Filter?: any,
  invocation?: any
) {
  const result = CheckProfile();
  if (result == true) {
    const uniqueCellId = generateUniqueCellId(invocation.address);
    return new ProfilerFunctions().fnBurgissUniverseCalcIRRProfiler(
      ID,
      Percentile,
      Year,
      Currency,
      ReturnConstituentCount,
      Filter,
      uniqueCellId
    );
  }
  return result;
}

/**
 * Calculates the requested percentile of the measure for the specified peer group.
 * @CustomFunction PrivateiUniverseCalcMIRR.Profiler
 * @param ID[][] Peer Group ID. Use 'Filter' to filter BMU using 'Filter' parameter.
 * @param [Percentile] The percentile value. Available values: 0.05, 0.25, 0.50, 0.75, 0.95.
 * @param [Year] The anniversary year. Available values include: 1-15.
 * @param [Currency] Individual results Currency. Default is user preference for Individual Results Currency if omitted.
 * @param [IndexID] Index ID. Default is user preference for Index if omitted.
 * @param [ReturnConstituentCount] If true, returns constituent count for the analysis. Default is false if omitted.
 * @param [Filter] Filter applied to BMU.
 * @param {CustomFunctions.Invocation} invocation - The invocation context.
 * @helpUrl https://burgiss.docsend.com/view/4c3zt8i
 * @requiresAddress
 */
// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
function BurgissUniverseCalcMIRRProfiler(
  ID?: any[][],
  Percentile?: any,
  Year?: any,
  Currency?: string,
  IndexID?: string,
  ReturnConstituentCount?: any,
  Filter?: any,
  invocation?: any
) {
  const result = CheckProfile();
  if (result == true) {
    const uniqueCellId = generateUniqueCellId(invocation.address);
    return new ProfilerFunctions().fnBurgissUniverseCalcMIRRProfiler(
      ID,
      Percentile,
      Year,
      Currency,
      IndexID,
      ReturnConstituentCount,
      Filter,
      uniqueCellId
    );
  }
  return result;
}
/**
 * Calculates the requested percentile of the measure for the specified peer group.
 * @CustomFunction PrivateiUniverseCalcDPI.Profiler
 * @param ID[][] Peer Group ID. Use 'Filter' to filter BMU using 'Filter' parameter.
 * @param [Percentile] The percentile value. Available values: 0.05, 0.25, 0.50, 0.75, 0.95.
 * @param [Year] The anniversary year. Available values include: 1-15.
 * @param [Currency] Individual results Currency. Default is user preference for Individual Results Currency if omitted.
 * @param [ReturnConstituentCount] If true, returns constituent count for the analysis. Default is false if omitted.
 * @param [Filter] Filter applied to BMU.
 * @param {CustomFunctions.Invocation} invocation - The invocation context.
 * @helpUrl https://burgiss.docsend.com/view/4c3zt8i
 * @requiresAddress
 */
// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
function BurgissUniverseCalcDPIProfiler(
  ID?: any[][],
  Percentile?: any,
  Year?: any,
  Currency?: string,
  ReturnConstituentCount?: any,
  Filter?: any,
  invocation?: any
) {
  const result = CheckProfile();
  if (result == true) {
    const uniqueCellId = generateUniqueCellId(invocation.address);
    return new ProfilerFunctions().fnBurgissUniverseCalcDPIProfiler(
      ID,
      Percentile,
      Year,
      Currency,
      ReturnConstituentCount,
      Filter,
      uniqueCellId
    );
  }
  return result;
}
/**
 * Calculates the requested percentile of the measure for the specified peer group.
 * @CustomFunction PrivateiUniverseCalcRVPI.Profiler
 * @param ID[][] Peer Group ID. Use 'Filter' to filter BMU using 'Filter' parameter.
 * @param [Percentile] The percentile value. Available values: 0.05, 0.25, 0.50, 0.75, 0.95.
 * @param [Year] The anniversary year. Available values include: 1-15.
 * @param [Currency] Individual results Currency. Default is user preference for Individual Results Currency if omitted.
 * @param [ReturnConstituentCount] If true, returns constituent count for the analysis. Default is false if omitted.
 * @param [Filter] Filter applied to BMU.
 * @param {CustomFunctions.Invocation} invocation - The invocation context.
 * @helpUrl https://burgiss.docsend.com/view/4c3zt8i
 * @requiresAddress
 */
// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
function BurgissUniverseCalcRVPIProfiler(
  ID?: any[][],
  Percentile?: any,
  Year?: any,
  Currency?: string,
  ReturnConstituentCount?: any,
  Filter?: any,
  invocation?: any
) {
  const result = CheckProfile();
  if (result == true) {
    const uniqueCellId = generateUniqueCellId(invocation.address);
    return new ProfilerFunctions().fnBurgissUniverseCalcRVPIProfiler(
      ID,
      Percentile,
      Year,
      Currency,
      ReturnConstituentCount,
      Filter,
      uniqueCellId
    );
  }
  return result;
}

/**
 * Calculates the requested percentile of the measure for the specified peer group.
 * @CustomFunction PrivateiUniverseCalcTVPI.Profiler
 * @param ID[][] Peer Group ID. Use 'Filter' to filter BMU using 'Filter' parameter.
 * @param [Percentile] The percentile value. Available values: 0.05, 0.25, 0.50, 0.75, 0.95.
 * @param [Year] The anniversary year. Available values include: 1-15.
 * @param [Currency] Individual results Currency. Default is user preference for Individual Results Currency if omitted.
 * @param [ReturnConstituentCount] If true, returns constituent count for the analysis. Default is false if omitted.
 * @param [Filter] Filter applied to BMU.
 * @param {CustomFunctions.Invocation} invocation - The invocation context.
 * @helpUrl https://burgiss.docsend.com/view/4c3zt8i
 * @requiresAddress
 */
// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
function BurgissUniverseCalcTVPIProfiler(
  ID?: any[][],
  Percentile?: any,
  Year?: any,
  Currency?: string,
  ReturnConstituentCount?: any,
  Filter?: any,
  invocation?: any
) {
  const result = CheckProfile();
  if (result == true) {
    const uniqueCellId = generateUniqueCellId(invocation.address);
    return new ProfilerFunctions().fnBurgissUniverseCalcTVPIProfiler(
      ID,
      Percentile,
      Year,
      Currency,
      ReturnConstituentCount,
      Filter,
      uniqueCellId
    );
  }
  return result;
}

/**
 * Calculates the requested percentile of the measure for the specified peer group.
 * @CustomFunction PrivateiUniverseCalcDistributions.Profiler
 * @param ID[][] Peer Group ID. Use 'Filter' to filter BMU using 'Filter' parameter.
 * @param [Percentile] The percentile value. Available values: 0.05, 0.25, 0.50, 0.75, 0.95.
 * @param [Year] The anniversary year. Available values include: 1-15.
 * @param [Currency] Individual results Currency. Default is user preference for Individual Results Currency if omitted.
 * @param [ReturnConstituentCount] If true, returns constituent count for the analysis. Default is false if omitted.
 * @param [Filter] Filter applied to BMU.
 * @param {CustomFunctions.Invocation} invocation - The invocation context.
 * @requiresAddress
 */
// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
function BurgissUniverseCalcDistributionsProfiler(
  ID?: any[][],
  Percentile?: any,
  Year?: any,
  Currency?: string,
  ReturnConstituentCount?: any,
  Filter?: any,
  invocation?: any
) {
  const result = CheckProfile();
  if (result == true) {
    const uniqueCellId = generateUniqueCellId(invocation.address);
    return new ProfilerFunctions().fnBurgissUniverseCalcDistributionsProfiler(
      ID,
      Percentile,
      Year,
      Currency,
      ReturnConstituentCount,
      Filter,
      uniqueCellId
    );
  }
  return result;
}

/**
 * Calculates the requested percentile of the measure for the specified peer group.
 * @CustomFunction PrivateiUniverseCalcDistributions_Annual.Profiler
 * @param ID[][] Peer Group ID. Use 'Filter' to filter BMU using 'Filter' parameter.
 * @param [Percentile] The percentile value. Available values: 0.05, 0.25, 0.50, 0.75, 0.95.
 * @param [Year] The anniversary year. Available values include: 1-15.
 * @param [Currency] Individual results Currency. Default is user preference for Individual Results Currency if omitted.
 * @param [ReturnConstituentCount] If true, returns constituent count for the analysis. Default is false if omitted.
 * @param [Filter] Filter applied to BMU.
 * @param {CustomFunctions.Invocation} invocation - The invocation context.
 * @helpUrl https://burgiss.docsend.com/view/4c3zt8i
 * @requiresAddress
 */
// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
function BurgissUniverseCalcDistributions_AnnualProfiler(
  ID?: any[][],
  Percentile?: any,
  Year?: any,
  Currency?: string,
  ReturnConstituentCount?: any,
  Filter?: any,
  invocation?: any
) {
  const result = CheckProfile();
  if (result == true) {
    const uniqueCellId = generateUniqueCellId(invocation.address);
    return new ProfilerFunctions().fnBurgissUniverseCalcDistributions_AnnualProfiler(
      ID,
      Percentile,
      Year,
      Currency,
      ReturnConstituentCount,
      Filter,
      uniqueCellId
    );
  }
  return result;
}

/**
 * Calculates the requested percentile of the measure for the specified peer group.
 * @CustomFunction PrivateiUniverseCalcNetCashFlow.Profiler
 * @param ID[][] Peer Group ID. Use 'Filter' to filter BMU using 'Filter' parameter.
 * @param [Percentile] The percentile value. Available values: 0.05, 0.25, 0.50, 0.75, 0.95.
 * @param [Year] The anniversary year. Available values include: 1-15.
 * @param [Currency] Individual results Currency. Default is user preference for Individual Results Currency if omitted.
 * @param [ReturnConstituentCount] If true, returns constituent count for the analysis. Default is false if omitted.
 * @param [Filter] Filter applied to BMU.
 * @param {CustomFunctions.Invocation} invocation - The invocation context.
 * @helpUrl https://burgiss.docsend.com/view/4c3zt8i
 * @requiresAddress
 */
// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
function BurgissUniverseCalcNetCashFlowProfiler(
  ID?: any[][],
  Percentile?: any,
  Year?: any,
  Currency?: string,
  ReturnConstituentCount?: any,
  Filter?: any,
  invocation?: any
) {
  const result = CheckProfile();
  if (result == true) {
    const uniqueCellId = generateUniqueCellId(invocation.address);
    return new ProfilerFunctions().fnBurgissUniverseCalcNetCashFlowProfiler(
      ID,
      Percentile,
      Year,
      Currency,
      ReturnConstituentCount,
      Filter,
      uniqueCellId
    );
  }
  return result;
}

/**
 * Calculates the requested percentile of the measure for the specified peer group.
 * @CustomFunction PrivateiUniverseCalcNetCashFlow_Annual.Profiler
 * @param ID[][] Peer Group ID. Use 'Filter' to filter BMU using 'Filter' parameter.
 * @param [Percentile] The percentile value. Available values: 0.05, 0.25, 0.50, 0.75, 0.95.
 * @param [Year] The anniversary year. Available values include: 1-15.
 * @param [Currency] Individual results Currency. Default is user preference for Individual Results Currency if omitted.
 * @param [ReturnConstituentCount] If true, returns constituent count for the analysis. Default is false if omitted.
 * @param [Filter] Filter applied to BMU.
 * @param {CustomFunctions.Invocation} invocation - The invocation context.
 * @helpUrl https://burgiss.docsend.com/view/4c3zt8i
 * @requiresAddress
 */
// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
function BurgissUniverseCalcNetCashFlow_AnnualProfiler(
  ID?: any[][],
  Percentile?: any,
  Year?: any,
  Currency?: string,
  ReturnConstituentCount?: any,
  Filter?: any,
  invocation?: any
) {
  const result = CheckProfile();
  if (result == true) {
    const uniqueCellId = generateUniqueCellId(invocation.address);
    return new ProfilerFunctions().fnBurgissUniverseCalcNetCashFlow_AnnualProfiler(
      ID,
      Percentile,
      Year,
      Currency,
      ReturnConstituentCount,
      Filter,
      uniqueCellId
    );
  }
  return result;
}

/**
 * Calculates the requested percentile of the measure for the specified peer group.
 * @CustomFunction PrivateiUniverseCalcPaidInCapital.Profiler
 * @param ID[][] Peer Group ID. Use 'Filter' to filter BMU using 'Filter' parameter.
 * @param [Percentile] The percentile value. Available values: 0.05, 0.25, 0.50, 0.75, 0.95.
 * @param [Year] The anniversary year. Available values include: 1-15.
 * @param [Currency] Individual results Currency. Default is user preference for Individual Results Currency if omitted.
 * @param [ReturnConstituentCount] If true, returns constituent count for the analysis. Default is false if omitted.
 * @param [Filter] Filter applied to BMU.
 * @param {CustomFunctions.Invocation} invocation - The invocation context.
 * @helpUrl https://burgiss.docsend.com/view/4c3zt8i
 * @requiresAddress
 */
// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
function BurgissUniverseCalcPaidInCapitalProfiler(
  ID?: any[][],
  Percentile?: any,
  Year?: any,
  Currency?: string,
  ReturnConstituentCount?: any,
  Filter?: any,
  invocation?: any
) {
  const result = CheckProfile();
  if (result == true) {
    const uniqueCellId = generateUniqueCellId(invocation.address);
    return new ProfilerFunctions().fnBurgissUniverseCalcPaidInCapitalProfiler(
      ID,
      Percentile,
      Year,
      Currency,
      ReturnConstituentCount,
      Filter,
      uniqueCellId
    );
  }
  return result;
}

/**
 * Calculates the requested percentile of the measure for the specified peer group.
 * @CustomFunction PrivateiUniverseCalcPaidInCapital_Annual.Profiler
 * @param ID[][] Peer Group ID. Use 'Filter' to filter BMU using 'Filter' parameter.
 * @param [Percentile] The percentile value. Available values: 0.05, 0.25, 0.50, 0.75, 0.95.
 * @param [Year] The anniversary year. Available values include: 1-15.
 * @param [Currency] Individual results Currency. Default is user preference for Individual Results Currency if omitted.
 * @param [ReturnConstituentCount] If true, returns constituent count for the analysis. Default is false if omitted.
 * @param [Filter] Filter applied to BMU.
 * @param {CustomFunctions.Invocation} invocation - The invocation context.
 * @helpUrl https://burgiss.docsend.com/view/4c3zt8i
 * @requiresAddress
 */
// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
function BurgissUniverseCalcPaidInCapital_AnnualProfiler(
  ID?: any[][],
  Percentile?: any,
  Year?: any,
  Currency?: string,
  ReturnConstituentCount?: any,
  Filter?: any,
  invocation?: any
) {
  const result = CheckProfile();
  if (result == true) {
    const uniqueCellId = generateUniqueCellId(invocation.address);
    return new ProfilerFunctions().fnBurgissUniverseCalcPaidInCapital_AnnualProfiler(
      ID,
      Percentile,
      Year,
      Currency,
      ReturnConstituentCount,
      Filter,
      uniqueCellId
    );
  }
  return result;
}

/**
 * Calculates the requested percentile of the measure for the specified peer group.
 * @CustomFunction PrivateiUniverseCalcValuation.Profiler
 * @param ID[][] Peer Group ID. Use 'Filter' to filter BMU using 'Filter' parameter.
 * @param [Percentile] The percentile value. Available values: 0.05, 0.25, 0.50, 0.75, 0.95.
 * @param [Year] The anniversary year. Available values include: 1-15.
 * @param [Currency] Individual results Currency. Default is user preference for Individual Results Currency if omitted.
 * @param [ReturnConstituentCount] If true, returns constituent count for the analysis. Default is false if omitted.
 * @param [Filter] Filter applied to BMU.
 * @param {CustomFunctions.Invocation} invocation - The invocation context.
 * @helpUrl https://burgiss.docsend.com/view/4c3zt8i
 * @requiresAddress
 */
// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
function BurgissUniverseCalcValuationProfiler(
  ID?: any[][],
  Percentile?: any,
  Year?: any,
  Currency?: string,
  ReturnConstituentCount?: any,
  Filter?: any,
  invocation?: any
) {
  const result = CheckProfile();
  if (result == true) {
    const uniqueCellId = generateUniqueCellId(invocation.address);
    return new ProfilerFunctions().fnBurgissUniverseCalcValuationProfiler(
      ID,
      Percentile,
      Year,
      Currency,
      ReturnConstituentCount,
      Filter,
      uniqueCellId
    );
  }
  return result;
}

/**
 * Calculates the requested percentile of the measure for the specified peer group.
 * @CustomFunction PrivateiUniverseCalcGGS_DirectAlpha.Profiler
 * @param ID[][] Peer Group ID. Use 'Filter' to filter BMU using 'Filter' parameter.
 * @param [Percentile] The percentile value. Available values: 0.05, 0.25, 0.50, 0.75, 0.95.
 * @param [Year] The anniversary year. Available values include: 1-15.
 * @param [Currency] Individual results Currency. Default is user preference for Individual Results Currency if omitted.
 * @param [IndexID] Index ID. Default is user preference for Index if omitted.
 * @param [ReturnConstituentCount] If true, returns constituent count for the analysis. Default is false if omitted.
 * @param [Filter] Filter applied to BMU.
 * @param {CustomFunctions.Invocation} invocation - The invocation context.
 * @helpUrl https://burgiss.docsend.com/view/4c3zt8i
 * @requiresAddress
 */
// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
function BurgissUniverseCalcGGS_DirectAlphaProfiler(
  ID?: any[][],
  Percentile?: any,
  Year?: any,
  Currency?: string,
  IndexID?: string,
  ReturnConstituentCount?: any,
  Filter?: any,
  invocation?: any
) {
  const result = CheckProfile();
  if (result == true) {
    const uniqueCellId = generateUniqueCellId(invocation.address);
    return new ProfilerFunctions().fnBurgissUniverseCalcGGS_DirectAlphaProfiler(
      ID,
      Percentile,
      Year,
      Currency,
      IndexID,
      ReturnConstituentCount,
      Filter,
      uniqueCellId
    );
  }
  return result;
}

/**
 * Calculates the requested percentile of the measure for the specified peer group.
 * @CustomFunction PrivateiUniverseCalcKS_PME.Profiler
 * @param ID[][] Peer Group ID. Use 'Filter' to filter BMU using 'Filter' parameter.
 * @param [Percentile] The percentile value. Available values: 0.05, 0.25, 0.50, 0.75, 0.95.
 * @param [Year] The anniversary year. Available values include: 1-15.
 * @param [Currency] Individual results Currency. Default is user preference for Individual Results Currency if omitted.
 * @param [IndexID] Index ID. Default is user preference for Index if omitted.
 * @param [ReturnConstituentCount] If true, returns constituent count for the analysis. Default is false if omitted.
 * @param [Filter] Filter applied to BMU.
 * @param {CustomFunctions.Invocation} invocation - The invocation context.
 * @helpUrl https://burgiss.docsend.com/view/4c3zt8i
 * @requiresAddress
 */
// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
function BurgissUniverseCalcKS_PMEProfiler(
  ID?: any[][],
  Percentile?: any,
  Year?: any,
  Currency?: string,
  IndexID?: string,
  ReturnConstituentCount?: any,
  Filter?: any,
  invocation?: any
) {
  const result = CheckProfile();
  if (result == true) {
    const uniqueCellId = generateUniqueCellId(invocation.address);
    return new ProfilerFunctions().fnBurgissUniverseCalcKS_PMEProfiler(
      ID,
      Percentile,
      Year,
      Currency,
      IndexID,
      ReturnConstituentCount,
      Filter,
      uniqueCellId
    );
  }
  return result;
}

/**
 * Calculates the requested percentile of the measure for the specified peer group.
 * @CustomFunction PrivateiUniverseCalcLN_ICMIRR.Profiler
 * @param ID[][] Peer Group ID. Use 'Filter' to filter BMU using 'Filter' parameter.
 * @param [Percentile] The percentile value. Available values: 0.05, 0.25, 0.50, 0.75, 0.95.
 * @param [Year] The anniversary year. Available values include: 1-15.
 * @param [Currency] Individual results Currency. Default is user preference for Individual Results Currency if omitted.
 * @param [IndexID] Index ID. Default is user preference for Index if omitted.
 * @param [ReturnConstituentCount] If true, returns constituent count for the analysis. Default is false if omitted.
 * @param [Filter] Filter applied to BMU.
 * @param {CustomFunctions.Invocation} invocation - The invocation context.
 * @helpUrl https://burgiss.docsend.com/view/4c3zt8i
 * @requiresAddress
 */
// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
function BurgissUniverseCalcLN_ICMIRRProfiler(
  ID?: any[][],
  Percentile?: any,
  Year?: any,
  Currency?: string,
  IndexID?: string,
  ReturnConstituentCount?: any,
  Filter?: any,
  invocation?: any
) {
  const result = CheckProfile();
  if (result == true) {
    const uniqueCellId = generateUniqueCellId(invocation.address);
    return new ProfilerFunctions().fnBurgissUniverseCalcLN_ICMIRRProfiler(
      ID,
      Percentile,
      Year,
      Currency,
      IndexID,
      ReturnConstituentCount,
      Filter,
      uniqueCellId
    );
  }
  return result;
}

/**
 * Calculates the requested percentile of the measure for the specified peer group.
 * @CustomFunction PrivateiUniverseCalcLN_ICMIRRSpread.Profiler
 * @param ID[][] Peer Group ID. Use 'Filter' to filter BMU using 'Filter' parameter.
 * @param [Percentile] The percentile value. Available values: 0.05, 0.25, 0.50, 0.75, 0.95.
 * @param [Year] The anniversary year. Available values include: 1-15.
 * @param [Currency] Individual results Currency. Default is user preference for Individual Results Currency if omitted.
 * @param [IndexID] Index ID. Default is user preference for Index if omitted.
 * @param [ReturnConstituentCount] If true, returns constituent count for the analysis. Default is false if omitted.
 * @param [Filter] Filter applied to BMU.
 * @param {CustomFunctions.Invocation} invocation - The invocation context.
 * @helpUrl https://burgiss.docsend.com/view/4c3zt8i
 * @requiresAddress
 */
// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
function BurgissUniverseCalcLN_ICMIRRSpreadProfiler(
  ID?: any[][],
  Percentile?: any,
  Year?: any,
  Currency?: string,
  IndexID?: string,
  ReturnConstituentCount?: any,
  Filter?: any,
  invocation?: any
) {
  const result = CheckProfile();
  if (result == true) {
    const uniqueCellId = generateUniqueCellId(invocation.address);
    return new ProfilerFunctions().fnBurgissUniverseCalcLN_ICMIRRSpreadProfiler(
      ID,
      Percentile,
      Year,
      Currency,
      IndexID,
      ReturnConstituentCount,
      Filter,
      uniqueCellId
    );
  }
  return result;
}

/**
 * Calculates the requested percentile of the measure for the specified peer group.
 * @CustomFunction PrivateiUniverseCalcLN_ICMValuationSpread.Profiler
 * @param ID[][] Peer Group ID. Use 'Filter' to filter BMU using 'Filter' parameter.
 * @param [Percentile] The percentile value. Available values: 0.05, 0.25, 0.50, 0.75, 0.95.
 * @param [Year] The anniversary year. Available values include: 1-15.
 * @param [Currency] Individual results Currency. Default is user preference for Individual Results Currency if omitted.
 * @param [IndexID] Index ID. Default is user preference for Index if omitted.
 * @param [ReturnConstituentCount] If true, returns constituent count for the analysis. Default is false if omitted.
 * @param [Filter] Filter applied to BMU.
 * @param {CustomFunctions.Invocation} invocation - The invocation context.
 * @helpUrl https://burgiss.docsend.com/view/4c3zt8i
 * @requiresAddress
 */
// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
function BurgissUniverseCalcLN_ICMValuationSpreadProfiler(
  ID?: any[][],
  Percentile?: any,
  Year?: any,
  Currency?: string,
  IndexID?: string,
  ReturnConstituentCount?: any,
  Filter?: any,
  invocation?: any
) {
  const result = CheckProfile();
  if (result == true) {
    const uniqueCellId = generateUniqueCellId(invocation.address);
    return new ProfilerFunctions().fnBurgissUniverseCalcLN_ICMValuationSpreadProfiler(
      ID,
      Percentile,
      Year,
      Currency,
      IndexID,
      ReturnConstituentCount,
      Filter,
      uniqueCellId
    );
  }
  return result;
}

/**
 * Calculates the requested percentile of the measure for the specified peer group.
 * @CustomFunction PrivateiUniverseCalcRIRR.Profiler
 * @param ID[][] Peer Group ID. Use 'Filter' to filter BMU using 'Filter' parameter.
 * @param [Percentile] The percentile value. Available values: 0.05, 0.25, 0.50, 0.75, 0.95.
 * @param [Year] The anniversary year. Available values include: 1-15.
 * @param [Currency] Individual results Currency. Default is user preference for Individual Results Currency if omitted.
 * @param [ReturnConstituentCount] If true, returns constituent count for the analysis. Default is false if omitted.
 * @param [Filter] Filter applied to BMU.
 * @param {CustomFunctions.Invocation} invocation - The invocation context.
 * @helpUrl https://burgiss.docsend.com/view/4c3zt8i
 * @requiresAddress
 */
// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
function BurgissUniverseCalcRIRRProfiler(
  ID?: any[][],
  Percentile?: any,
  Year?: any,
  Currency?: string,
  ReturnConstituentCount?: any,
  Filter?: any,
  invocation?: any
) {
  const result = CheckProfile();
  if (result == true) {
    const uniqueCellId = generateUniqueCellId(invocation.address);
    return new ProfilerFunctions().fnBurgissUniverseCalcRIRRProfiler(
      ID,
      Percentile,
      Year,
      Currency,
      ReturnConstituentCount,
      Filter,
      uniqueCellId
    );
  }
  return result;
}

//#endregion Profiler
//#region UA Measures
/**
 * Calculates the requested percentile of the measure for the specified peer group.
 * @CustomFunction PrivateiUniverseCalcNetCashFlow.Percentile
 * @param [ID] vPeer Group ID. Use 'Filter' to filter BMU using 'Filter' parameter.
 * @param [Percentile] The percentile value. Available values: 0.05, 0.25, 0.50, 0.75, 0.95.
 * @param [Currency]  Individual results Currency. Default is user preference for Individual Results Currency if omitted.
 * @param [StartDate] Start date of the analysis. Default is inception if omitted.
 * @param [EndDate] End date of the analysis. Default is the most recent available quarter end date if omitted.
 * @param [ReturnConstituentCount]  If true, returns constituent count for the analysis. Default is false if omitted.
 * @param [Filter]  Filter applied to BMU.
 * @param {CustomFunctions.Invocation} invocation - The invocation context.
 * @helpUrl https://burgiss.docsend.com/view/4c3zt8i
 * @requiresAddress
 */

// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
function BurgissUniverseCalcNetCashFlowPercentile(
  ID: any[][],
  Percentile: any,
  Currency: any,
  StartDate?: any,
  EndDate?: any,
  ReturnConstituentCount?: any,
  Filter?: any,
  invocation?: any
) {
  const result = CheckProfile();
  if (result == true) {
    const uniqueCellId = generateUniqueCellId(invocation.address);
    return new UAMeasuresFunctions().fnBurgissUniverseCalcNetCashFlowPercentile(
      ID,
      Percentile,
      Currency,
      StartDate,
      EndDate,
      ReturnConstituentCount,
      Filter,
      uniqueCellId
    );
  }
  return result;
}
/**
 * Calculates the requested percentile of the measure for the specified peer group.
 * @CustomFunction PrivateiUniverseCalcIRR.Percentile
 * @param ID[][] Peer Group ID. Use 'Filter' to filter BMU using 'Filter' parameter.
 * @param [Percentile] The percentile value. Available values: 0.05, 0.25, 0.50, 0.75, 0.95.
 * @param [Currency]  Individual results Currency. Default is user preference for Individual Results Currency if omitted.
 * @param [StartDate] Start date of the analysis. Default is inception if omitted.
 * @param [EndDate] End date of the analysis. Default is the most recent available quarter end date if omitted.
 * @param [ReturnConstituentCount] If true, returns constituent count for the analysis. Default is false if omitted.
 * @param [Filter] Filter applied to BMU.
 * @param {CustomFunctions.Invocation} invocation - The invocation context.
 * @helpUrl https://burgiss.docsend.com/view/4c3zt8i
 * @requiresAddress
 */
// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
function BurgissUniverseCalcIRRPercentile(
  ID: any[][],
  Percentile: any,
  Currency: any,
  StartDate?: any,
  EndDate?: any,
  ReturnConstituentCount?: any,
  Filter?: any,
  invocation?: any
) {
  const result = CheckProfile();
  if (result == true) {
    const uniqueCellId = generateUniqueCellId(invocation.address);
    return new UAMeasuresFunctions().fnBurgissUniverseCalcIRRPercentile(
      ID,
      Percentile,
      Currency,
      StartDate,
      EndDate,
      ReturnConstituentCount,
      Filter,
      uniqueCellId
    );
  }
  return result;
}
/**
 * Calculates the requested percentile of the measure for the specified peer group.
 * @CustomFunction PrivateiUniverseCalcIRR_QTD.Percentile
 * @param ID[][] Peer Group ID. Use 'Filter' to filter BMU using 'Filter' parameter.
 * @param [Percentile] The percentile value. Available values: 0.05, 0.25, 0.50, 0.75, 0.95.
 * @param [Currency]  Individual results Currency. Default is user preference for Individual Results Currency if omitted.
 * @param [EndDate] End date of the analysis. Default is the most recent available quarter end date if omitted.
 * @param [ReturnConstituentCount] If true, returns constituent count for the analysis. Default is false if omitted.
 * @param [Filter] Filter applied to BMU.
 * @param {CustomFunctions.Invocation} invocation - The invocation context.
 * @helpUrl https://burgiss.docsend.com/view/4c3zt8i
 * @requiresAddress
 */
// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
function BurgissUniverseCalcIRR_QTDPercentile(
  ID: any[][],
  Percentile: any,
  Currency: any,
  EndDate?: any,
  ReturnConstituentCount?: any,
  Filter?: any,
  invocation?: any
) {
  const result = CheckProfile();
  if (result == true) {
    const uniqueCellId = generateUniqueCellId(invocation.address);
    return new UAMeasuresFunctions().fnBurgissUniverseCalcIRR_QTDPercentile(
      ID,
      Percentile,
      Currency,
      EndDate,
      ReturnConstituentCount,
      Filter,
      uniqueCellId
    );
  }
  return result;
}

/**
 * Calculates the requested percentile of the measure for the specified peer group.
 * @CustomFunction PrivateiUniverseCalcIRR_YTD.Percentile
 * @param ID[][] Peer Group ID. Use 'Filter' to filter BMU using 'Filter' parameter.
 * @param [Percentile] The percentile value. Available values: 0.05, 0.25, 0.50, 0.75, 0.95.
 * @param [Currency]  Individual results Currency. Default is user preference for Individual Results Currency if omitted.
 * @param [EndDate] End date of the analysis. Default is the most recent available quarter end date if omitted.
 * @param [ReturnConstituentCount] If true, returns constituent count for the analysis. Default is false if omitted.
 * @param [Filter] Filter applied to BMU.
 * @param {CustomFunctions.Invocation} invocation - The invocation context.
 * @helpUrl https://burgiss.docsend.com/view/4c3zt8i
 * @requiresAddress
 */
// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
function BurgissUniverseCalcIRR_YTDPercentile(
  ID: any[][],
  Percentile: any,
  Currency: any,
  EndDate?: any,
  ReturnConstituentCount?: any,
  Filter?: any,
  invocation?: any
) {
  const result = CheckProfile();
  if (result == true) {
    const uniqueCellId = generateUniqueCellId(invocation.address);
    return new UAMeasuresFunctions().fnBurgissUniverseCalcIRR_YTDPercentile(
      ID,
      Percentile,
      Currency,
      EndDate,
      ReturnConstituentCount,
      Filter,
      uniqueCellId
    );
  }
  return result;
}

/**
 * Calculates the requested percentile of the measure for the specified peer group.
 * @CustomFunction PrivateiUniverseCalcIRR_1Year.Percentile
 * @param ID[][] Peer Group ID. Use 'Filter' to filter BMU using 'Filter' parameter.
 * @param [Percentile] The percentile value. Available values: 0.05, 0.25, 0.50, 0.75, 0.95.
 * @param [Currency]  Individual results Currency. Default is user preference for Individual Results Currency if omitted.
 * @param [EndDate] End date of the analysis. Default is the most recent available quarter end date if omitted.
 * @param [ReturnConstituentCount] If true, returns constituent count for the analysis. Default is false if omitted.
 * @param [Filter] Filter applied to BMU.
 * @param {CustomFunctions.Invocation} invocation - The invocation context.
 * @helpUrl https://burgiss.docsend.com/view/4c3zt8i
 * @requiresAddress
 */
// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
function BurgissUniverseCalcIRR_1YearPercentile(
  ID: any[][],
  Percentile: any,
  Currency: any,
  EndDate?: any,
  ReturnConstituentCount?: any,
  Filter?: any,
  invocation?: any
) {
  const result = CheckProfile();
  if (result == true) {
    const uniqueCellId = generateUniqueCellId(invocation.address);
    return new UAMeasuresFunctions().fnBurgissUniverseCalcIRR_1YearPercentile(
      ID,
      Percentile,
      Currency,
      EndDate,
      ReturnConstituentCount,
      Filter,
      uniqueCellId
    );
  }
  return result;
}

/**
 * Calculates the requested percentile of the measure for the specified peer group.
 * @CustomFunction PrivateiUniverseCalcIRR_3Year.Percentile
 * @param ID[][] Peer Group ID. Use 'Filter' to filter BMU using 'Filter' parameter.
 * @param [Percentile] The percentile value. Available values: 0.05, 0.25, 0.50, 0.75, 0.95.
 * @param [Currency]  Individual results Currency. Default is user preference for Individual Results Currency if omitted.
 * @param [EndDate] End date of the analysis. Default is the most recent available quarter end date if omitted.
 * @param [ReturnConstituentCount] If true, returns constituent count for the analysis. Default is false if omitted.
 * @param [Filter] Filter applied to BMU.
 * @param {CustomFunctions.Invocation} invocation - The invocation context.
 * @helpUrl https://burgiss.docsend.com/view/4c3zt8i
 * @requiresAddress
 */
// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
function BurgissUniverseCalcIRR_3YearPercentile(
  ID: any[][],
  Percentile: any,
  Currency: any,
  EndDate?: any,
  ReturnConstituentCount?: any,
  Filter?: any,
  invocation?: any
) {
  const result = CheckProfile();
  if (result == true) {
    const uniqueCellId = generateUniqueCellId(invocation.address);
    return new UAMeasuresFunctions().fnBurgissUniverseCalcIRR_3YearPercentile(
      ID,
      Percentile,
      Currency,
      EndDate,
      ReturnConstituentCount,
      Filter,
      uniqueCellId
    );
  }
  return result;
}

/**
 * Calculates the requested percentile of the measure for the specified peer group.
 * @CustomFunction PrivateiUniverseCalcIRR_5Year.Percentile
 * @param ID[][] Peer Group ID. Use 'Filter' to filter BMU using 'Filter' parameter.
 * @param [Percentile] The percentile value. Available values: 0.05, 0.25, 0.50, 0.75, 0.95.
 * @param [Currency]  Individual results Currency. Default is user preference for Individual Results Currency if omitted.
 * @param [EndDate] End date of the analysis. Default is the most recent available quarter end date if omitted.
 * @param [ReturnConstituentCount] If true, returns constituent count for the analysis. Default is false if omitted.
 * @param [Filter] Filter applied to BMU.
 * @param {CustomFunctions.Invocation} invocation - The invocation context.
 * @helpUrl https://burgiss.docsend.com/view/4c3zt8i
 * @requiresAddress
 */
// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
function BurgissUniverseCalcIRR_5YearPercentile(
  ID: any[][],
  Percentile: any,
  Currency: any,
  EndDate?: any,
  ReturnConstituentCount?: any,
  Filter?: any,
  invocation?: any
) {
  const result = CheckProfile();
  if (result == true) {
    const uniqueCellId = generateUniqueCellId(invocation.address);
    return new UAMeasuresFunctions().fnBurgissUniverseCalcIRR_5YearPercentile(
      ID,
      Percentile,
      Currency,
      EndDate,
      ReturnConstituentCount,
      Filter,
      uniqueCellId
    );
  }
  return result;
}

/**
 * Calculates the requested percentile of the measure for the specified peer group.
 * @CustomFunction PrivateiUniverseCalcIRR_10Year.Percentile
 * @param ID[][] Peer Group ID. Use 'Filter' to filter BMU using 'Filter' parameter.
 * @param [Percentile] The percentile value. Available values: 0.05, 0.25, 0.50, 0.75, 0.95.
 * @param [Currency]  Individual results Currency. Default is user preference for Individual Results Currency if omitted.
 * @param [EndDate] End date of the analysis. Default is the most recent available quarter end date if omitted.
 * @param [ReturnConstituentCount] If true, returns constituent count for the analysis. Default is false if omitted.
 * @param [Filter] Filter applied to BMU.
 * @param {CustomFunctions.Invocation} invocation - The invocation context.
 * @helpUrl https://burgiss.docsend.com/view/4c3zt8i
 * @requiresAddress
 */
// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
function BurgissUniverseCalcIRR_10YearPercentile(
  ID: any[][],
  Percentile: any,
  Currency: any,
  EndDate?: any,
  ReturnConstituentCount?: any,
  Filter?: any,
  invocation?: any
) {
  const result = CheckProfile();
  if (result == true) {
    const uniqueCellId = generateUniqueCellId(invocation.address);
    return new UAMeasuresFunctions().fnBurgissUniverseCalcIRR_10YearPercentile(
      ID,
      Percentile,
      Currency,
      EndDate,
      ReturnConstituentCount,
      Filter,
      uniqueCellId
    );
  }
  return result;
}

/**
 * Calculates the requested percentile of the measure for the specified peer group.
 * @CustomFunction PrivateiUniverseCalcIRR_15Year.Percentile
 * @param ID[][] Peer Group ID. Use 'Filter' to filter BMU using 'Filter' parameter.
 * @param [Percentile] The percentile value. Available values: 0.05, 0.25, 0.50, 0.75, 0.95.
 * @param [Currency]  Individual results Currency. Default is user preference for Individual Results Currency if omitted.
 * @param [EndDate] End date of the analysis. Default is the most recent available quarter end date if omitted.
 * @param [ReturnConstituentCount] If true, returns constituent count for the analysis. Default is false if omitted.
 * @param [Filter] Filter applied to BMU.
 * @param {CustomFunctions.Invocation} invocation - The invocation context.
 * @helpUrl https://burgiss.docsend.com/view/4c3zt8i
 * @requiresAddress
 */
// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
function BurgissUniverseCalcIRR_15YearPercentile(
  ID: any[][],
  Percentile: any,
  Currency: any,
  EndDate?: any,
  ReturnConstituentCount?: any,
  Filter?: any,
  invocation?: any
) {
  const result = CheckProfile();
  if (result == true) {
    const uniqueCellId = generateUniqueCellId(invocation.address);
    return new UAMeasuresFunctions().fnBurgissUniverseCalcIRR_15YearPercentile(
      ID,
      Percentile,
      Currency,
      EndDate,
      ReturnConstituentCount,
      Filter,
      uniqueCellId
    );
  }
  return result;
}

/**
 * Calculates the requested percentile of the measure for the specified peer group.
 * @CustomFunction PrivateiUniverseCalcIRR_20Year.Percentile
 * @param ID[][] Peer Group ID. Use 'Filter' to filter BMU using 'Filter' parameter.
 * @param [Percentile] The percentile value. Available values: 0.05, 0.25, 0.50, 0.75, 0.95.
 * @param [Currency]  Individual results Currency. Default is user preference for Individual Results Currency if omitted.
 * @param [EndDate] End date of the analysis. Default is the most recent available quarter end date if omitted.
 * @param [ReturnConstituentCount] If true, returns constituent count for the analysis. Default is false if omitted.
 * @param [Filter] Filter applied to BMU.
 * @param {CustomFunctions.Invocation} invocation - The invocation context.
 * @helpUrl https://burgiss.docsend.com/view/4c3zt8i
 * @requiresAddress
 */
// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
function BurgissUniverseCalcIRR_20YearPercentile(
  ID: any[][],
  Percentile: any,
  Currency: any,
  EndDate?: any,
  ReturnConstituentCount?: any,
  Filter?: any,
  invocation?: any
) {
  const result = CheckProfile();
  if (result == true) {
    const uniqueCellId = generateUniqueCellId(invocation.address);
    return new UAMeasuresFunctions().fnBurgissUniverseCalcIRR_20YearPercentile(
      ID,
      Percentile,
      Currency,
      EndDate,
      ReturnConstituentCount,
      Filter,
      uniqueCellId
    );
  }
  return result;
}

/**
 * Calculates the requested percentile of the measure for the specified peer group.
 * @CustomFunction PrivateiUniverseCalcDPI.Percentile
 * @param ID[][] Peer Group ID. Use 'Filter' to filter BMU using 'Filter' parameter.
 * @param [Percentile] The percentile value. Available values: 0.05, 0.25, 0.50, 0.75, 0.95.
 * @param [Currency]  Individual results Currency. Default is user preference for Individual Results Currency if omitted.
 * @param [EndDate] End date of the analysis. Default is the most recent available quarter end date if omitted.
 * @param [ReturnConstituentCount] If true, returns constituent count for the analysis. Default is false if omitted.
 * @param [Filter] Filter applied to BMU.
 * @param {CustomFunctions.Invocation} invocation - The invocation context.
 * @helpUrl https://burgiss.docsend.com/view/4c3zt8i
 * @requiresAddress
 */
// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
function BurgissUniverseCalcDPIPercentile(
  ID: any[][],
  Percentile: any,
  Currency: any,
  EndDate?: any,
  ReturnConstituentCount?: any,
  Filter?: any,
  invocation?: any
) {
  const result = CheckProfile();
  if (result == true) {
    const uniqueCellId = generateUniqueCellId(invocation.address);
    return new UAMeasuresFunctions().fnBurgissUniverseCalcDPIPercentile(
      ID,
      Percentile,
      Currency,
      EndDate,
      ReturnConstituentCount,
      Filter,
      uniqueCellId
    );
  }
  return result;
}

/**
 * Calculates the requested percentile of the measure for the specified peer group.
 * @CustomFunction PrivateiUniverseCalcGGS_DirectAlpha.Percentile
 * @param ID[][] Peer Group ID. Use 'Filter' to filter BMU using 'Filter' parameter.
 * @param [PercenTile] The percentile value. Available values: 0.05, 0.25, 0.50, 0.75, 0.95."
 * @param [Currency] Individual results Currency. Default is user preference for Individual Results Currency if omitted.
 * @param [StartDate] Start date of the analysis. Default is inception if omitted.
 * @param [EndDate] End date of the analysis. Default is the most recent available quarter end date if omitted.
 * @param [IndexID] Index ID. Default is user preference for Index if omitted.
 * @param [ReturnConstituentCount] If true, returns constituent count for the analysis. Default is false if omitted.
 * @param [Filter] Filter applied to BMU.
 * @param {CustomFunctions.Invocation} invocation - The invocation context.
 * @helpUrl https://burgiss.docsend.com/view/4c3zt8i
 * @requiresAddress
 */
// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
function BurgissUniverseCalcGSS_DirectAlphaPercentile(
  ID?: any[][],
  PercenTile?: any,
  Currency?: string,
  StartDate?: any,
  EndDate?: any,
  IndexID?: string,
  ReturnConstituentCount?: any,
  Filter?: any,
  invocation?: any
) {
  const result = CheckProfile();
  if (result == true) {
    const uniqueCellId = generateUniqueCellId(invocation.address);
    return new UAMeasuresFunctions().fnBurgissUniverseCalcGSS_DirectAlphaPercentile(
      ID,
      PercenTile,
      Currency,
      StartDate,
      EndDate,
      ReturnConstituentCount,
      Filter,
      IndexID,
      uniqueCellId
    );
  }
  return result;
}

/**
 * Calculates the requested percentile of the measure for the specified peer group.
 * @CustomFunction PrivateiUniverseCalcKS_PME.Percentile
 * @param ID[][] Peer Group ID. Use 'Filter' to filter BMU using 'Filter' parameter.
 * @param [PercenTile] The percentile value. Available values: 0.05, 0.25, 0.50, 0.75, 0.95."
 * @param [Currency] Individual results Currency. Default is user preference for Individual Results Currency if omitted.
 * @param [StartDate] Start date of the analysis. Default is inception if omitted.
 * @param [EndDate] End date of the analysis. Default is the most recent available quarter end date if omitted.
 * @param [IndexID] Index ID. Default is user preference for Index if omitted.
 * @param [ReturnConstituentCount] If true, returns constituent count for the analysis. Default is false if omitted.
 * @param [Filter] Filter applied to BMU.
 * @param {CustomFunctions.Invocation} invocation - The invocation context.
 * @helpUrl https://burgiss.docsend.com/view/4c3zt8i
 * @requiresAddress
 */
// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
function BurgissUniverseCalcKS_PMEPercentile(
  ID?: any[][],
  PercenTile?: any,
  Currency?: string,
  StartDate?: any,
  EndDate?: any,
  IndexID?: string,
  ReturnConstituentCount?: any,
  Filter?: any,
  invocation?: any
) {
  const result = CheckProfile();
  if (result == true) {
    const uniqueCellId = generateUniqueCellId(invocation.address);
    return new UAMeasuresFunctions().fnBurgissUniverseCalcKS_PMEPercentile(
      ID,
      PercenTile,
      Currency,
      StartDate,
      EndDate,
      ReturnConstituentCount,
      Filter,
      IndexID,
      uniqueCellId
    );
  }
  return result;
}
/**
 * Calculates the requested percentile of the measure for the specified peer group.
 * @CustomFunction PrivateiUniverseCalcLN_ICMIRR.Percentile
 * @param ID[][] Peer Group ID. Use 'Filter' to filter BMU using 'Filter' parameter.
 * @param [PercenTile] The percentile value. Available values: 0.05, 0.25, 0.50, 0.75, 0.95."
 * @param [Currency] Individual results Currency. Default is user preference for Individual Results Currency if omitted.
 * @param [StartDate] Start date of the analysis. Default is inception if omitted.
 * @param [EndDate] End date of the analysis. Default is the most recent available quarter end date if omitted.
 * @param [IndexID] Index ID. Default is user preference for Index if omitted.
 * @param [ReturnConstituentCount] If true, returns constituent count for the analysis. Default is false if omitted.
 * @param [Filter] Filter applied to BMU.
 * @param {CustomFunctions.Invocation} invocation - The invocation context.
 * @helpUrl https://burgiss.docsend.com/view/4c3zt8i
 * @requiresAddress
 */
// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
function BurgissUniverseCalcLN_ICMIRRPercentile(
  ID?: any[][],
  PercenTile?: any,
  Currency?: string,
  StartDate?: any,
  EndDate?: any,
  IndexID?: string,
  ReturnConstituentCount?: any,
  Filter?: any,
  invocation?: any
) {
  const result = CheckProfile();
  if (result == true) {
    const uniqueCellId = generateUniqueCellId(invocation.address);
    return new UAMeasuresFunctions().fnBurgissUniverseCalcLN_ICMIRRPercentile(
      ID,
      PercenTile,
      Currency,
      StartDate,
      EndDate,
      ReturnConstituentCount,
      Filter,
      IndexID,
      uniqueCellId
    );
  }
  return result;
}
/**
 * Calculates the requested percentile of the measure for the specified peer group.
 * @CustomFunction PrivateiUniverseCalcLN_ICMIRRSpread.Percentile
 * @param ID[][] Peer Group ID. Use 'Filter' to filter BMU using 'Filter' parameter.
 * @param [PercenTile] The percentile value. Available values: 0.05, 0.25, 0.50, 0.75, 0.95."
 * @param [Currency] Individual results Currency. Default is user preference for Individual Results Currency if omitted.
 * @param [StartDate] Start date of the analysis. Default is inception if omitted.
 * @param [EndDate] End date of the analysis. Default is the most recent available quarter end date if omitted.
 * @param [IndexID] Index ID. Default is user preference for Index if omitted.
 * @param [ReturnConstituentCount] If true, returns constituent count for the analysis. Default is false if omitted.
 * @param [Filter] Filter applied to BMU.
 * @param {CustomFunctions.Invocation} invocation - The invocation context.
 * @helpUrl https://burgiss.docsend.com/view/4c3zt8i
 * @requiresAddress
 */
// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
function BurgissUniverseCalcLN_ICMIRRSpreadPercentile(
  ID?: any[][],
  PercenTile?: any,
  Currency?: string,
  StartDate?: any,
  EndDate?: any,
  IndexID?: string,
  ReturnConstituentCount?: any,
  Filter?: any,
  invocation?: any
) {
  const result = CheckProfile();
  if (result == true) {
    const uniqueCellId = generateUniqueCellId(invocation.address);
    return new UAMeasuresFunctions().fnBurgissUniverseCalcLN_ICMIRRSpreadPercentile(
      ID,
      PercenTile,
      Currency,
      StartDate,
      EndDate,
      ReturnConstituentCount,
      Filter,
      IndexID,
      uniqueCellId
    );
  }
  return result;
}
/**
 * Calculates the requested percentile of the measure for the specified peer group.
 * @CustomFunction PrivateiUniverseCalcLN_ICMValuationSpread.Percentile
 * @param ID[][] Peer Group ID. Use 'Filter' to filter BMU using 'Filter' parameter.
 * @param [PercenTile] The percentile value. Available values: 0.05, 0.25, 0.50, 0.75, 0.95."
 * @param [Currency] Individual results Currency. Default is user preference for Individual Results Currency if omitted.
 * @param [StartDate] Start date of the analysis. Default is inception if omitted.
 * @param [EndDate] End date of the analysis. Default is the most recent available quarter end date if omitted.
 * @param [IndexID] Index ID. Default is user preference for Index if omitted.
 * @param [ReturnConstituentCount] If true, returns constituent count for the analysis. Default is false if omitted.
 * @param [Filter] Filter applied to BMU.
 * @param {CustomFunctions.Invocation} invocation - The invocation context.
 * @helpUrl https://burgiss.docsend.com/view/4c3zt8i
 * @requiresAddress
 */
// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
function BurgissUniverseCalcLN_ICMValuationSpreadPercentile(
  ID?: any[][],
  PercenTile?: any,
  Currency?: string,
  StartDate?: any,
  EndDate?: any,
  IndexID?: string,
  ReturnConstituentCount?: any,
  Filter?: any,
  invocation?: any
) {
  const result = CheckProfile();
  if (result == true) {
    const uniqueCellId = generateUniqueCellId(invocation.address);
    return new UAMeasuresFunctions().fnBurgissUniverseCalcLN_ICMValuationSpreadPercentile(
      ID,
      PercenTile,
      Currency,
      StartDate,
      EndDate,
      ReturnConstituentCount,
      Filter,
      IndexID,
      uniqueCellId
    );
  }
  return result;
}

/**
 * Calculates the pooled value of the measure for the specified peer group.
 * @CustomFunction PrivateiUniverseCalcMIRR.Pooled
 * @param ID[][] Peer Group ID. Use 'Filter' to filter BMU using 'Filter' parameter.
 * @param [Currency] Individual results Currency. Default is user preference for Individual Results Currency if omitted.
 * @param [StartDate] Start date of the analysis. Default is inception if omitted.
 * @param [EndDate] End date of the analysis. Default is the most recent available quarter end date if omitted.
 * @param [IndexID] Index ID. Default is user preference for Index if omitted.
 * @param [ReturnConstituentCount] If true, returns constituent count for the analysis. Default is false if omitted.
 * @param [Filter] Filter applied to BMU.
 * @param {CustomFunctions.Invocation} invocation - The invocation context.
 * @helpUrl https://burgiss.docsend.com/view/4c3zt8i
 * @requiresAddress
 */
// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
function BurgissUniverseCalcMIRRPooled(
  ID?: any[][],
  Currency?: string,
  StartDate?: any,
  EndDate?: any,
  IndexID?: string,
  ReturnConstituentCount?: any,
  Filter?: any,
  invocation?: any
) {
  const result = CheckProfile();
  if (result == true) {
    const uniqueCellId = generateUniqueCellId(invocation.address);
    return new UAMeasuresFunctions().fnBurgissUniverseCalcMIRRPooled(
      ID,
      Currency,
      StartDate,
      EndDate,
      ReturnConstituentCount,
      Filter,
      IndexID,
      uniqueCellId
    );
  }
  return result;
}

/**
 * Calculates the pooled value of the measure for the specified peer group.
 * @CustomFunction PrivateiUniverseCalcGGS_DirectAlpha.Pooled
 * @param ID[][] Peer Group ID. Use 'Filter' to filter BMU using 'Filter' parameter.
 * @param [Currency] Individual results Currency. Default is user preference for Individual Results Currency if omitted.
 * @param [StartDate] Start date of the analysis. Default is inception if omitted.
 * @param [EndDate] End date of the analysis. Default is the most recent available quarter end date if omitted.
 * @param [IndexID] Index ID. Default is user preference for Index if omitted.
 * @param [ReturnConstituentCount] If true, returns constituent count for the analysis. Default is false if omitted.
 * @param [Filter] Filter applied to BMU.
 * @param {CustomFunctions.Invocation} invocation - The invocation context.
 * @helpUrl https://burgiss.docsend.com/view/4c3zt8i
 * @requiresAddress
 */
// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
function BurgissUniverseCalcGSS_DirectAlphaPooled(
  ID?: any[][],
  Currency?: string,
  StartDate?: any,
  EndDate?: any,
  IndexID?: string,
  ReturnConstituentCount?: any,
  Filter?: any,
  invocation?: any
) {
  const result = CheckProfile();
  if (result == true) {
    const uniqueCellId = generateUniqueCellId(invocation.address);
    return new UAMeasuresFunctions().fnBurgissUniverseCalcGSS_DirectAlphaPooled(
      ID,
      Currency,
      StartDate,
      EndDate,
      ReturnConstituentCount,
      IndexID,
      Filter,
      uniqueCellId
    );
  }
  return result;
}

/**
 * Calculates the pooled value of the measure for the specified peer group.
 * @CustomFunction PrivateiUniverseCalcKS_PME.Pooled
 * @param ID[][] Peer Group ID. Use 'Filter' to filter BMU using 'Filter' parameter.
 * @param [Currency] Individual results Currency. Default is user preference for Individual Results Currency if omitted.
 * @param [StartDate] Start date of the analysis. Default is inception if omitted.
 * @param [EndDate] End date of the analysis. Default is the most recent available quarter end date if omitted.
 * @param [IndexID] Index ID. Default is user preference for Index if omitted.
 * @param [ReturnConstituentCount] If true, returns constituent count for the analysis. Default is false if omitted.
 * @param [Filter] Filter applied to BMU.
 * @param {CustomFunctions.Invocation} invocation - The invocation context.
 * @helpUrl https://burgiss.docsend.com/view/4c3zt8i
 * @requiresAddress
 */
// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
function BurgissUniverseCalcKS_PMEPooled(
  ID?: any[][],
  Currency?: string,
  StartDate?: any,
  EndDate?: any,
  IndexID?: string,
  ReturnConstituentCount?: any,
  Filter?: any,
  invocation?: any
) {
  const result = CheckProfile();
  if (result == true) {
    const uniqueCellId = generateUniqueCellId(invocation.address);
    return new UAMeasuresFunctions().fnBurgissUniverseCalcKS_PMEPooled(
      ID,
      Currency,
      StartDate,
      EndDate,
      ReturnConstituentCount,
      Filter,
      IndexID,
      uniqueCellId
    );
  }
  return result;
}
/**
 * Calculates the pooled value of the measure for the specified peer group.
 * @CustomFunction PrivateiUniverseCalcLN_ICMIRR.Pooled
 * @param ID[][] Peer Group ID. Use 'Filter' to filter BMU using 'Filter' parameter.
 * @param [Currency] Individual results Currency. Default is user preference for Individual Results Currency if omitted.
 * @param [StartDate] Start date of the analysis. Default is inception if omitted.
 * @param [EndDate] End date of the analysis. Default is the most recent available quarter end date if omitted.
 * @param [IndexID] Index ID. Default is user preference for Index if omitted.
 * @param [ReturnConstituentCount] If true, returns constituent count for the analysis. Default is false if omitted.
 * @param [Filter] Filter applied to BMU.
 * @param {CustomFunctions.Invocation} invocation - The invocation context.
 * @helpUrl https://burgiss.docsend.com/view/4c3zt8i
 * @requiresAddress
 */
// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
function BurgissUniverseCalcLN_ICMIRRPooled(
  ID?: any[][],
  Currency?: string,
  StartDate?: any,
  EndDate?: any,
  IndexID?: string,
  ReturnConstituentCount?: any,
  Filter?: any,
  invocation?: any
) {
  const result = CheckProfile();
  if (result == true) {
    const uniqueCellId = generateUniqueCellId(invocation.address);
    return new UAMeasuresFunctions().fnBurgissUniverseCalcLN_ICMIRRPooled(
      ID,
      Currency,
      StartDate,
      EndDate,
      ReturnConstituentCount,
      Filter,
      IndexID,
      uniqueCellId
    );
  }
  return result;
}
/**
 * Calculates the pooled value of the measure for the specified peer group.
 * @CustomFunction PrivateiUniverseCalcLN_ICMIRRSpread.Pooled
 * @param ID[][] Peer Group ID. Use 'Filter' to filter BMU using 'Filter' parameter.
 * @param [Currency] Individual results Currency. Default is user preference for Individual Results Currency if omitted.
 * @param [StartDate] Start date of the analysis. Default is inception if omitted.
 * @param [EndDate] End date of the analysis. Default is the most recent available quarter end date if omitted.
 * @param [IndexID] Index ID. Default is user preference for Index if omitted.
 * @param [ReturnConstituentCount] If true, returns constituent count for the analysis. Default is false if omitted.
 * @param [Filter] Filter applied to BMU.
 * @param {CustomFunctions.Invocation} invocation - The invocation context.
 * @helpUrl https://burgiss.docsend.com/view/4c3zt8i
 * @requiresAddress
 */
// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
function BurgissUniverseCalcLN_ICMIRRSpreadPooled(
  ID?: any[][],
  Currency?: string,
  StartDate?: any,
  EndDate?: any,
  IndexID?: string,
  ReturnConstituentCount?: any,
  Filter?: any,
  invocation?: any
) {
  const result = CheckProfile();
  if (result == true) {
    const uniqueCellId = generateUniqueCellId(invocation.address);
    return new UAMeasuresFunctions().fnBurgissUniverseCalcLN_ICMIRRSpreadPooled(
      ID,
      Currency,
      StartDate,
      EndDate,
      ReturnConstituentCount,
      Filter,
      IndexID,
      uniqueCellId
    );
  }
  return result;
}
/**
 * Calculates the pooled value of the measure for the specified peer group.
 * @CustomFunction PrivateiUniverseCalcLN_ICMValuationSpread.Pooled
 * @param ID[][] Peer Group ID. Use 'Filter' to filter BMU using 'Filter' parameter.
 * @param [Currency] Individual results Currency. Default is user preference for Individual Results Currency if omitted.
 * @param [StartDate] Start date of the analysis. Default is inception if omitted.
 * @param [EndDate] End date of the analysis. Default is the most recent available quarter end date if omitted.
 * @param [IndexID] Index ID. Default is user preference for Index if omitted.
 * @param [ReturnConstituentCount] If true, returns constituent count for the analysis. Default is false if omitted.
 * @param [Filter] Filter applied to BMU.
 * @param {CustomFunctions.Invocation} invocation - The invocation context.
 * @helpUrl https://burgiss.docsend.com/view/4c3zt8i
 * @requiresAddress
 */
// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
function BurgissUniverseCalcLN_ICMValuationSpreadPooled(
  ID?: any[][],
  Currency?: string,
  StartDate?: any,
  EndDate?: any,
  IndexID?: string,
  ReturnConstituentCount?: any,
  Filter?: any,
  invocation?: any
) {
  const result = CheckProfile();
  if (result == true) {
    const uniqueCellId = generateUniqueCellId(invocation.address);
    return new UAMeasuresFunctions().fnBurgissUniverseCalcLN_ICMValuationSpreadPooled(
      ID,
      Currency,
      StartDate,
      EndDate,
      ReturnConstituentCount,
      Filter,
      IndexID,
      uniqueCellId
    );
  }
  return result;
}
/**
 * Calculates the pooled value of the measure for the specified peer group.
 * @CustomFunction PrivateiUniverseCalcRIRR.Pooled
 * @param ID[][] Peer Group ID. Use 'Filter' to filter BMU using 'Filter' parameter.
 * @param [Currency] Individual results Currency. Default is user preference for Individual Results Currency if omitted.
 * @param [StartDate] Start date of the analysis. Default is inception if omitted.
 * @param [EndDate] End date of the analysis. Default is the most recent available quarter end date if omitted.
 * @param [ReturnConstituentCount] If true, returns constituent count for the analysis. Default is false if omitted.
 * @param [Filter] Filter applied to BMU.
 * @param {CustomFunctions.Invocation} invocation - The invocation context.
 * @helpUrl https://burgiss.docsend.com/view/4c3zt8i
 * @requiresAddress
 */
// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
function BurgissUniverseCalcRIRRPooled(
  ID?: any[][],
  Currency?: string,
  StartDate?: any,
  EndDate?: any,
  ReturnConstituentCount?: any,
  Filter?: any,
  invocation?: any
) {
  const result = CheckProfile();
  if (result == true) {
    const uniqueCellId = generateUniqueCellId(invocation.address);
    return new UAMeasuresFunctions().fnBurgissUniverseCalcRIRRPooled(
      ID,
      Currency,
      StartDate,
      EndDate,
      ReturnConstituentCount,
      Filter,
      uniqueCellId
    );
  }
  return result;
}
/**
 * Calculates the pooled value of the measure for the specified peer group.
 * @CustomFunction PrivateiUniverseCalcDistributions.Pooled
 * @param ID[][] Peer Group ID. Use 'Filter' to filter BMU using 'Filter' parameter.
 * @param [Currency] Individual results Currency. Default is user preference for Individual Results Currency if omitted.
 * @param [StartDate] Start date of the analysis. Default is inception if omitted.
 * @param [EndDate] End date of the analysis. Default is the most recent available quarter end date if omitted.
 * @param [ReturnConstituentCount] If true, returns constituent count for the analysis. Default is false if omitted.
 * @param [Filter] Filter applied to BMU.
 * @param {CustomFunctions.Invocation} invocation - The invocation context.
 * @helpUrl https://burgiss.docsend.com/view/4c3zt8i
 * @requiresAddress
 */
// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
function BurgissUniverseCalcDistributionsPooled(
  ID?: any[][],
  Currency?: string,
  StartDate?: any,
  EndDate?: any,
  ReturnConstituentCount?: any,
  Filter?: any,
  invocation?: any
) {
  const result = CheckProfile();
  if (result == true) {
    const uniqueCellId = generateUniqueCellId(invocation.address);
    return new UAMeasuresFunctions().fnBurgissUniverseCalcDistributionsPooled(
      ID,
      Currency,
      StartDate,
      EndDate,
      ReturnConstituentCount,
      Filter,
      uniqueCellId
    );
  }
  return result;
}
/**
 * Calculates the pooled value of the measure for the specified peer group.
 * @CustomFunction PrivateiUniverseCalcNetCashFlow.Pooled
 * @param ID[][] Peer Group ID. Use 'Filter' to filter BMU using 'Filter' parameter.
 * @param [Currency] Individual results Currency. Default is user preference for Individual Results Currency if omitted.
 * @param [StartDate] Start date of the analysis. Default is inception if omitted.
 * @param [EndDate] End date of the analysis. Default is the most recent available quarter end date if omitted.
 * @param [ReturnConstituentCount] If true, returns constituent count for the analysis. Default is false if omitted.
 * @param [Filter] Filter applied to BMU.
 * @param {CustomFunctions.Invocation} invocation - The invocation context.
 * @helpUrl https://burgiss.docsend.com/view/4c3zt8i
 * @requiresAddress
 */
// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
function BurgissUniverseCalcNetCashFlowPooled(
  ID?: any[][],
  Currency?: string,
  StartDate?: any,
  EndDate?: any,
  ReturnConstituentCount?: any,
  Filter?: any,
  invocation?: any
) {
  const result = CheckProfile();
  if (result == true) {
    const uniqueCellId = generateUniqueCellId(invocation.address);
    return new UAMeasuresFunctions().fnBurgissUniverseCalcNetCashFlowPooled(
      ID,
      Currency,
      StartDate,
      EndDate,
      ReturnConstituentCount,
      Filter,
      uniqueCellId
    );
  }
  return result;
}
/**
 * Calculates the pooled value of the measure for the specified peer group.
 * @CustomFunction PrivateiUniverseCalcPaidInCapital.Pooled
 * @param ID[][] Peer Group ID. Use 'Filter' to filter BMU using 'Filter' parameter.
 * @param [Currency] Individual results Currency. Default is user preference for Individual Results Currency if omitted.
 * @param [StartDate] Start date of the analysis. Default is inception if omitted.
 * @param [EndDate] End date of the analysis. Default is the most recent available quarter end date if omitted.
 * @param [ReturnConstituentCount] If true, returns constituent count for the analysis. Default is false if omitted.
 * @param [Filter] Filter applied to BMU.
 * @param {CustomFunctions.Invocation} invocation - The invocation context.
 * @helpUrl https://burgiss.docsend.com/view/4c3zt8i
 * @requiresAddress
 */
// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
function BurgissUniverseCalcPaidInCapitalPooled(
  ID?: any[][],
  Currency?: string,
  StartDate?: any,
  EndDate?: any,
  ReturnConstituentCount?: any,
  Filter?: any,
  invocation?: any
) {
  const result = CheckProfile();
  if (result == true) {
    const uniqueCellId = generateUniqueCellId(invocation.address);
    return new UAMeasuresFunctions().fnBurgissUniverseCalcPaidInCapitalPooled(
      ID,
      Currency,
      StartDate,
      EndDate,
      ReturnConstituentCount,
      Filter,
      uniqueCellId
    );
  }
  return result;
}
/**
 * Calculates the pooled value of the measure for the specified peer group.
 * @CustomFunction PrivateiUniverseCalcCapitalization.Pooled
 * @param ID[][] Peer Group ID. Use 'Filter' to filter BMU using 'Filter' parameter.
 * @param [Currency] Individual results Currency. Default is user preference for Individual Results Currency if omitted.
 * @param [StartDate] Start date of the analysis. Default is inception if omitted.
 * @param [EndDate] End date of the analysis. Default is the most recent available quarter end date if omitted.
 * @param [ReturnConstituentCount] If true, returns constituent count for the analysis. Default is false if omitted.
 * @param [Filter] Filter applied to BMU.
 * @param {CustomFunctions.Invocation} invocation - The invocation context.
 * @helpUrl https://burgiss.docsend.com/view/4c3zt8i
 * @requiresAddress
 */
// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
function BurgissUniverseCalcCapitalizationPooled(
  ID?: any[][],
  Currency?: string,
  StartDate?: any,
  EndDate?: any,
  ReturnConstituentCount?: any,
  Filter?: any,
  invocation?: any
) {
  const result = CheckProfile();
  if (result == true) {
    const uniqueCellId = generateUniqueCellId(invocation.address);
    return new UAMeasuresFunctions().fnBurgissUniverseCalcCapitalizationPooled(
      ID,
      Currency,
      StartDate,
      EndDate,
      ReturnConstituentCount,
      Filter,
      uniqueCellId
    );
  }
  return result;
}

/**
 * Calculates the pooled value of the measure for the specified peer group.
 * @CustomFunction PrivateiUniverseCalcTWRR_QTD.Pooled
 * @param [ID] Peer Group ID. Use 'Filter' to filter BMU using 'Filter' parameter.
 * @param [Currency]  Pooled results Currency. Default is user preference for Pooled Results Currency if omitted.
 * @param [EndDate] End date of the analysis. Default is the most recent available quarter end date if omitted.
 * @param [ReturnConstituentCount]  If true, returns constituent count for the analysis. Default is false if omitted.
 * @param [Filter] Filter applied to BMU.
 * @param {CustomFunctions.Invocation} invocation - The invocation context.
 * @helpUrl https://burgiss.docsend.com/view/4c3zt8i
 * @requiresAddress
 */
// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
function burgissUniverseCalcTWRR_QTDPooled(
  ID: any[][],
  Currency: any,
  EndDate?: any,
  ReturnConstituentCount?: any,
  Filter?: any,
  invocation?: any
) {
  const result = CheckProfile();
  if (result == true) {
    const uniqueCellId = generateUniqueCellId(invocation.address);
    return new UAMeasuresFunctions().fnBurgissUniverseCalcTWRR_QTDPooled(
      ID,
      Currency,
      EndDate,
      ReturnConstituentCount,
      Filter,
      uniqueCellId
    );
  }
  return result;
}
/**
 * Calculates the pooled value of the measure for the specified peer group..
 * @CustomFunction PrivateiUniverseCalcIRR_NW.Pooled
 * @param [ID] Peer Group ID. Use 'Filter' to filter BMU using 'Filter' parameter.
 * @param [Currency]  Pooled results Currency. Default is user preference for Pooled Results Currency if omitted.
 * @param [EndDate] End date of the analysis. Default is the most recent available quarter end date if omitted.
 * @param [ReturnConstituentCount]  If true, returns constituent count for the analysis. Default is false if omitted.
 * @param [Filter] Filter applied to BMU.
 * @param {CustomFunctions.Invocation} invocation - The invocation context.
 * @helpUrl https://burgiss.docsend.com/view/4c3zt8i
 * @requiresAddress
 */
// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
function burgissUniverseCalcIRR_NWPooled(
  ID: any[][],
  Currency: any,
  EndDate?: any,
  ReturnConstituentCount?: any,
  Filter?: any,
  invocation?: any
) {
  const result = CheckProfile();
  if (result == true) {
    const uniqueCellId = generateUniqueCellId(invocation.address);
    return new UAMeasuresFunctions().fnBurgissUniverseCalcIRR_NWPooled(
      ID,
      Currency,
      EndDate,
      ReturnConstituentCount,
      Filter,
      uniqueCellId
    );
  }
  return result;
}

/**
 * Calculates the pooled value of the measure for the specified peer group.
 * @CustomFunction PrivateiUniverseCalcIRR.Pooled
 * @param [ID] vPeer Group ID. Use 'Filter' to filter BMU using 'Filter' parameter.
 * @param [Currency]  Pooled results Currency. Default is user preference for Pooled Results Currency if omitted.
 * @param [StartDate] Start date of the analysis. Default is inception if omitted.
 * @param [EndDate] End date of the analysis. Default is the most recent available quarter end date if omitted.
 * @param [ReturnConstituentCount]  If true, returns constituent count for the analysis. Default is false if omitted.
 * @param [Filter] Filter applied to BMU.
 * @param {CustomFunctions.Invocation} invocation - The invocation context.
 * @helpUrl https://burgiss.docsend.com/view/4c3zt8i
 * @requiresAddress
 */
// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
function burgissUniverseCalcIRRPooled(
  ID: any[][],
  Currency: any,
  StartDate: any,
  EndDate?: any,
  ReturnConstituentCount?: any,
  Filter?: any,
  invocation?: any
) {
  const result = CheckProfile();
  if (result == true) {
    const uniqueCellId = generateUniqueCellId(invocation.address);
    return new UAMeasuresFunctions().fnBurgissUniverseCalcIRRPooled(
      ID,
      Currency,
      StartDate,
      EndDate,
      ReturnConstituentCount,
      Filter,
      uniqueCellId
    );
  }
  return result;
}

/**
 * Calculates the pooled value of the measure for the specified peer group.
 * @CustomFunction PrivateiUniverseCalcIRR_QTD.Pooled
 * @param [ID] Peer Group ID. Use 'Filter' to filter BMU using 'Filter' parameter.
 * @param [Currency]  Pooled results Currency. Default is user preference for Pooled Results Currency if omitted.
 * @param [EndDate] End date of the analysis. Default is the most recent available quarter end date if omitted.
 * @param [ReturnConstituentCount]  If true, returns constituent count for the analysis. Default is false if omitted.
 * @param [Filter] Filter applied to BMU.
 * @param {CustomFunctions.Invocation} invocation - The invocation context.
 * @helpUrl https://burgiss.docsend.com/view/4c3zt8i
 * @requiresAddress
 */
// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
function burgissUniverseCalcIRR_QTDPooled(
  ID: any[][],
  Currency: any,
  EndDate?: any,
  ReturnConstituentCount?: any,
  Filter?: any,
  invocation?: any
) {
  const result = CheckProfile();
  if (result == true) {
    const uniqueCellId = generateUniqueCellId(invocation.address);
    return new UAMeasuresFunctions().fnBurgissUniverseCalcIRR_QTDPooled(
      ID,
      Currency,
      EndDate,
      ReturnConstituentCount,
      Filter,
      uniqueCellId
    );
  }
  return result;
}

/**
 * Calculates the pooled value of the measure for the specified peer group.
 * @CustomFunction PrivateiUniverseCalcIRR_YTD.Pooled
 * @param [ID] Peer Group ID. Use 'Filter' to filter BMU using 'Filter' parameter.
 * @param [Currency]  Pooled results Currency. Default is user preference for Pooled Results Currency if omitted.
 * @param [EndDate] End date of the analysis. Default is the most recent available quarter end date if omitted.
 * @param [ReturnConstituentCount]  If true, returns constituent count for the analysis. Default is false if omitted.
 * @param [Filter] Filter applied to BMU.
 * @param {CustomFunctions.Invocation} invocation - The invocation context.
 * @helpUrl https://burgiss.docsend.com/view/4c3zt8i
 * @requiresAddress
 */
// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
function burgissUniverseCalcIRR_YTDPooled(
  ID: any[][],
  Currency: any,
  EndDate?: any,
  ReturnConstituentCount?: any,
  Filter?: any,
  invocation?: any
) {
  const result = CheckProfile();
  if (result == true) {
    const uniqueCellId = generateUniqueCellId(invocation.address);
    return new UAMeasuresFunctions().fnBurgissUniverseCalcIRR_YTDPooled(
      ID,
      Currency,
      EndDate,
      ReturnConstituentCount,
      Filter,
      uniqueCellId
    );
  }
  return result;
}
/**
 * Calculates the pooled value of the measure for the specified peer group.
 * @CustomFunction PrivateiUniverseCalcIRR_1Year.Pooled
 * @param [ID] Peer Group ID. Use 'Filter' to filter BMU using 'Filter' parameter.
 * @param [Currency]  Pooled results Currency. Default is user preference for Pooled Results Currency if omitted.
 * @param [EndDate] End date of the analysis. Default is the most recent available quarter end date if omitted.
 * @param [ReturnConstituentCount]  If true, returns constituent count for the analysis. Default is false if omitted.
 * @param [Filter] Filter applied to BMU.
 * @param {CustomFunctions.Invocation} invocation - The invocation context.
 * @helpUrl https://burgiss.docsend.com/view/4c3zt8i
 * @requiresAddress
 */
// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
function burgissUniverseCalcIRR_1YearPooled(
  ID: any[][],
  Currency: any,
  EndDate?: any,
  ReturnConstituentCount?: any,
  Filter?: any,
  invocation?: any
) {
  const result = CheckProfile();
  if (result == true) {
    const uniqueCellId = generateUniqueCellId(invocation.address);
    return new UAMeasuresFunctions().fnBurgissUniverseCalcIRR_1YearPooled(
      ID,
      Currency,
      EndDate,
      ReturnConstituentCount,
      Filter,
      uniqueCellId
    );
  }
  return result;
}

/**
 * Calculates the pooled value of the measure for the specified peer group.
 * @CustomFunction PrivateiUniverseCalcIRR_3Year.Pooled
 * @param [ID] Peer Group ID. Use 'Filter' to filter BMU using 'Filter' parameter.
 * @param [Currency]  Pooled results Currency. Default is user preference for Pooled Results Currency if omitted.
 * @param [EndDate] End date of the analysis. Default is the most recent available quarter end date if omitted.
 * @param [ReturnConstituentCount]  If true, returns constituent count for the analysis. Default is false if omitted.
 * @param [Filter] Filter applied to BMU.
 * @param {CustomFunctions.Invocation} invocation - The invocation context.
 * @helpUrl https://burgiss.docsend.com/view/4c3zt8i
 * @requiresAddress
 */
// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
function burgissUniverseCalcIRR_3YearPooled(
  ID: any[][],
  Currency: any,
  EndDate?: any,
  ReturnConstituentCount?: any,
  Filter?: any,
  invocation?: any
) {
  const result = CheckProfile();
  if (result == true) {
    const uniqueCellId = generateUniqueCellId(invocation.address);
    return new UAMeasuresFunctions().fnBurgissUniverseCalcIRR_3YearPooled(
      ID,
      Currency,
      EndDate,
      ReturnConstituentCount,
      Filter,
      uniqueCellId
    );
  }
  return result;
}

/**
 * Calculates the pooled value of the measure for the specified peer group.
 * @CustomFunction PrivateiUniverseCalcIRR_5Year.Pooled
 * @param [ID] Peer Group ID. Use 'Filter' to filter BMU using 'Filter' parameter.
 * @param [Currency]  Pooled results Currency. Default is user preference for Pooled Results Currency if omitted.
 * @param [EndDate] End date of the analysis. Default is the most recent available quarter end date if omitted.
 * @param [ReturnConstituentCount]  If true, returns constituent count for the analysis. Default is false if omitted.
 * @param [Filter] Filter applied to BMU.
 * @param {CustomFunctions.Invocation} invocation - The invocation context.
 * @helpUrl https://burgiss.docsend.com/view/4c3zt8i
 * @requiresAddress
 */
// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
function burgissUniverseCalcIRR_5YearPooled(
  ID: any[][],
  Currency: any,
  EndDate?: any,
  ReturnConstituentCount?: any,
  Filter?: any,
  invocation?: any
) {
  const result = CheckProfile();
  if (result == true) {
    const uniqueCellId = generateUniqueCellId(invocation.address);
    return new UAMeasuresFunctions().fnBurgissUniverseCalcIRR_5YearPooled(
      ID,
      Currency,
      EndDate,
      ReturnConstituentCount,
      Filter,
      uniqueCellId
    );
  }
  return result;
}

/**
 * Calculates the pooled value of the measure for the specified peer group.
 * @CustomFunction PrivateiUniverseCalcIRR_10Year.Pooled
 * @param [ID] Peer Group ID. Use 'Filter' to filter BMU using 'Filter' parameter.
 * @param [Currency]  Pooled results Currency. Default is user preference for Pooled Results Currency if omitted.
 * @param [EndDate] End date of the analysis. Default is the most recent available quarter end date if omitted.
 * @param [ReturnConstituentCount]  If true, returns constituent count for the analysis. Default is false if omitted.
 * @param [Filter] Filter applied to BMU.
 * @param {CustomFunctions.Invocation} invocation - The invocation context.
 * @helpUrl https://burgiss.docsend.com/view/4c3zt8i
 * @requiresAddress
 */
// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
function burgissUniverseCalcIRR_10YearPooled(
  ID: any[][],
  Currency: any,
  EndDate?: any,
  ReturnConstituentCount?: any,
  Filter?: any,
  invocation?: any
) {
  const result = CheckProfile();
  if (result == true) {
    const uniqueCellId = generateUniqueCellId(invocation.address);
    return new UAMeasuresFunctions().fnBurgissUniverseCalcIRR_10YearPooled(
      ID,
      Currency,
      EndDate,
      ReturnConstituentCount,
      Filter,
      uniqueCellId
    );
  }
  return result;
}

/**
 * Calculates the pooled value of the measure for the specified peer group.
 * @CustomFunction PrivateiUniverseCalcIRR_15Year.Pooled
 * @param [ID] Peer Group ID. Use 'Filter' to filter BMU using 'Filter' parameter.
 * @param [Currency]  Pooled results Currency. Default is user preference for Pooled Results Currency if omitted.
 * @param [EndDate] End date of the analysis. Default is the most recent available quarter end date if omitted.
 * @param [ReturnConstituentCount]  If true, returns constituent count for the analysis. Default is false if omitted.
 * @param [Filter] Filter applied to BMU.
 * @param {CustomFunctions.Invocation} invocation - The invocation context.
 * @helpUrl https://burgiss.docsend.com/view/4c3zt8i
 * @requiresAddress
 */
// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
function burgissUniverseCalcIRR_15YearPooled(
  ID: any[][],
  Currency: any,
  EndDate?: any,
  ReturnConstituentCount?: any,
  Filter?: any,
  invocation?: any
) {
  const result = CheckProfile();
  if (result == true) {
    const uniqueCellId = generateUniqueCellId(invocation.address);
    return new UAMeasuresFunctions().fnBurgissUniverseCalcIRR_15YearPooled(
      ID,
      Currency,
      EndDate,
      ReturnConstituentCount,
      Filter,
      uniqueCellId
    );
  }
  return result;
}

/**
 * Calculates the pooled value of the measure for the specified peer group.
 * @CustomFunction PrivateiUniverseCalcIRR_20Year.Pooled
 * @param [ID] Peer Group ID. Use 'Filter' to filter BMU using 'Filter' parameter.
 * @param [Currency]  Pooled results Currency. Default is user preference for Pooled Results Currency if omitted.
 * @param [EndDate] End date of the analysis. Default is the most recent available quarter end date if omitted.
 * @param [ReturnConstituentCount]  If true, returns constituent count for the analysis. Default is false if omitted.
 * @param [Filter] Filter applied to BMU.
 * @param {CustomFunctions.Invocation} invocation - The invocation context.
 * @helpUrl https://burgiss.docsend.com/view/4c3zt8i
 * @requiresAddress
 */
// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
function burgissUniverseCalcIRR_20YearPooled(
  ID: any[][],
  Currency: any,
  EndDate?: any,
  ReturnConstituentCount?: any,
  Filter?: any,
  invocation?: any
) {
  const result = CheckProfile();
  if (result == true) {
    const uniqueCellId = generateUniqueCellId(invocation.address);
    return new UAMeasuresFunctions().fnBurgissUniverseCalcIRR_20YearPooled(
      ID,
      Currency,
      EndDate,
      ReturnConstituentCount,
      Filter,
      uniqueCellId
    );
  }
  return result;
}

/**
 * Calculates the pooled value of the measure for the specified peer group.
 * @CustomFunction PrivateiUniverseCalcDPI.Pooled
 * @param [ID] Peer Group ID. Use 'Filter' to filter BMU using 'Filter' parameter.
 * @param [Currency]  Pooled results Currency. Default is user preference for Pooled Results Currency if omitted.
 * @param [EndDate] End date of the analysis. Default is the most recent available quarter end date if omitted.
 * @param [ReturnConstituentCount]  If true, returns constituent count for the analysis. Default is false if omitted.
 * @param {CustomFunctions.Invocation} invocation - The invocation context.
 * @helpUrl https://burgiss.docsend.com/view/4c3zt8i
 * @requiresAddress
 */
// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
function burgissUniverseCalcDPIPooled(
  ID: any[][],
  Currency: any,
  EndDate?: any,
  ReturnConstituentCount?: any,
  invocation?: any
) {
  const result = CheckProfile();
  if (result == true) {
    const uniqueCellId = generateUniqueCellId(invocation.address);
    return new UAMeasuresFunctions().fnBurgissUniverseCalcDPIPooled(
      ID,
      Currency,
      EndDate,
      ReturnConstituentCount,
      uniqueCellId
    );
  }
  return result;
}
/**
 * Calculates the pooled value of the measure for the specified peer group.
 * @CustomFunction PrivateiUniverseCalcRVPI.Pooled
 * @param [ID] Peer Group ID. Use 'Filter' to filter BMU using 'Filter' parameter.
 * @param [Currency]  Pooled results Currency. Default is user preference for Pooled Results Currency if omitted.
 * @param [EndDate] End date of the analysis. Default is the most recent available quarter end date if omitted.
 * @param [ReturnConstituentCount]  If true, returns constituent count for the analysis. Default is false if omitted.
 * @param [Filter] Filter applied to BMU.
 * @param {CustomFunctions.Invocation} invocation - The invocation context.
 * @helpUrl https://burgiss.docsend.com/view/4c3zt8i
 * @requiresAddress
 */
// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
function burgissUniverseCalcRVPIPooled(
  ID: any[][],
  Currency: any,
  EndDate?: any,
  ReturnConstituentCount?: any,
  Filter?: any,
  invocation?: any
) {
  const result = CheckProfile();
  if (result == true) {
    const uniqueCellId = generateUniqueCellId(invocation.address);
    return new UAMeasuresFunctions().fnBurgissUniverseCalcRVPIPooled(
      ID,
      Currency,
      EndDate,
      ReturnConstituentCount,
      Filter,
      uniqueCellId
    );
  }
  return result;
}

/**
 * Calculates the pooled value of the measure for the specified peer group.
 * @CustomFunction PrivateiUniverseCalcTVPI.Pooled
 * @param [ID] Peer Group ID. Use 'Filter' to filter BMU using 'Filter' parameter.
 * @param [Currency]  Pooled results Currency. Default is user preference for Pooled Results Currency if omitted.
 * @param [EndDate] End date of the analysis. Default is the most recent available quarter end date if omitted.
 * @param [ReturnConstituentCount]  If true, returns constituent count for the analysis. Default is false if omitted.
 * @param [Filter] Filter applied to BMU.
 * @param {CustomFunctions.Invocation} invocation - The invocation context.
 * @helpUrl https://burgiss.docsend.com/view/4c3zt8i
 * @requiresAddress
 */
// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
function burgissUniverseCalcTVPIPooled(
  ID: any[][],
  Currency: any,
  EndDate?: any,
  ReturnConstituentCount?: any,
  Filter?: any,
  invocation?: any
) {
  const result = CheckProfile();
  if (result == true) {
    const uniqueCellId = generateUniqueCellId(invocation.address);
    return new UAMeasuresFunctions().fnBurgissUniverseCalcTVPIPooled(
      ID,
      Currency,
      EndDate,
      ReturnConstituentCount,
      Filter,
      uniqueCellId
    );
  }
  return result;
}

/**
 * Calculates the pooled value of the measure for the specified peer group.
 * @CustomFunction PrivateiUniverseCalcValuation.Pooled
 * @param [ID] Peer Group ID. Use 'Filter' to filter BMU using 'Filter' parameter.
 * @param [Currency]  Pooled results Currency. Default is user preference for Pooled Results Currency if omitted.
 * @param [EndDate] End date of the analysis. Default is the most recent available quarter end date if omitted.
 * @param [ReturnConstituentCount]  If true, returns constituent count for the analysis. Default is false if omitted.
 * @param [Filter] Filter applied to BMU.
 * @param {CustomFunctions.Invocation} invocation - The invocation context.
 * @helpUrl https://burgiss.docsend.com/view/4c3zt8i
 * @requiresAddress
 */
// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
function burgissUniverseCalcValuationPooled(
  ID: any[][],
  Currency: any,
  EndDate?: any,
  ReturnConstituentCount?: any,
  Filter?: any,
  invocation?: any
) {
  const result = CheckProfile();
  if (result == true) {
    const uniqueCellId = generateUniqueCellId(invocation.address);
    return new UAMeasuresFunctions().fnBurgissUniverseCalcValuationPooled(
      ID,
      Currency,
      EndDate,
      ReturnConstituentCount,
      Filter,
      uniqueCellId
    );
  }
  return result;
}

/**
 * Calculates the requested percentile of the measure for the specified peer group.
 * @CustomFunction PrivateiUniverseCalcDistributions.Percentile
 * @param [ID] Peer Group ID. Use 'Filter' to filter BMU using 'Filter' parameter.
 * @param [Percentile] The percentile value. Available values: 0.05, 0.25, 0.50, 0.75, 0.95.
 * @param [Currency]  Individual results Currency. Default is user preference for Individual Results Currency if omitted.
 * @param [StartDate] Start date of the analysis. Default is inception if omitted.
 * @param [EndDate] End date of the analysis. Default is the most recent available quarter end date if omitted.
 * @param [ReturnConstituentCount]  If true, returns constituent count for the analysis. Default is false if omitted.
 * @param [Filter]  Filter applied to BMU.
 * @param {CustomFunctions.Invocation} invocation - The invocation context.
 * @helpUrl https://burgiss.docsend.com/view/4c3zt8i
 * @requiresAddress
 */
// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
function BurgissUniverseCalcDistributionsPercentile(
  ID: any[][],
  Percentile: any,
  Currency: any,
  StartDate?: any,
  EndDate?: any,
  ReturnConstituentCount?: any,
  Filter?: any,
  invocation?: any
) {
  const result = CheckProfile();
  if (result == true) {
    const uniqueCellId = generateUniqueCellId(invocation.address);
    return new UAMeasuresFunctions().fnBurgissUniverseCalcDistributionsPercentile(
      ID,
      Percentile,
      Currency,
      StartDate,
      EndDate,
      ReturnConstituentCount,
      Filter,
      uniqueCellId
    );
  }
  return result;
}

/**
 * Calculates the requested percentile of the measure for the specified peer group.
 * @CustomFunction PrivateiUniverseCalcMIRR.Percentile
 * @param [ID] Peer Group ID. Use 'Filter' to filter BMU using 'Filter' parameter.
 * @param [Percentile] The percentile value. Available values: 0.05, 0.25, 0.50, 0.75, 0.95.
 * @param [Currency]  Individual results Currency. Default is user preference for Individual Results Currency if omitted.
 * @param [StartDate] Start date of the analysis. Default is inception if omitted.
 * @param [EndDate] End date of the analysis. Default is the most recent available quarter end date if omitted.
 * @param [IndexID] Index ID. Default is user preference for Index if omitted.
 * @param [ReturnConstituentCount]  If true, returns constituent count for the analysis. Default is false if omitted.
 * @param [Filter]  Filter applied to BMU.
 * @param {CustomFunctions.Invocation} invocation - The invocation context.
 * @helpUrl https://burgiss.docsend.com/view/4c3zt8i
 * @requiresAddress
 */
// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
function BurgissUniverseCalcMIRRPercentile(
  ID: any[][],
  Percentile: any,
  Currency: any,
  StartDate?: any,
  EndDate?: any,
  IndexID?: any,
  ReturnConstituentCount?: any,
  Filter?: any,
  invocation?: any
) {
  const result = CheckProfile();
  if (result == true) {
    const uniqueCellId = generateUniqueCellId(invocation.address);
    return new UAMeasuresFunctions().fnBurgissUniverseCalcMIRRPercentile(
      ID,
      Percentile,
      Currency,
      StartDate,
      EndDate,
      IndexID,
      ReturnConstituentCount,
      Filter,
      uniqueCellId
    );
  }
  return result;
}

/**
 * Calculates the requested percentile of the measure for the specified peer group.
 * @CustomFunction PrivateiUniverseCalcPaidInCapital.Percentile
 * @param [ID] Peer Group ID. Use 'Filter' to filter BMU using 'Filter' parameter.
 * @param [Percentile] The percentile value. Available values: 0.05, 0.25, 0.50, 0.75, 0.95.
 * @param [Currency]  Individual results Currency. Default is user preference for Individual Results Currency if omitted.
 * @param [StartDate] Start date of the analysis. Default is inception if omitted.
 * @param [EndDate] End date of the analysis. Default is the most recent available quarter end date if omitted.
 * @param [ReturnConstituentCount]  If true, returns constituent count for the analysis. Default is false if omitted.
 * @param [Filter]  Filter applied to BMU.
 * @param {CustomFunctions.Invocation} invocation - The invocation context.
 * @helpUrl https://burgiss.docsend.com/view/4c3zt8i
 * @requiresAddress
 */
// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
function BurgissUniverseCalcPaidInCapitalPercentile(
  ID: any[][],
  Percentile: any,
  Currency: any,
  StartDate?: any,
  EndDate?: any,
  ReturnConstituentCount?: any,
  Filter?: any,
  invocation?: any
) {
  const result = CheckProfile();
  if (result == true) {
    const uniqueCellId = generateUniqueCellId(invocation.address);
    return new UAMeasuresFunctions().fnBurgissUniverseCalcPaidInCapitalPercentile(
      ID,
      Percentile,
      Currency,
      StartDate,
      EndDate,
      ReturnConstituentCount,
      Filter,
      uniqueCellId
    );
  }
  return result;
}

/**
 * Calculates the requested percentile of the measure for the specified peer group.
 * @CustomFunction PrivateiUniverseCalcRIRR.Percentile
 * @param [ID] Peer Group ID. Use 'Filter' to filter BMU using 'Filter' parameter.
 * @param [Percentile] The percentile value. Available values: 0.05, 0.25, 0.50, 0.75, 0.95.
 * @param [Currency]  Individual results Currency. Default is user preference for Individual Results Currency if omitted.
 * @param [StartDate] Start date of the analysis. Default is inception if omitted.
 * @param [EndDate] End date of the analysis. Default is the most recent available quarter end date if omitted.
 * @param [ReturnConstituentCount]  If true, returns constituent count for the analysis. Default is false if omitted.
 * @param [Filter]  Filter applied to BMU.
 * @param {CustomFunctions.Invocation} invocation - The invocation context.
 * @helpUrl https://burgiss.docsend.com/view/4c3zt8i
 * @requiresAddress
 */
// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
function BurgissUniverseCalcRIRRPercentile(
  ID: any[][],
  Percentile: any,
  Currency: any,
  StartDate?: any,
  EndDate?: any,
  ReturnConstituentCount?: any,
  Filter?: any,
  invocation?: any
) {
  const result = CheckProfile();
  if (result == true) {
    const uniqueCellId = generateUniqueCellId(invocation.address);
    return new UAMeasuresFunctions().fnBurgissUniverseCalcRIRRPercentile(
      ID,
      Percentile,
      Currency,
      StartDate,
      EndDate,
      ReturnConstituentCount,
      Filter,
      uniqueCellId
    );
  }
  return result;
}

/**
 * Calculates the requested percentile of the measure for the specified peer group.
 * @CustomFunction PrivateiUniverseCalcRVPI.Percentile
 * @param [ID] Peer Group ID. Use 'Filter' to filter BMU using 'Filter' parameter.
 * @param [Percentile] The percentile value. Available values: 0.05, 0.25, 0.50, 0.75, 0.95.
 * @param [Currency]  Individual results Currency. Default is user preference for Individual Results Currency if omitted.
 * @param [EndDate] End date of the analysis. Default is the most recent available quarter end date if omitted.
 * @param [ReturnConstituentCount]  If true, returns constituent count for the analysis. Default is false if omitted.
 * @param [Filter]  Filter applied to BMU.
 * @param {CustomFunctions.Invocation} invocation - The invocation context.
 * @helpUrl https://burgiss.docsend.com/view/4c3zt8i
 * @requiresAddress
 */
// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
function BurgissUniverseCalcRVPIPercentile(
  ID: any[][],
  Percentile: any,
  Currency: any,
  EndDate?: any,
  ReturnConstituentCount?: any,
  Filter?: any,
  invocation?: any
) {
  const result = CheckProfile();
  if (result == true) {
    const uniqueCellId = generateUniqueCellId(invocation.address);
    return new UAMeasuresFunctions().fnBurgissUniverseCalcRVPIPercentile(
      ID,
      Percentile,
      Currency,
      EndDate,
      ReturnConstituentCount,
      Filter,
      uniqueCellId
    );
  }
  return result;
}

/**
 * Calculates the requested percentile of the measure for the specified peer group.
 * @CustomFunction PrivateiUniverseCalcTVPI.Percentile
 * @param [ID] Peer Group ID. Use 'Filter' to filter BMU using 'Filter' parameter.
 * @param [Percentile] The percentile value. Available values: 0.05, 0.25, 0.50, 0.75, 0.95.
 * @param [Currency]  Individual results Currency. Default is user preference for Individual Results Currency if omitted.
 * @param [EndDate] End date of the analysis. Default is the most recent available quarter end date if omitted.
 * @param [ReturnConstituentCount]  If true, returns constituent count for the analysis. Default is false if omitted.
 * @param [Filter]  Filter applied to BMU.
 * @param {CustomFunctions.Invocation} invocation - The invocation context.
 * @helpUrl https://burgiss.docsend.com/view/4c3zt8i
 * @requiresAddress
 */
// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
function BurgissUniverseCalcTVPIPercentile(
  ID: any[][],
  Percentile: any,
  Currency: any,
  EndDate?: any,
  ReturnConstituentCount?: any,
  Filter?: any,
  invocation?: any
) {
  const result = CheckProfile();
  if (result == true) {
    const uniqueCellId = generateUniqueCellId(invocation.address);
    return new UAMeasuresFunctions().fnBurgissUniverseCalcTVPIPercentile(
      ID,
      Percentile,
      Currency,
      EndDate,
      ReturnConstituentCount,
      Filter,
      uniqueCellId
    );
  }
  return result;
}

/**
 * Calculates the requested percentile of the measure for the specified peer group.
 * @CustomFunction PrivateiUniverseCalcValuation.Percentile
 * @param [ID] Peer Group ID. Use 'Filter' to filter BMU using 'Filter' parameter.
 * @param [Percentile] The percentile value. Available values: 0.05, 0.25, 0.50, 0.75, 0.95.
 * @param [Currency]  Individual results Currency. Default is user preference for Individual Results Currency if omitted.
 * @param [EndDate] End date of the analysis. Default is the most recent available quarter end date if omitted.
 * @param [ReturnConstituentCount]  If true, returns constituent count for the analysis. Default is false if omitted.
 * @param [Filter]  Filter applied to BMU.
 * @param {CustomFunctions.Invocation} invocation - The invocation context.
 * @helpUrl https://burgiss.docsend.com/view/4c3zt8i
 * @requiresAddress
 */
// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
function BurgissUniverseCalcValuationPercentile(
  ID: any[][],
  Percentile: any,
  Currency: any,
  EndDate?: any,
  ReturnConstituentCount?: any,
  Filter?: any,
  invocation?: any
) {
  const result = CheckProfile();
  if (result == true) {
    const uniqueCellId = generateUniqueCellId(invocation.address);
    return new UAMeasuresFunctions().fnBurgissUniverseCalcValuationPercentile(
      ID,
      Percentile,
      Currency,
      EndDate,
      ReturnConstituentCount,
      Filter,
      uniqueCellId
    );
  }
  return result;
}
//#endregion UA Measures

/**
 * Add two numbers
 * @customfunction
 * @param {number} first First number
 * @param {number} second Second number
 * @returns {number} The sum of the two numbers.
 */
export function add(first, second) {
  return first + second;
}

/**
 * Displays the current time once a second
 * @customfunction
 * @param {CustomFunctions.StreamingInvocation<string>} invocation Custom function invocation
 */
export function clock(invocation) {
  const timer = setInterval(() => {
    const time = currentTime();
    invocation.setResult(time);
  }, 1000);

  invocation.onCanceled = () => {
    clearInterval(timer);
  };
}

/**
 * Returns the current time
 * @returns {string} String with the current time formatted for the current locale.
 */
export function currentTime() {
  return new Date().toLocaleTimeString();
}

/**
 * Increments a value once a second.
 * @customfunction
 * @param {number} incrementBy Amount to increment
 * @param {CustomFunctions.StreamingInvocation<number>} invocation
 */
export function increment(incrementBy, invocation) {
  let result = 0;
  const timer = setInterval(() => {
    result += incrementBy;
    invocation.setResult(result);
  }, 1000);

  invocation.onCanceled = () => {
    clearInterval(timer);
  };
}

/**
 * Writes a message to console.log().
 * @customfunction LOG
 * @param {string} message String to write.
 * @returns String to write.
 */
export function logMessage(message) {
  return message;
}

CustomFunctions.associate("PRIVATEICALCCAR", BurgissCalcCFaR);
CustomFunctions.associate("PRIVATEIGETATTRIBUTE", BurgissGetAttribute);
CustomFunctions.associate("PRIVATEICALCTARGETSIZE", BurgissCalcTargetSize);
CustomFunctions.associate("PRIVATEICALCPOOLEDRANKINGIRR", BurgissCalcPooledRankingIRR);
CustomFunctions.associate("PRIVATEICALCPOOLEDRANKINGTWRR", BurgissCalcPooledRankingTWRR);
CustomFunctions.associate("PRIVATEICALCPOOLEDRANKINGDPI", BurgissCalcPooledRankingDPI);
CustomFunctions.associate("PRIVATEICALCPOOLEDRANKINGRVPI", BurgissCalcPooledRankingRVPI);
CustomFunctions.associate("PRIVATEICALCPOOLEDRANKINGTVPI", BurgissCalcPooledRankingTVPI);
CustomFunctions.associate("PRIVATEICALCPOOLEDRANKINGIRR.BYWEIGHT", burgissCalcPooledRankingIRRByWeight);
CustomFunctions.associate("PRIVATEICALCPOOLEDRANKINGTWRR.BYWEIGHT", burgissCalcPooledRankingTWRRByWeight);
CustomFunctions.associate("PRIVATEICALCPOOLEDRANKINGDPI.BYWEIGHT", burgissCalcPooledRankingDPIByWeight);
CustomFunctions.associate("PRIVATEICALCPOOLEDRANKINGRVPI.BYWEIGHT", burgissCalcPooledRankingRVPIByWeight);
CustomFunctions.associate("PRIVATEICALCPOOLEDRANKINGTVPI.BYWEIGHT", burgissCalcPooledRankingTVPIByWeight);
CustomFunctions.associate("PRIVATEICALCPOOLEDRANKINGIRR.BYCOMMITMENT", burgissCalcPooledRankingIRRByCommitment);
CustomFunctions.associate("PRIVATEICALCPOOLEDRANKINGTWRR.BYCOMMITMENT", burgissCalcPooledRankingTWRRByCommitment);
CustomFunctions.associate("PRIVATEICALCPOOLEDRANKINGDPI.BYCOMMITMENT", burgissCalcPooledRankingDPIByCommitment);
CustomFunctions.associate("PRIVATEICALCPOOLEDRANKINGRVPI.BYCOMMITMENT", burgissCalcPooledRankingRVPIByCommitment);
CustomFunctions.associate("PRIVATEICALCPOOLEDRANKINGTVPI.BYCOMMITMENT", burgissCalcPooledRankingTVPIByCommitment);
CustomFunctions.associate("PRIVATEIHOLDINGCALCCOSTBASIS", BurgissHoldingCalcCostBasis);
CustomFunctions.associate("PRIVATEIHOLDINGCALCRETURNOFCAPITAL", BurgissHoldingCalcReturnOfCapital);
CustomFunctions.associate("PRIVATEIHOLDINGCALCPROCEEDS", BurgissHoldingCalcProceeds);
CustomFunctions.associate("PRIVATEIHOLDINGCALCVALUATION", BurgissHoldingCalcValuation);
CustomFunctions.associate("PRIVATEIHOLDINGCALCVALUATION.DATE", BurgissHoldingCalcValuationDate);
CustomFunctions.associate("PRIVATEIHOLDINGCALCDPI", BurgissHoldingCalcDPI);
CustomFunctions.associate("PRIVATEIHOLDINGCALCRVPI", BurgissHoldingCalcRVPI);
CustomFunctions.associate("PRIVATEIHOLDINGCALCTVPI", BurgissHoldingCalcTVPI);
CustomFunctions.associate("PRIVATEIHOLDINGCALCTOTALINVESTED", BurgissHoldingCalcTotalInvested);
CustomFunctions.associate("PRIVATEICALCCOSTBASISFXGAINLOSS", BurgissCalcCostBasisFXGainLoss);
CustomFunctions.associate("PRIVATEICALCTWRR", BurgissCalcTWRR);
CustomFunctions.associate("PRIVATEICALCCOSTBASIS", BurgissCalcCostBasis);
CustomFunctions.associate("PRIVATEICALCFUNDING", BurgissCalcFunding);
CustomFunctions.associate("PRIVATEICALCPAIDIN", BurgissCalcPaidIn);
CustomFunctions.associate("PRIVATEICALCFUNDSIZE", BurgissCalcFundSize);
CustomFunctions.associate("PRIVATEICALCDISTRIBUTIONS.RECALLABLECAPITAL", BurgissCalcDistributionsRecallableCapital);
CustomFunctions.associate("PRIVATEICALCDISTRIBUTIONS.OTHER", BurgissCalcDistributionsOther);
CustomFunctions.associate("PRIVATEICALCDISTRIBUTIONS.RETURNOFCAPITAL", BurgissCalcDistributionsReturnofCapital);
CustomFunctions.associate("PRIVATEICALCDISTRIBUTIONS.CAPITALGAINS", BurgissCalcDistributionsCapitalgains);
CustomFunctions.associate("PRIVATEICALCDISTRIBUTIONS.INCOME", BurgissCalcDistributionsIncome);
CustomFunctions.associate("PRIVATEICALCCASH.OTHER", BurgissCalcCashOther);
CustomFunctions.associate("PRIVATEICALCCASH.RETURNOFCAPITAL", BurgissCalcCashReturnofCapital);
CustomFunctions.associate("PRIVATEICALCCASH.CAPITALGAINS", BurgissCashCapitalGains);
CustomFunctions.associate("PRIVATEICALCCASH.INCOME", BurgissCalcCashIncome);
CustomFunctions.associate("PRIVATEICALCCASH.RECALLABLECAPITAL", BurgissCalcCashRecallableCapital);
CustomFunctions.associate("PRIVATEICALCVALUATION.DATE", BurgissCalcValuationDate);
CustomFunctions.associate("PRIVATEICALCNETCAPITALCONTRIBUTED", BurgissCalcNetCapitalContributed);
CustomFunctions.associate("PRIVATEICALCVALUATION.ROLLFORWARD", BurgissCalcValuationRollForward);
CustomFunctions.associate("PRIVATEICALCMIRR", BurgissCalcMIRR);
CustomFunctions.associate("PRIVATEICALCLN_ICMIRR", BurgissCalcLN_ICMIRR);
CustomFunctions.associate("PRIVATEICALCLN_ICMIRRSPREAD", BurgissCalcLN_ICMIRRSpread);
CustomFunctions.associate("PRIVATEICALCLN_ICMVALUATION", BurgissCalcLN_ICMValuation);
CustomFunctions.associate("PRIVATEICALCKS_PME", BurgissCalcKS_PME);
CustomFunctions.associate("PRIVATEICALCGGS_DIRECTALPHA", BurgissCalcGGS_DirectAlpha);
CustomFunctions.associate("PRIVATEICALCSTOCK.CAPITALGAINS", BurgissCalcStockCapitalGains);
CustomFunctions.associate("PRIVATEICALCSTOCK.COST", BurgissCalcStockCost);
CustomFunctions.associate("PRIVATEICALCSTOCK.RECALLABLECAPITAL", BurgissCalcStockRecallableCapital);
CustomFunctions.associate("PRIVATEICALCSTOCK.RETURNOFCAPITAL", BurgissCalcStockReturnOfCapital);
CustomFunctions.associate("PRIVATEICALCADJUSTEDCOMMITMENT", BurgissCalcAdjustedCommitment);
CustomFunctions.associate("PRIVATEICALCCASH", BurgissCalcCash);
CustomFunctions.associate("PRIVATEICALCIRR", BurgissCalcIRR);
CustomFunctions.associate("PRIVATEICALCRIRR", BurgissCalcRIRR);
CustomFunctions.associate("PRIVATEICALCDPI", BurgissCalcDPI);
CustomFunctions.associate("PRIVATEICALCTVPI", BurgissCalcTVPI);
CustomFunctions.associate("PRIVATEICALCRVPI", BurgissCalcRVPI);
CustomFunctions.associate("PRIVATEICALCUNFUNDED", BurgissCalcUnfunded);
CustomFunctions.associate("PRIVATEICALCVALUATION", BurgissCalcValuation);
CustomFunctions.associate("PRIVATEICALCDISTRIBUTIONS", BurgissCalcDistributions);
CustomFunctions.associate("PRIVATEICALCCOMMITMENT", BurgissCalcCommitment);
CustomFunctions.associate("PRIVATEICALCSTOCK", BurgissCalcStock);
CustomFunctions.associate("PRIVATEICALCFEES", BurgissCalcFees);
CustomFunctions.associate("PRIVATEICALCPEERGROUP.CAPITALIZATION", BurgissCalcPeerGroupCapitalization);
CustomFunctions.associate("PRIVATEICALCIRR.PERCENTRANK", BurgissUniverseRankIRR);
CustomFunctions.associate("PRIVATEICALCDPI.PERCENTRANK", BurgissUniverseRankDPI);
CustomFunctions.associate("PRIVATEICALCTVPI.PERCENTRANK", BurgissUniverseRankTVPI);
CustomFunctions.associate("PRIVATEICALCRVPI.PERCENTRANK", BurgissUniverseRankRVPI);
CustomFunctions.associate("PRIVATEIUNIVERSECALCIRR.PERCENTRANK", BurgissUniverseCalcIRRPercentRank);
CustomFunctions.associate("PRIVATEIUNIVERSECALCIRR_QTD.PERCENTRANK", BurgissUniverseCalcIRR_QTDPercentRank);
CustomFunctions.associate("PRIVATEIUNIVERSECALCIRR_YTD.PERCENTRANK", BurgissUniverseCalcIRR_YTDPercentRank);
CustomFunctions.associate("PRIVATEIUNIVERSECALCIRR_1YEAR.PERCENTRANK", BurgissUniverseCalcIRR_1YearPercentRank);
CustomFunctions.associate("PRIVATEIUNIVERSECALCIRR_3YEAR.PERCENTRANK", BurgissUniverseCalcIRR_3YearPercentRank);
CustomFunctions.associate("PRIVATEIUNIVERSECALCIRR_5YEAR.PERCENTRANK", BurgissUniverseCalcIRR_5YearPercentRank);
CustomFunctions.associate("PRIVATEIUNIVERSECALCIRR_10YEAR.PERCENTRANK", BurgissUniverseCalcIRR_10YearPercentRank);
CustomFunctions.associate("PRIVATEIUNIVERSECALCIRR_15YEAR.PERCENTRANK", BurgissUniverseCalcIRR_15YearPercentRank);
CustomFunctions.associate("PRIVATEIUNIVERSECALCIRR_20YEAR.PERCENTRANK", BurgissUniverseCalcIRR_20YearPercentRank);
CustomFunctions.associate("PRIVATEIUNIVERSECALCDPI.PERCENTRANK", BurgissUniverseCalcDPIPercentRank);
CustomFunctions.associate("PRIVATEIUNIVERSECALCRVPI.PERCENTRANK", BurgissUniverseCalcRVPIPercentRank);
CustomFunctions.associate("PRIVATEIUNIVERSECALCTVPI.PERCENTRANK", BurgissUniverseCalcTVPIPercentRank);
CustomFunctions.associate("PRIVATEIUNIVERSECALCVALUATION.PERCENTRANK", BurgissUniverseCalcValuationPercentRank);
CustomFunctions.associate("PRIVATEIUNIVERSECALCMIRR.PERCENTRANK", BurgissUniverseCalcMIRRPercentRank);
CustomFunctions.associate("PRIVATEIUNIVERSECALCRIRR.PERCENTRANK", BurgissUniverseCalcRIRRPercentRank);
CustomFunctions.associate("PRIVATEIUNIVERSECALCDISTRIBUTIONS.PERCENTRANK", BurgissUniverseCalcDistributionsPercentRank);
CustomFunctions.associate("PRIVATEIUNIVERSECALCNETCASHFLOW.PERCENTRANK", BurgissUniverseCalcNetCashFlowPercentRank);
CustomFunctions.associate("PRIVATEIUNIVERSECALCPAIDINCAPITAL.PERCENTRANK", BurgissUniverseCalcPaidInCapitalPercentRank);
CustomFunctions.associate("PRIVATEIUNIVERSECALCGGS_DIRECTALPHA.PERCENTRANK", BurgissUniverseCalcGSS_DirectAlphaPercentRank);
CustomFunctions.associate("PRIVATEIUNIVERSECALCKS_PME.PERCENTRANK", BurgissUniverseCalcKS_PMEPercentRank);
CustomFunctions.associate("PRIVATEIUNIVERSECALCLN_ICMIRR.PERCENTRANK", BurgissUniverseCalcLN_ICMIRRPercentRank);
CustomFunctions.associate("PRIVATEIUNIVERSECALCLN_ICMIRRSPREAD.PERCENTRANK", BurgissUniverseCalcLN_ICMIRRSpreadPercentRank);
CustomFunctions.associate("PRIVATEIUNIVERSECALCLN_ICMVALUATIONSPREAD.PERCENTRANK", BurgissUniverseCalcLN_ICMValuationSpreadPercentRank);
CustomFunctions.associate("PRIVATEICALCPOOLEDBENCHMARKIRR", BurgissCalcPooledBenchmarkIRR);
CustomFunctions.associate("PRIVATEICALCPOOLEDBENCHMARKTWRR", BurgissCalcPooledBenchmarkTWRR);
CustomFunctions.associate("PRIVATEICALCPOOLEDBENCHMARKDPI", BurgissCalcPooledBenchmarkDPI);
CustomFunctions.associate("PRIVATEICALCPOOLEDBENCHMARKTVPI", BurgissCalcPooledBenchmarkTVPI);
CustomFunctions.associate("PRIVATEICALCPOOLEDBENCHMARKRVPI", BurgissCalcPooledBenchmarkRVPI);
CustomFunctions.associate("PRIVATEICALCPOOLEDBENCHMARKDPI.BYWEIGHT", BurgissCalcPooledBenchmarkDPIByWeight);
CustomFunctions.associate("PRIVATEICALCPOOLEDBENCHMARKTVPI.BYWEIGHT", BurgissCalcPooledBenchmarkTVPIByWeight);
CustomFunctions.associate("PRIVATEICALCPOOLEDBENCHMARKRVPI.BYWEIGHT", BurgissCalcPooledBenchmarkRVPIByWeight);
CustomFunctions.associate("PRIVATEICALCPOOLEDBENCHMARKDPI.BYCOMMITMENT", BurgissCalcPooledBenchmarkDPIByCommitment);
CustomFunctions.associate("PRIVATEICALCPOOLEDBENCHMARKTVPI.BYCOMMITMENT", BurgissCalcPooledBenchmarkTVPIByCommitment);
CustomFunctions.associate("PRIVATEICALCPOOLEDBENCHMARKRVPI.BYCOMMITMENT", BurgissCalcPooledBenchmarkRVPIByCommitment);
CustomFunctions.associate("PRIVATEICALCPOOLEDBENCHMARKTWRR.BYWEIGHT", BurgissCalcPooledBenchmarkTWRRByWeight);
CustomFunctions.associate("PRIVATEICALCPOOLEDBENCHMARKIRR.BYWEIGHT", BurgissCalcPooledBenchmarkIRRByWeight);
CustomFunctions.associate("PRIVATEICALCPOOLEDBENCHMARKIRR.BYCOMMITMENT", BurgissCalcPooledBenchmarkIRRByCommitment);
CustomFunctions.associate("PRIVATEICALCPOOLEDBENCHMARKTWRR.BYCOMMITMENT", BurgissCalcPooledBenchmarkTWRRByCommitment);
CustomFunctions.associate("PRIVATEICALCFUNDING.MAXDATE", BurgissCalcFundingMaxDate);
CustomFunctions.associate("PRIVATEICALCFUNDING.MINDATE", BurgissCalcFundingMinDate);
CustomFunctions.associate("PRIVATEICALCCASH.MAXDATE", BurgissCalcCashMaxDate);
CustomFunctions.associate("PRIVATEICALCCASH.MINDATE", BurgissCalcCashMinDate);
CustomFunctions.associate("PRIVATEICALCFEES.MAXDATE", BurgissCalcFeesMaxDate);
CustomFunctions.associate("PRIVATEICALCFEES.MINDATE", BurgissCalcFeesMinDate);
CustomFunctions.associate("PRIVATEICALCVALUATION.MAXDATE", BurgissCalcValuationMaxDate);
CustomFunctions.associate("PRIVATEICALCVALUATION.MINDATE", BurgissCalcValuationMinDate);
CustomFunctions.associate("PRIVATEICALCSTOCK.MINDATE", BurgissCalcStockMinDate);
CustomFunctions.associate("PRIVATEICALCSTOCK.MAXDATE", BurgissCalcStockMaxDate);
CustomFunctions.associate("PRIVATEIUNIVERSECALCIRR.PROFILER", BurgissUniverseCalcIRRProfiler);
CustomFunctions.associate("PRIVATEIUNIVERSECALCMIRR.PROFILER", BurgissUniverseCalcMIRRProfiler);
CustomFunctions.associate("PRIVATEIUNIVERSECALCDPI.PROFILER", BurgissUniverseCalcDPIProfiler);
CustomFunctions.associate("PRIVATEIUNIVERSECALCRVPI.PROFILER", BurgissUniverseCalcRVPIProfiler);
CustomFunctions.associate("PRIVATEIUNIVERSECALCTVPI.PROFILER", BurgissUniverseCalcTVPIProfiler);
CustomFunctions.associate("PRIVATEIUNIVERSECALCDISTRIBUTIONS.PROFILER", BurgissUniverseCalcDistributionsProfiler);
CustomFunctions.associate("PRIVATEIUNIVERSECALCDISTRIBUTIONS_ANNUAL.PROFILER", BurgissUniverseCalcDistributions_AnnualProfiler);
CustomFunctions.associate("PRIVATEIUNIVERSECALCNETCASHFLOW.PROFILER", BurgissUniverseCalcNetCashFlowProfiler);
CustomFunctions.associate("PRIVATEIUNIVERSECALCNETCASHFLOW_ANNUAL.PROFILER", BurgissUniverseCalcNetCashFlow_AnnualProfiler);
CustomFunctions.associate("PRIVATEIUNIVERSECALCPAIDINCAPITAL.PROFILER", BurgissUniverseCalcPaidInCapitalProfiler);
CustomFunctions.associate("PRIVATEIUNIVERSECALCPAIDINCAPITAL_ANNUAL.PROFILER", BurgissUniverseCalcPaidInCapital_AnnualProfiler);
CustomFunctions.associate("PRIVATEIUNIVERSECALCVALUATION.PROFILER", BurgissUniverseCalcValuationProfiler);
CustomFunctions.associate("PRIVATEIUNIVERSECALCGGS_DIRECTALPHA.PROFILER", BurgissUniverseCalcGGS_DirectAlphaProfiler);
CustomFunctions.associate("PRIVATEIUNIVERSECALCKS_PME.PROFILER", BurgissUniverseCalcKS_PMEProfiler);
CustomFunctions.associate("PRIVATEIUNIVERSECALCLN_ICMIRR.PROFILER", BurgissUniverseCalcLN_ICMIRRProfiler);
CustomFunctions.associate("PRIVATEIUNIVERSECALCLN_ICMIRRSPREAD.PROFILER", BurgissUniverseCalcLN_ICMIRRSpreadProfiler);
CustomFunctions.associate("PRIVATEIUNIVERSECALCLN_ICMVALUATIONSPREAD.PROFILER", BurgissUniverseCalcLN_ICMValuationSpreadProfiler);
CustomFunctions.associate("PRIVATEIUNIVERSECALCRIRR.PROFILER", BurgissUniverseCalcRIRRProfiler);
CustomFunctions.associate("PRIVATEIUNIVERSECALCNETCASHFLOW.PERCENTILE", BurgissUniverseCalcNetCashFlowPercentile);
CustomFunctions.associate("PRIVATEIUNIVERSECALCIRR.PERCENTILE", BurgissUniverseCalcIRRPercentile);
CustomFunctions.associate("PRIVATEIUNIVERSECALCIRR_QTD.PERCENTILE", BurgissUniverseCalcIRR_QTDPercentile);
CustomFunctions.associate("PRIVATEIUNIVERSECALCIRR_YTD.PERCENTILE", BurgissUniverseCalcIRR_YTDPercentile);
CustomFunctions.associate("PRIVATEIUNIVERSECALCIRR_1YEAR.PERCENTILE", BurgissUniverseCalcIRR_1YearPercentile);
CustomFunctions.associate("PRIVATEIUNIVERSECALCIRR_3YEAR.PERCENTILE", BurgissUniverseCalcIRR_3YearPercentile);
CustomFunctions.associate("PRIVATEIUNIVERSECALCIRR_5YEAR.PERCENTILE", BurgissUniverseCalcIRR_5YearPercentile);
CustomFunctions.associate("PRIVATEIUNIVERSECALCIRR_10YEAR.PERCENTILE", BurgissUniverseCalcIRR_10YearPercentile);
CustomFunctions.associate("PRIVATEIUNIVERSECALCIRR_15YEAR.PERCENTILE", BurgissUniverseCalcIRR_15YearPercentile);
CustomFunctions.associate("PRIVATEIUNIVERSECALCIRR_20YEAR.PERCENTILE", BurgissUniverseCalcIRR_20YearPercentile);
CustomFunctions.associate("PRIVATEIUNIVERSECALCDPI.PERCENTILE", BurgissUniverseCalcDPIPercentile);
CustomFunctions.associate("PRIVATEIUNIVERSECALCGGS_DIRECTALPHA.PERCENTILE", BurgissUniverseCalcGSS_DirectAlphaPercentile);
CustomFunctions.associate("PRIVATEIUNIVERSECALCKS_PME.PERCENTILE", BurgissUniverseCalcKS_PMEPercentile);
CustomFunctions.associate("PRIVATEIUNIVERSECALCLN_ICMIRR.PERCENTILE", BurgissUniverseCalcLN_ICMIRRPercentile);
CustomFunctions.associate("PRIVATEIUNIVERSECALCLN_ICMIRRSPREAD.PERCENTILE", BurgissUniverseCalcLN_ICMIRRSpreadPercentile);
CustomFunctions.associate("PRIVATEIUNIVERSECALCLN_ICMVALUATIONSPREAD.PERCENTILE", BurgissUniverseCalcLN_ICMValuationSpreadPercentile);
CustomFunctions.associate("PRIVATEIUNIVERSECALCMIRR.POOLED", BurgissUniverseCalcMIRRPooled);
CustomFunctions.associate("PRIVATEIUNIVERSECALCGGS_DIRECTALPHA.POOLED", BurgissUniverseCalcGSS_DirectAlphaPooled);
CustomFunctions.associate("PRIVATEIUNIVERSECALCKS_PME.POOLED", BurgissUniverseCalcKS_PMEPooled);
CustomFunctions.associate("PRIVATEIUNIVERSECALCLN_ICMIRR.POOLED", BurgissUniverseCalcLN_ICMIRRPooled);
CustomFunctions.associate("PRIVATEIUNIVERSECALCLN_ICMIRRSPREAD.POOLED", BurgissUniverseCalcLN_ICMIRRSpreadPooled);
CustomFunctions.associate("PRIVATEIUNIVERSECALCLN_ICMVALUATIONSPREAD.POOLED", BurgissUniverseCalcLN_ICMValuationSpreadPooled);
CustomFunctions.associate("PRIVATEIUNIVERSECALCRIRR.POOLED", BurgissUniverseCalcRIRRPooled);
CustomFunctions.associate("PRIVATEIUNIVERSECALCDISTRIBUTIONS.POOLED", BurgissUniverseCalcDistributionsPooled);
CustomFunctions.associate("PRIVATEIUNIVERSECALCNETCASHFLOW.POOLED", BurgissUniverseCalcNetCashFlowPooled);
CustomFunctions.associate("PRIVATEIUNIVERSECALCPAIDINCAPITAL.POOLED", BurgissUniverseCalcPaidInCapitalPooled);
CustomFunctions.associate("PRIVATEIUNIVERSECALCCAPITALIZATION.POOLED", BurgissUniverseCalcCapitalizationPooled);
CustomFunctions.associate("PRIVATEIUNIVERSECALCTWRR_QTD.POOLED", burgissUniverseCalcTWRR_QTDPooled);
CustomFunctions.associate("PRIVATEIUNIVERSECALCIRR_NW.POOLED", burgissUniverseCalcIRR_NWPooled);
CustomFunctions.associate("PRIVATEIUNIVERSECALCIRR.POOLED", burgissUniverseCalcIRRPooled);
CustomFunctions.associate("PRIVATEIUNIVERSECALCIRR_QTD.POOLED", burgissUniverseCalcIRR_QTDPooled);
CustomFunctions.associate("PRIVATEIUNIVERSECALCIRR_YTD.POOLED", burgissUniverseCalcIRR_YTDPooled);
CustomFunctions.associate("PRIVATEIUNIVERSECALCIRR_1YEAR.POOLED", burgissUniverseCalcIRR_1YearPooled);
CustomFunctions.associate("PRIVATEIUNIVERSECALCIRR_3YEAR.POOLED", burgissUniverseCalcIRR_3YearPooled);
CustomFunctions.associate("PRIVATEIUNIVERSECALCIRR_5YEAR.POOLED", burgissUniverseCalcIRR_5YearPooled);
CustomFunctions.associate("PRIVATEIUNIVERSECALCIRR_10YEAR.POOLED", burgissUniverseCalcIRR_10YearPooled);
CustomFunctions.associate("PRIVATEIUNIVERSECALCIRR_15YEAR.POOLED", burgissUniverseCalcIRR_15YearPooled);
CustomFunctions.associate("PRIVATEIUNIVERSECALCIRR_20YEAR.POOLED", burgissUniverseCalcIRR_20YearPooled);
CustomFunctions.associate("PRIVATEIUNIVERSECALCDPI.POOLED", burgissUniverseCalcDPIPooled);
CustomFunctions.associate("PRIVATEIUNIVERSECALCRVPI.POOLED", burgissUniverseCalcRVPIPooled);
CustomFunctions.associate("PRIVATEIUNIVERSECALCTVPI.POOLED", burgissUniverseCalcTVPIPooled);
CustomFunctions.associate("PRIVATEIUNIVERSECALCVALUATION.POOLED", burgissUniverseCalcValuationPooled);
CustomFunctions.associate("PRIVATEIUNIVERSECALCDISTRIBUTIONS.PERCENTILE", BurgissUniverseCalcDistributionsPercentile);
CustomFunctions.associate("PRIVATEIUNIVERSECALCMIRR.PERCENTILE", BurgissUniverseCalcMIRRPercentile);
CustomFunctions.associate("PRIVATEIUNIVERSECALCPAIDINCAPITAL.PERCENTILE", BurgissUniverseCalcPaidInCapitalPercentile);
CustomFunctions.associate("PRIVATEIUNIVERSECALCRIRR.PERCENTILE", BurgissUniverseCalcRIRRPercentile);
CustomFunctions.associate("PRIVATEIUNIVERSECALCRVPI.PERCENTILE", BurgissUniverseCalcRVPIPercentile);
CustomFunctions.associate("PRIVATEIUNIVERSECALCTVPI.PERCENTILE", BurgissUniverseCalcTVPIPercentile);
CustomFunctions.associate("PRIVATEIUNIVERSECALCVALUATION.PERCENTILE", BurgissUniverseCalcValuationPercentile);
CustomFunctions.associate("ADD", add);
CustomFunctions.associate("CLOCK", clock);
CustomFunctions.associate("INCREMENT", increment);
CustomFunctions.associate("LOG", logMessage);